/**
 * Word document operations via Office JS API.
 *
 * Provides three capabilities:
 *   1. Scaffold: Create ICH E3 heading structure in a blank document
 *   2. Insert: Place content under a specific heading as modules complete
 *   3. Detect: Find which section the cursor is currently in
 *
 * All operations use Word.run() for transactional document access.
 * Falls back gracefully when running outside Word (browser-only mode).
 *
 * SSOT: This is the single gateway for all Word document manipulation
 * from the add-in. Components never call Word.run() directly.
 */

/* global Word */

import type { CustomSpineSection, LiveDocStyleSpec } from "../api/client";
import { brand } from "../config/brand";
import { formatAndInsert, DEFAULT_CONFIG } from "./formatter";
import type { FontConfig } from "./formatter/formatterConfig";
// NOTE: syncEngine also imports from this module — the cycle is safe because
// _isLikelyLoadingPlaceholderImage is a hoisted function declaration used
// only at runtime (never during module init).
import { _isLikelyLoadingPlaceholderImage } from "./syncEngine";
import { beginLiveDocLoad, endLiveDocLoad } from "./liveDocLoadState";
import { withWordOpLog } from "./wordOpsLogger";

/** Every ``Word.run`` callsite in this module routes through here so its
 *  duration shows up in the WordOpsPanel. Exported for reuse in syncEngine. */
export function runWord<T>(name: string, body: (context: Word.RequestContext) => Promise<T>): Promise<T> {
  return withWordOpLog(`wordWriter.${name}`, () => Word.run(body));
}

/** Check if demo mode is active (hides detailed error markers).
 *  Toggle via URL param: ?demo=true
 */
function isDemoMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get("demo") === "true";
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SectionHeading {
  number: string;
  title: string;
  level: 1 | 2 | 3;
}

export interface InsertContentParams {
  sectionNumber: string;
  content: string; // plain text or markdown-ish
  scrollAfterInsert?: boolean; // whether to scroll to the inserted content after insertion completes; default false
  /** CUSTOM-submission provision ONLY: the writer's heading TITLE, used as a
   *  strictly-additive fallback when the number strategies miss (a custom heading
   *  renders its title with no leading number and no bookmark). Undefined for
   *  every normal/template run — when absent the title branch never runs and this
   *  function behaves exactly as before. See ``_headingTitleMatches``. */
  sectionTitle?: string;
}

export interface CursorSectionInfo {
  sectionNumber: string;
  headingText: string;
  level: number;
}

// ---------------------------------------------------------------------------
// ICH E3 skeleton — headings that get scaffolded in a blank document
// ---------------------------------------------------------------------------

const ICH_E3_SKELETON: SectionHeading[] = [
  { number: "1", title: "Title Page", level: 1 },
  { number: "2", title: "Synopsis", level: 1 },
  { number: "3", title: "Table of Contents", level: 1 },
  {
    number: "4",
    title: "List of Abbreviations and Definitions of Terms",
    level: 1,
  },
  { number: "5", title: "Ethics", level: 1 },
  {
    number: "5.1",
    title: "Independent Ethics Committee / Institutional Review Board",
    level: 2,
  },
  { number: "5.2", title: "Ethical Conduct of the Study", level: 2 },
  { number: "5.3", title: "Subject Information and Consent", level: 2 },
  {
    number: "6",
    title: "Investigators and Study Administrative Structure",
    level: 1,
  },
  { number: "7", title: "Introduction", level: 1 },
  { number: "8", title: "Study Objectives", level: 1 },
  { number: "9", title: "Investigational Plan", level: 1 },
  { number: "9.1", title: "Overall Study Design and Plan", level: 2 },
  { number: "9.2", title: "Discussion of Study Design", level: 2 },
  { number: "9.3", title: "Selection of Study Population", level: 2 },
  { number: "9.3.1", title: "Inclusion Criteria", level: 3 },
  { number: "9.3.2", title: "Exclusion Criteria", level: 3 },
  { number: "9.4", title: "Treatments", level: 2 },
  { number: "9.4.1", title: "Extent of Exposure", level: 3 },
  { number: "9.4.2", title: "Treatment Compliance", level: 3 },
  { number: "9.5", title: "Efficacy and Safety Measurements", level: 2 },
  { number: "9.6", title: "Statistical Methods", level: 2 },
  { number: "10", title: "Study Subjects", level: 1 },
  { number: "10.1", title: "Subject Disposition", level: 2 },
  {
    number: "10.2",
    title: "Demographics and Baseline Characteristics",
    level: 2,
  },
  {
    number: "10.3",
    title: "Medical History and Concurrent Illnesses",
    level: 2,
  },
  { number: "11", title: "Efficacy Evaluation", level: 1 },
  { number: "11.1", title: "Data Sets Analysed", level: 2 },
  { number: "12", title: "Results", level: 1 },
  { number: "12.1", title: "Primary Efficacy Endpoint Results", level: 2 },
  {
    number: "12.1.1",
    title: "Sensitivity and Supplementary Analyses",
    level: 3,
  },
  { number: "12.2", title: "Secondary Efficacy Endpoint Results", level: 2 },
  { number: "13", title: "Safety Evaluation", level: 1 },
  { number: "13.1", title: "Extent of Exposure", level: 2 },
  { number: "13.2", title: "Adverse Events", level: 2 },
  { number: "13.3", title: "Deaths, Other Serious Adverse Events", level: 2 },
  { number: "13.4", title: "Clinical Laboratory Evaluation", level: 2 },
  { number: "14", title: "Discussion and Overall Conclusions", level: 1 },
  { number: "15", title: "Tables, Figures, and Graphs", level: 1 },
  { number: "16", title: "Appendices", level: 1 },
];

// ---------------------------------------------------------------------------
// Office JS availability check
// ---------------------------------------------------------------------------

function isOfficeAvailable(): boolean {
  try {
    return typeof Word !== "undefined" && typeof Word.run === "function";
  } catch {
    return false;
  }
}

const SINGLE_LINE_SPACING_PT = 12;

/**
 * The returned paragraph is where {@link _fillImageParagraph} drops the picture.
 */
async function _createImageHostParagraph(context: Word.RequestContext, selection: Word.Range): Promise<Word.Paragraph> {
  const parentContentControl = selection.parentContentControlOrNullObject;
  const parentTable = selection.parentTableOrNullObject;
  parentContentControl.load("isNullObject");
  parentTable.load("isNullObject");
  await context.sync();

  if (!parentContentControl.isNullObject) {
    return parentContentControl.getRange("After").insertParagraph("", "After");
  }
  if (!parentTable.isNullObject) {
    return parentTable.getRange("After").insertParagraph("", "After");
  }
  // Free body: put the figure on its own new line after the cursor's paragraph.
  return selection.paragraphs.getFirst().insertParagraph("", "After");
}

function _fillImageParagraph(host: Word.Paragraph, base64: string, width: number): void {
  host.leftIndent = 0;
  host.rightIndent = 0;
  host.firstLineIndent = 0;
  host.spaceBefore = 0;
  host.spaceAfter = 0;
  host.lineSpacing = SINGLE_LINE_SPACING_PT;

  const picture = host.insertInlinePictureFromBase64(base64, "Start");
  picture.width = width;
  picture.lockAspectRatio = true;
}

export async function insertInlineImageAtSelection(dataUri: string, width = 468): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  // insertInlinePictureFromBase64 wants RAW base64, not the data: prefix.
  const base64 = dataUri.includes(",") ? dataUri.split(",")[1] : dataUri;
  if (!base64) return false;
  return runWord("insertInlineImageAtSelection", async (context) => {
    const selection = context.document.getSelection();
    const host = await _createImageHostParagraph(context, selection);
    _fillImageParagraph(host, base64, width);
    await context.sync();
    return true;
  });
}
/**
 * Detect if an image/inline shape is currently selected in the document.
 * Returns true if a picture is selected, false otherwise.
 */
export async function isImageSelected(): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  return runWord("isImageSelected", async (context) => {
    const selection = context.document.getSelection();
    // InlineShapes within the selection; if the selection contains a picture, this will be non-empty
    const shapes = selection.inlinePictures;
    shapes.load("items");
    await context.sync();
    return shapes.items.length > 0;
  });
}

/**
 * Replace a selected image with a new one from a data-URI.
 * Only works if an image is currently selected.
 *
 * The replacement reuses the selected figure's own paragraph — staying in
 * place, per the existing replace flow — and sets it to single/"auto" spacing
 * so a figure that was being clipped by the paragraph's "Exactly" line spacing
 * comes back whole, without extra whitespace. See {@link _fillImageParagraph}.
 * The old picture is removed only after the replacement has been inserted.
 * Returns true on success, false if no image is selected or operation fails.
 */
export async function replaceSelectedImage(dataUri: string, width = 468): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  const base64 = dataUri.includes(",") ? dataUri.split(",")[1] : dataUri;
  if (!base64) return false;
  return runWord("replaceSelectedImage", async (context) => {
    const selection = context.document.getSelection();
    const shapes = selection.inlinePictures;
    shapes.load("items");
    await context.sync();
    if (shapes.items.length === 0) return false;
    const oldPicture = shapes.items[0];
    // Reuse the figure's own paragraph as the host (replace in place); single
    // "auto" spacing lets Word size the line to the new picture.
    _fillImageParagraph(oldPicture.paragraph, base64, width);
    oldPicture.delete();
    await context.sync();
    return true;
  });
}

// Diagnostic hook for custom-property reads/writes. Currently a no-op — the
// verbose [word-props-diag] logging was intentionally silenced on HEAD to cut
// console noise. Kept as a single call site so it can be re-enabled centrally
// if custom-property issues need tracing again.
function logCustomPropsSnapshot(_label: string, _props: Word.CustomProperty[]): void {
  /* no-op */
}

// ---------------------------------------------------------------------------
// Document identity — unique ID per document, survives across sessions
// ---------------------------------------------------------------------------

const ASTHRA_DOC_ID_KEY = "AsthrDocumentId";
const ASTHRA_RUN_ID_KEY = "AsthrActiveRunId";
/** JSON blob of the last committed selection — library + doctype context. */
const ASTHRA_SESSION_CONTEXT_KEY = "AsthrSessionContext";
/** JSON marker for last backend snapshot projected into Word. */
const ASTHRA_HYDRATION_MARKER_KEY = "AsthrHydrationMarker";
/** Current insertion mode for the active session. */
const ASTHRA_INSERTION_MODE_KEY = "AsthrInsertionMode";
/** Set (to the run_id) once the pipeline draft has been loaded into Word for that
 *  run. GATES the real-time sync engine: before the first load the document holds
 *  only pre-load placeholder text that must NEVER be synced. Value is the run_id so
 *  it self-invalidates when a different run becomes active. */
const ASTHRA_SYNC_READY_KEY = "AsthrSyncReady";
/** Two-state document-ownership pointer, persisted so it survives a task-pane
 *  reload mid-run. "server" — a draft / "Draft more sections" run is in progress
 *  and the server owns the document; live sync MUST stay off so the add-in never
 *  pushes the Word body over the server's concurrent writes (which tangles the
 *  DocService block DLL and scrambles the loaded document). "live" — the drafted
 *  document has been loaded into Word and the live document owns it; sync is
 *  allowed. Set to "server" when a run starts, back to "live" when the draft is
 *  loaded. */
const ASTHRA_DRAFT_POINTER_KEY = "AsthrDraftPointer";

export type WordInsertionMode = "section" | "batch";

export interface WordHydrationMarker {
  runId: string;
  snapshotVersion: string | null;
  snapshotTs: number | null;
  appliedAt: number;
}

/**
 * Persist current insertion mode to Word custom properties.
 * Best-effort and never throws (browser-only mode safe).
 */
export async function saveWordInsertionMode(mode: WordInsertionMode): Promise<void> {
  if (!isOfficeAvailable()) return;
  try {
    await runWord("saveWordInsertionMode", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      logCustomPropsSnapshot("before saveWordInsertionMode", props.items);

      for (const item of props.items) {
        if (item.key === ASTHRA_INSERTION_MODE_KEY) {
          item.delete();
        }
      }
      props.add(ASTHRA_INSERTION_MODE_KEY, mode);
      await context.sync();
      logCustomPropsSnapshot("after saveWordInsertionMode", props.items);
    });
  } catch (err) {
    console.warn("[wordWriter] saveWordInsertionMode failed (non-fatal):", err);
  }
}

/**
 * Load persisted insertion mode from Word custom properties.
 * Returns null when missing, invalid, or Office.js unavailable.
 */
export async function loadWordInsertionMode(): Promise<WordInsertionMode | null> {
  if (!isOfficeAvailable()) return null;
  try {
    return await runWord("loadWordInsertionMode", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      logCustomPropsSnapshot("loadWordInsertionMode", props.items);

      for (const item of props.items) {
        if (item.key !== ASTHRA_INSERTION_MODE_KEY) continue;
        const value = item.value as string;
        return value === "batch" || value === "section" ? value : null;
      }
      return null;
    });
  } catch (err) {
    console.warn("[wordWriter] loadWordInsertionMode failed (non-fatal):", err);
    return null;
  }
}

/**
 * Get or create a unique document ID for the current Word document.
 * Stored in Word custom properties — persists across sessions.
 */
export async function getOrCreateDocumentId(): Promise<string> {
  if (!isOfficeAvailable()) return "";

  return runWord("getOrCreateDocumentId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();

    // Diagnostic: enumerate every custom property we can see, so we
    // can tell apart "property was never written" from "property was
    // written but stripped on save / OneDrive sync".
    logCustomPropsSnapshot("customProperties on open", props.items);

    // Check if ID already exists
    for (const item of props.items) {
      if (item.key === ASTHRA_DOC_ID_KEY) {
        const existing = item.value as string;
        return existing;
      }
    }

    // Create new ID
    const docId = `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    props.add(ASTHRA_DOC_ID_KEY, docId);
    await context.sync();
    return docId;
  });
}

/**
 * Read the existing Word document ID without creating a new one.
 * Returns null when the property is missing or Office.js is unavailable.
 */
export async function loadDocumentId(): Promise<string | null> {
  if (!isOfficeAvailable()) return null;

  return runWord("loadDocumentId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();
    logCustomPropsSnapshot("loadDocumentId", props.items);

    for (const item of props.items) {
      if (item.key === ASTHRA_DOC_ID_KEY) {
        const existing = item.value as string;
        return existing || null;
      }
    }
    return null;
  });
}

/**
 * Persist a known Word document ID into custom properties.
 * Used when a downloaded doc reopens without the original document ID.
 */
export async function saveDocumentId(docId: string): Promise<void> {
  if (!isOfficeAvailable()) return;

  await runWord("saveDocumentId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();
    logCustomPropsSnapshot("before saveDocumentId", props.items);

    for (const item of props.items) {
      if (item.key === ASTHRA_DOC_ID_KEY) {
        item.delete();
      }
    }
    props.add(ASTHRA_DOC_ID_KEY, docId);
    await context.sync();
    logCustomPropsSnapshot("after saveDocumentId", props.items);
  });
}

/**
 * Rotate the document's AsthrDocumentId — used when the writer signals
 * "I'm starting a new project on this Word file" (e.g. after emptying
 * the body of a previously-drafted document).
 *
 * Mints a fresh id, replaces the AsthrDocumentId custom property, and
 * also strips AsthrActiveRunId so the new project doesn't inherit a
 * dead run binding from the previous one. The OLD id's backend record
 * is left intact (orphaned but recoverable) — we never delete history,
 * we just stop pointing at it.
 *
 * Returns the NEW id, or "" if Office.js is unavailable.
 */
export async function rotateDocumentId(): Promise<string> {
  if (!isOfficeAvailable()) return "";

  return runWord("rotateDocumentId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();

    for (const item of props.items) {
      if (
        item.key === ASTHRA_DOC_ID_KEY ||
        item.key === ASTHRA_RUN_ID_KEY ||
        item.key === ASTHRA_SESSION_CONTEXT_KEY ||
        item.key === ASTHRA_HYDRATION_MARKER_KEY ||
        item.key === ASTHRA_INSERTION_MODE_KEY ||
        item.key === ASTHRA_SYNC_READY_KEY
      ) {
        item.delete();
      }
    }
    await context.sync();

    const newId = `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    props.add(ASTHRA_DOC_ID_KEY, newId);
    await context.sync();
    console.info("[docid-diag] rotated to new id", { newId });
    return newId;
  });
}

/**
 * Mark this Word document as READY for real-time sync: the pipeline draft has now
 * been loaded into the body for `runId`. The sync engine must never run before
 * this — a fresh run shows placeholder text ("Drafting your…") that would
 * otherwise get pushed to DocService over the real (server-side) draft.
 *
 * Stored as a custom property (persists across sessions, so a reopen of an
 * already-loaded document resumes sync without a re-load). The value is the
 * run_id, so the gate self-invalidates when a different run becomes active.
 * Best-effort; never throws.
 */
export async function markSyncReady(runId: string): Promise<void> {
  if (!isOfficeAvailable() || !runId) return;
  try {
    await runWord("markSyncReady", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      for (const item of props.items) {
        if (item.key === ASTHRA_SYNC_READY_KEY) item.delete();
      }
      props.add(ASTHRA_SYNC_READY_KEY, runId);
      await context.sync();
      logCustomPropsSnapshot("after markSyncReady", props.items);
    });
  } catch (err) {
    console.warn("[wordWriter] markSyncReady failed (non-fatal):", err);
  }
}

/**
 * Whether this document has been marked ready for sync for `runId` (i.e. the
 * pipeline draft was loaded at least once for the currently-active run). Returns
 * false when the property is missing, belongs to a different run, or Office.js is
 * unavailable — in every "not clearly ready" case sync stays gated off.
 */
export async function isSyncReady(runId: string): Promise<boolean> {
  if (!isOfficeAvailable() || !runId) return false;
  try {
    return await runWord("isSyncReady", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      for (const item of props.items) {
        if (item.key === ASTHRA_SYNC_READY_KEY) {
          return (item.value as string) === runId;
        }
      }
      return false;
    });
  } catch (err) {
    console.warn("[wordWriter] isSyncReady failed (non-fatal):", err);
    return false;
  }
}

export type DraftPointer = "live" | "server";

/**
 * Set the document-ownership pointer (see ASTHRA_DRAFT_POINTER_KEY). Persisted
 * as a custom property so it survives a task-pane reload mid-run — an in-memory
 * pause flag alone would reset to "sync on" on reload while the server is still
 * drafting. Best-effort; never throws.
 */
export async function setDraftPointer(value: DraftPointer): Promise<void> {
  if (!isOfficeAvailable()) return;
  try {
    await runWord("setDraftPointer", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      for (const item of props.items) {
        if (item.key === ASTHRA_DRAFT_POINTER_KEY) item.delete();
      }
      props.add(ASTHRA_DRAFT_POINTER_KEY, value);
      await context.sync();
      logCustomPropsSnapshot(`after setDraftPointer(${value})`, props.items);
    });
  } catch (err) {
    console.warn("[wordWriter] setDraftPointer failed (non-fatal):", err);
  }
}

/**
 * Read the document-ownership pointer. Returns null when unset or Office.js is
 * unavailable. A null/absent pointer is NOT "server-owned" — it just means the
 * pointer has never been set, and the isSyncReady gate still governs sync.
 */
export async function getDraftPointer(): Promise<DraftPointer | null> {
  if (!isOfficeAvailable()) return null;
  try {
    return await runWord("getDraftPointer", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      for (const item of props.items) {
        if (item.key === ASTHRA_DRAFT_POINTER_KEY) {
          return (item.value as DraftPointer) ?? null;
        }
      }
      return null;
    });
  } catch (err) {
    console.warn("[wordWriter] getDraftPointer failed (non-fatal):", err);
    return null;
  }
}

/**
 * Get the active run ID bound to this document, if any.
 */
export async function getActiveRunId(): Promise<string | null> {
  if (!isOfficeAvailable()) return null;

  return runWord("getActiveRunId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();
    logCustomPropsSnapshot("getActiveRunId", props.items);

    for (const item of props.items) {
      if (item.key === ASTHRA_RUN_ID_KEY) {
        return item.value as string;
      }
    }
    return null;
  });
}

/**
 * Bind a run ID to this document. Prevents other runs from writing.
 */
export async function setActiveRunId(runId: string): Promise<void> {
  if (!isOfficeAvailable()) return;

  await runWord("setActiveRunId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();
    logCustomPropsSnapshot("before setActiveRunId", props.items);

    // Remove existing if any
    for (const item of props.items) {
      if (item.key === ASTHRA_RUN_ID_KEY) {
        item.delete();
      }
    }
    props.add(ASTHRA_RUN_ID_KEY, runId);
    await context.sync();
    logCustomPropsSnapshot("after setActiveRunId", props.items);
  });
}

/**
 * Clear the active run ID (on completion or cancel).
 */
export async function clearActiveRunId(): Promise<void> {
  if (!isOfficeAvailable()) return;

  await runWord("clearActiveRunId", async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();
    logCustomPropsSnapshot("before clearActiveRunId", props.items);

    for (const item of props.items) {
      if (item.key === ASTHRA_RUN_ID_KEY) {
        item.delete();
      }
    }
    await context.sync();
    logCustomPropsSnapshot("after clearActiveRunId", props.items);
  });
}

export async function loadWordHydrationMarker(): Promise<WordHydrationMarker | null> {
  if (!isOfficeAvailable()) return null;
  try {
    return await runWord("loadWordHydrationMarker", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      logCustomPropsSnapshot("loadWordHydrationMarker", props.items);

      for (const item of props.items) {
        if (item.key !== ASTHRA_HYDRATION_MARKER_KEY) continue;
        try {
          const parsed = JSON.parse(item.value as string) as Partial<WordHydrationMarker>;
          if (typeof parsed.runId !== "string") return null;
          if (parsed.snapshotVersion !== null && typeof parsed.snapshotVersion !== "string") return null;
          if (parsed.snapshotTs !== null && typeof parsed.snapshotTs !== "number") return null;
          if (typeof parsed.appliedAt !== "number") return null;
          return {
            runId: parsed.runId,
            snapshotVersion: parsed.snapshotVersion ?? null,
            snapshotTs: parsed.snapshotTs ?? null,
            appliedAt: parsed.appliedAt,
          };
        } catch {
          return null;
        }
      }
      return null;
    });
  } catch (err) {
    console.warn("[wordWriter] loadWordHydrationMarker failed (non-fatal):", err);
    return null;
  }
}

export async function saveWordHydrationMarker(marker: {
  runId: string;
  snapshotVersion: string | null;
  snapshotTs: number | null;
}): Promise<void> {
  if (!isOfficeAvailable()) return;
  try {
    await runWord("saveWordHydrationMarker", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      logCustomPropsSnapshot("before saveWordHydrationMarker", props.items);

      for (const item of props.items) {
        if (item.key === ASTHRA_HYDRATION_MARKER_KEY) {
          item.delete();
        }
      }

      const payload: WordHydrationMarker = {
        runId: marker.runId,
        snapshotVersion: marker.snapshotVersion,
        snapshotTs: marker.snapshotTs,
        appliedAt: Date.now(),
      };
      props.add(ASTHRA_HYDRATION_MARKER_KEY, JSON.stringify(payload));
      await context.sync();
      logCustomPropsSnapshot("after saveWordHydrationMarker", props.items);
    });
  } catch (err) {
    console.warn("[wordWriter] saveWordHydrationMarker failed (non-fatal):", err);
  }
}

export async function clearWordHydrationMarker(): Promise<void> {
  if (!isOfficeAvailable()) return;
  try {
    await runWord("clearWordHydrationMarker", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();
      logCustomPropsSnapshot("before clearWordHydrationMarker", props.items);

      for (const item of props.items) {
        if (item.key === ASTHRA_HYDRATION_MARKER_KEY) {
          item.delete();
        }
      }
      await context.sync();
      logCustomPropsSnapshot("after clearWordHydrationMarker", props.items);
    });
  } catch (err) {
    console.warn("[wordWriter] clearWordHydrationMarker failed (non-fatal):", err);
  }
}

/**
 * Detect the "skeleton-only" failure state: a body that has heading
 * paragraphs but no real prose under them. Used by the restore path to
 * self-heal docs whose hydration marker claims they're hydrated but whose
 * body is actually empty (e.g. a prior restore wrote 0 sections, then the
 * marker locked re-hydration out — see useRunControl ``shouldHydrateWord``).
 *
 * Returns true only when at least one heading-styled paragraph exists AND
 * there is no non-empty, non-heading body paragraph. Title-page table cells
 * are excluded (same ``parentTableCellOrNullObject`` filter the insert path
 * uses) so the title-page chrome never counts as "content". Returns false
 * when Office is unavailable or on any error — callers treat false as "looks
 * fine, don't force a re-hydrate" so a probe failure can never wipe content.
 */
/** Snapshot of a paragraph used by the headings-only classifier. */
interface ParagraphProbeSnapshot {
  text: string;
  style: string;
  styleBuiltIn: string;
  isTableCell: boolean;
  /** Zero-based row of the containing table cell — set only for table-cell
   *  paragraphs, used to reconstruct a section's data tables as markdown at
   *  custom-submission import time. Undefined for body paragraphs. */
  tableRowIndex?: number;
  /** Zero-based column of the containing table cell (see tableRowIndex). */
  tableCellIndex?: number;
}

/** The body placeholder the scaffold writes under an unfilled heading
 *  (word_writer.py::_add_placeholder / the add-in scaffold). At custom import
 *  this must NOT be captured as a section's real content — otherwise a run
 *  imported before the writer filled it persists placeholders that a later
 *  "Load doc" faithfully paints back over the real Word content. */
const SCAFFOLD_PLACEHOLDER = "Content not yet generated.";

/** Turn accumulated table-cell snapshots (row/col/text) into a GitHub-flavoured
 *  markdown table so a custom section's data tables survive the import →
 *  ``draft_full`` → Load round-trip (``insertSectionContent`` renders markdown
 *  tables back into Word tables). Cells are ordered by (row, col); a blank cell
 *  stays blank. Returns "" when there is nothing renderable. Pure. */
function _tableCellsToMarkdown(cells: Array<{ row: number; col: number; text: string }>): string {
  if (cells.length === 0) return "";
  const maxRow = Math.max(...cells.map((c) => c.row));
  const maxCol = Math.max(...cells.map((c) => c.col));
  const grid: string[][] = Array.from({ length: maxRow + 1 }, () => Array<string>(maxCol + 1).fill(""));
  for (const c of cells) {
    // Multi-paragraph cells accumulate their lines with a space (a markdown
    // table cell can't hold a hard line break without <br>, and a space keeps
    // the value readable + re-parseable).
    const prior = grid[c.row][c.col];
    grid[c.row][c.col] = prior ? `${prior} ${c.text.trim()}` : c.text.trim();
  }
  const rowMd = (r: string[]) => `| ${r.map((cell) => cell.replace(/\|/g, "\\|")).join(" | ")} |`;
  const lines = [rowMd(grid[0]), `| ${grid[0].map(() => "---").join(" | ")} |`];
  for (let r = 1; r <= maxRow; r++) lines.push(rowMd(grid[r]));
  return lines.join("\n");
}

/** Parse accumulated title-page table cells into a structured ``title_fields``
 *  map (label → value, in document row order) — but ONLY for a clean 2-column
 *  metadata table (the standard regulatory title-page layout: label in col 0,
 *  value in col 1). Returns ``null`` for anything else (0 cells, a single column,
 *  or ≥3 columns) so the caller falls back to a markdown table.
 *
 *  This is the custom-submission counterpart to the drafted title page's
 *  structured ``title_fields`` dict: capturing the metadata structurally (rather
 *  than as a GFM markdown table, which forces a header row and, on Load, grid
 *  borders) lets Load repaint it through the borderless title-page renderer. The
 *  trailing colon on a label is stripped so the value renderer re-adds a single
 *  canonical one. A blank label or blank value row is skipped. Pure. */
function _titleCellsToFields(
  cells: Array<{ row: number; col: number; text: string }>,
): Record<string, string> | null {
  if (cells.length === 0) return null;
  const maxCol = Math.max(...cells.map((c) => c.col));
  if (maxCol !== 1) return null; // not a 2-column label/value table
  const maxRow = Math.max(...cells.map((c) => c.row));
  const grid: string[][] = Array.from({ length: maxRow + 1 }, () => ["", ""]);
  for (const c of cells) {
    // Multi-paragraph cells accumulate their lines with a space (mirrors
    // _tableCellsToMarkdown) so each field stays a single label→value pair.
    const prior = grid[c.row][c.col];
    grid[c.row][c.col] = prior ? `${prior} ${c.text.trim()}` : c.text.trim();
  }
  const fields: Record<string, string> = {};
  for (let r = 0; r <= maxRow; r++) {
    const label = grid[r][0].replace(/:+$/, "").trim();
    const value = grid[r][1].trim();
    if (!label || !value) continue; // skip spacer / blank rows
    fields[label] = value; // last write wins on a duplicate label
  }
  return Object.keys(fields).length > 0 ? fields : null;
}

/**
 * Pure core of {@link isBodyHeadingsOnly}: given paragraph snapshots, return
 * true when there is at least one heading paragraph but no non-empty,
 * non-heading body paragraph (table-cell paragraphs excluded — they are the
 * title-page chrome, not section content). Extracted so the classification is
 * unit-testable without an Office context.
 */
function _isHeadingsOnlyParagraphs(paragraphs: ParagraphProbeSnapshot[]): boolean {
  let headingCount = 0;
  let bodyContentCount = 0;
  for (const para of paragraphs) {
    if (para.isTableCell) continue; // ignore table-cell paragraphs (title-page chrome)
    const builtIn = (para.styleBuiltIn || "").toLowerCase();
    const displayName = (para.style || "").toLowerCase();
    const isHeading = builtIn.includes("heading") || displayName.includes("heading");
    if (isHeading) {
      headingCount += 1;
    } else if (para.text.trim().length > 0) {
      bodyContentCount += 1;
    }
  }
  return headingCount > 0 && bodyContentCount === 0;
}
/**
 * The ONE heading-style rule: a paragraph is a heading when either its
 * built-in style (``Heading1``…) or its display style name (the backend's
 * custom ``AsthraHeading1/2/3``, which report ``styleBuiltIn === "Other"``)
 * contains "heading". Pure; every heading check in this module delegates
 * here so the built-in-only / display-name-only variants can't drift apart.
 */
function _isHeadingStyle(styleBuiltIn: string | null | undefined, styleName: string | null | undefined): boolean {
  const builtIn = (styleBuiltIn || "").toLowerCase();
  const displayName = (styleName || "").toLowerCase();
  return builtIn.includes("heading") || displayName.includes("heading");
}

// Helper for checking both snapshot and Word.Paragraph types
function _isHeadingFromSnapshot(snapshot: ParagraphProbeSnapshot): boolean {
  return _isHeadingStyle(snapshot.styleBuiltIn, snapshot.style);
}

export async function isBodyHeadingsOnly(): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  try {
    return await runWord("isBodyHeadingsOnly", async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("text,style,styleBuiltIn");
      await context.sync();

      const parentTableCells = paragraphs.items.map((p: Word.Paragraph) => p.parentTableCellOrNullObject);
      for (const c of parentTableCells) {
        c.load("isNullObject");
      }
      await context.sync();

      const snapshots: ParagraphProbeSnapshot[] = paragraphs.items.map((para, i) => ({
        text: para.text,
        style: para.style || "",
        styleBuiltIn: para.styleBuiltIn || "",
        isTableCell: !parentTableCells[i].isNullObject,
      }));
      return _isHeadingsOnlyParagraphs(snapshots);
    });
  } catch (err) {
    console.warn("[wordWriter] isBodyHeadingsOnly probe failed (non-fatal):", err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Existing-doc outline reader (custom submission)
// ---------------------------------------------------------------------------

/** One heading of an existing document, before content roll-up. */
export interface OutlineNode {
  /** Section number parsed from the heading text, blank if none. */
  number: string;
  /** Heading text (number stripped when one was parsed off the front). */
  title: string;
  /** Heading depth (1 = top level). */
  level: number;
  /** True when non-heading prose appears directly beneath this heading,
   *  before the next heading. */
  hasOwnProse: boolean;
  /** The heading's OWN prose (paragraphs directly beneath it, before the next
   *  heading), joined by newlines. Persisted server-side as the section's
   *  auditable SSOT so the regulatory audit can read/annotate it. */
  ownText: string;
  /** True for the synthetic leading "Title Page" node — the document's
   *  title-page block (everything before the first real heading: the title
   *  line, confidentiality note, and the metadata table). The title line +
   *  confidentiality prose live in ``ownText``; a 2-column metadata table is
   *  captured structurally into {@link titleFields} instead. Distinct from a
   *  real heading: it carries no number and is restored as leading chrome (no
   *  injected heading) rather than scaffolded as a section. */
  isTitle?: boolean;
  /** Structured title-page fields (label→value, in document order) parsed from
   *  the title block's 2-column metadata table. Only set on the ``isTitle`` node.
   *  Persisted onto the title module's ``title_fields`` (like a drafted title
   *  page) so Load repaints it through the borderless title-page renderer rather
   *  than a bordered markdown table. A non-2-column pre-heading table is not
   *  captured here — it stays a markdown table in ``ownText``. */
  titleFields?: Record<string, string>;
}

/**
 * Strict outline-heading test for the custom-spine reader. Counts only the nine
 * real outline levels — built-in ``Heading1``–``Heading9`` or the backend's
 * custom ``AsthraHeading1``–``AsthraHeading9`` (which report
 * ``styleBuiltIn === "Other"``). Deliberately NOT the broad
 * ``_isHeadingStyle`` substring test: real documents carry TOC / figure /
 * footnote styles whose names contain "heading", and the substring check
 * balloons a 30-section doc into 1500+ "headings" (see the note on
 * ``OUTLINE_HEADING_STYLE_RE``).
 */
const _ASTHRA_HEADING_STYLE_RE = /^asthraheading\s?[1-9]$/i;
function _isOutlineHeadingSnapshot(p: ParagraphProbeSnapshot): boolean {
  return (
    OUTLINE_HEADING_STYLE_RE.test(p.styleBuiltIn || "") ||
    OUTLINE_HEADING_STYLE_RE.test(p.style || "") ||
    _ASTHRA_HEADING_STYLE_RE.test(p.style || "")
  );
}

/** The synthetic leading node's title. Not written into the doc as a heading on
 *  Load — the loader restores this node's ``ownText`` as leading chrome. */
const TITLE_PAGE_NODE_TITLE = "Title Page";

/**
 * Pure core of {@link readCustomSpineOutline}: turn paragraph snapshots into a
 * flat, document-ordered list of heading nodes. Body paragraphs are attributed
 * to the most recent heading (setting ``hasOwnProse``).
 *
 * The title-page block — everything BEFORE the first real heading (the title
 * line, confidentiality note, and the metadata table) — is captured verbatim
 * as markdown into a synthetic leading node flagged ``isTitle``, so it survives
 * the import → ``draft_full`` → Load round-trip instead of being dropped.
 * Extracted so the walk is unit-testable without an Office context.
 */
function _buildOutlineFromParagraphs(paragraphs: ParagraphProbeSnapshot[]): OutlineNode[] {
  const nodes: OutlineNode[] = [];
  // Table cells accumulated since the last non-table paragraph, plus the node
  // they belong to (the heading they sit under, or -1 for the pre-heading title
  // block). Flushed as one markdown table when a non-table paragraph (prose or
  // the next heading) ends the table, or when a new table starts.
  let pendingCells: Array<{ row: number; col: number; text: string }> = [];
  let pendingOwner = -1;
  let sawFirstRow = false; // a (row 0) cell already recorded → a second one starts a new table
  // Markdown fragments for the leading title-page block (content before the
  // first heading). Prose lines (title, subtitle, confidentiality) accumulate
  // here; a 2-column metadata table is captured structurally into titleFields.
  const titleParts: string[] = [];
  // Structured title-page fields (label→value) parsed from the leading block's
  // 2-column metadata table(s). Stays null until such a table is seen, so a doc
  // with only a non-2-column pre-heading table falls back to the markdown path.
  let titleFields: Record<string, string> | null = null;

  const appendTitle = (text: string): void => {
    const trimmed = text.trim();
    if (trimmed.length === 0 || trimmed === SCAFFOLD_PLACEHOLDER) return;
    titleParts.push(trimmed);
  };

  const appendProse = (nodeIndex: number, text: string): void => {
    const trimmed = text.trim();
    // The scaffold placeholder is not real content — skip it so an unfilled
    // section imports EMPTY rather than persisting a fake body.
    if (trimmed.length === 0 || trimmed === SCAFFOLD_PLACEHOLDER) return;
    const node = nodes[nodeIndex];
    node.hasOwnProse = true;
    node.ownText = node.ownText ? `${node.ownText}\n${trimmed}` : trimmed;
  };

  const flushTable = (): void => {
    if (pendingCells.length > 0) {
      // A table owned by a heading attaches to it as markdown; a pre-heading
      // table (owner -1) is title-page chrome. A clean 2-column pre-heading
      // table is the metadata block — capture it structurally into titleFields
      // so Load repaints it borderless; anything else stays a markdown table.
      if (pendingOwner >= 0) {
        appendProse(pendingOwner, _tableCellsToMarkdown(pendingCells));
      } else {
        const fields = _titleCellsToFields(pendingCells);
        if (fields) {
          titleFields = { ...(titleFields ?? {}), ...fields };
        } else {
          appendTitle(_tableCellsToMarkdown(pendingCells));
        }
      }
    }
    pendingCells = [];
    pendingOwner = -1;
    sawFirstRow = false;
  };

  for (const para of paragraphs) {
    if (para.isTableCell) {
      const row = para.tableRowIndex ?? 0;
      const col = para.tableCellIndex ?? 0;
      // Two data tables back-to-back: a fresh (0,0) after we already captured
      // row 0 means the previous table ended.
      if (sawFirstRow && row === 0 && col === 0) flushTable();
      if (row === 0) sawFirstRow = true;
      pendingCells.push({ row, col, text: para.text || "" });
      // nodes.length - 1 is -1 before the first heading → the title block.
      pendingOwner = nodes.length - 1;
      continue;
    }
    // Any non-table paragraph closes an open table first.
    flushTable();
    if (_isOutlineHeadingSnapshot(para)) {
      const raw = (para.text || "").trim();
      const parsed = _parseSectionNumber(raw);
      nodes.push({
        number: parsed?.number ?? "",
        title: parsed?.title ?? raw,
        level: _getHeadingLevel(para.styleBuiltIn, para.style),
        hasOwnProse: false,
        ownText: "",
      });
    } else if (para.text.trim().length > 0) {
      // Non-heading prose belongs to the heading it sits under, or — before the
      // first heading — to the leading title-page block.
      if (nodes.length > 0) appendProse(nodes.length - 1, para.text);
      else appendTitle(para.text);
    }
  }
  flushTable();

  // Prepend the captured title-page block as a synthetic leading node. Level 0
  // marks it as pre-outline chrome; it carries no number and the loader renders
  // it as leading content rather than a heading. Created when there is EITHER
  // chrome prose OR a structured metadata table (a doc may open straight into
  // the metadata table with no title line).
  if (titleParts.length > 0 || titleFields) {
    nodes.unshift({
      number: "",
      title: TITLE_PAGE_NODE_TITLE,
      level: 0,
      // The node "has content" when it holds chrome prose OR structured fields,
      // so an all-table title block isn't rolled up as empty.
      hasOwnProse: titleParts.length > 0 || titleFields !== null,
      ownText: titleParts.join("\n\n"),
      ...(titleFields ? { titleFields } : {}),
      isTitle: true,
    });
  }
  return nodes;
}

/**
 * Roll content status up the outline: a heading "has content" when it has its
 * own prose OR any of its descendant headings does (a descendant is a following
 * node of strictly greater depth, up to the next node at the same-or-shallower
 * level). This is the "green if descendants have content" rule. Pure; returns a
 * boolean per node aligned with the input order.
 */
function _rollUpHasContent(nodes: OutlineNode[]): boolean[] {
  return nodes.map((node, i) => {
    if (node.hasOwnProse) return true;
    for (let j = i + 1; j < nodes.length; j++) {
      if (nodes[j].level <= node.level) break; // left this heading's subtree
      if (nodes[j].hasOwnProse) return true;
    }
    return false;
  });
}

/**
 * Pure: outline nodes → custom spine sections with synthetic ids and rolled-up
 * content status. Exported (via ``_testing``) for unit testing.
 */
function _outlineToCustomSpine(nodes: OutlineNode[]): CustomSpineSection[] {
  const hasContent = _rollUpHasContent(nodes);
  return nodes.map((node, i) => ({
    id: `CUSTOM-${i + 1}`,
    number: node.number,
    title: node.title,
    level: node.level,
    has_content: hasContent[i],
    // The section's own prose (empty for a pure parent heading). Persisted
    // server-side as the auditable SSOT; parents with no direct prose simply
    // aren't independently auditable.
    content: node.ownText || undefined,
    // The synthetic leading title-page block — restored as leading chrome (no
    // heading) on Load, not scaffolded as a numbered section.
    ...(node.isTitle ? { is_title: true } : {}),
    // Structured metadata fields (label→value) from the title block's 2-column
    // table. Persisted onto the title module's title_fields so Load repaints it
    // through the borderless title-page renderer.
    ...(node.titleFields ? { title_fields: node.titleFields } : {}),
  }));
}

/** One table-cell paragraph, tagged with its cell coordinates, in Word's
 *  row-major flatten order (row 0 / col 0's paragraphs, then row 0 / col 1's, …).
 *  Recovered from the tables collection so table membership can be assigned to
 *  the flat body-paragraph list WITHOUT minting a ``parentTableCellOrNullObject``
 *  proxy per body paragraph. */
interface TableRunCell {
  row: number;
  col: number;
  text: string;
}

/** Per-paragraph table-cell membership, aligned with the flat body-paragraph
 *  list — the subset of {@link ParagraphProbeSnapshot} the table probe fills. */
interface ParagraphTableMembership {
  isTableCell: boolean;
  tableRowIndex?: number;
  tableCellIndex?: number;
}

/** Smallest index ``i >= from`` where ``flatTexts[i..i+run.length)`` exactly
 *  equals the run's cell texts, or -1 when the run isn't present from ``from``.
 *  Exact (untrimmed) equality is safe because the body paragraph and the cell
 *  paragraph are the SAME Word paragraph — ``.text`` returns the same string. */
function _findTableRunStart(flatTexts: string[], run: TableRunCell[], from: number): number {
  const L = run.length;
  for (let i = from; i + L <= flatTexts.length; i++) {
    let matched = true;
    for (let k = 0; k < L; k++) {
      if (flatTexts[i + k] !== run[k].text) {
        matched = false;
        break;
      }
    }
    if (matched) return i;
  }
  return -1;
}

/**
 * Pure core of the fast path in {@link readCustomSpineOutline}: place each
 * table's contiguous run of paragraphs inside the flat, document-ordered body
 * paragraph list, so table-cell membership + (row, col) can be assigned WITHOUT
 * a per-paragraph ``parentTableCellOrNullObject`` probe — the O(document)-proxy
 * hang (see the 2026-07-18 note on {@link hydrateLiveDocFromBase64}).
 *
 * Word flattens a table's paragraphs into ``body.paragraphs`` as one contiguous
 * run in row-major cell order, and tables appear in the flat list in the same
 * order as ``body.tables``. A greedy left-to-right exact-text match therefore
 * places every run unambiguously in the common case.
 *
 * Returns a membership array aligned with ``flatTexts`` on success, or ``null``
 * when any table can't be confidently placed — a run whose cell text is entirely
 * blank (nothing to anchor on), or a sequence that doesn't match (an unexpected
 * flatten order). The caller then falls back to the exact per-paragraph probe.
 * Never guesses: an unplaceable table means ``null`` (the safe path), never a
 * mis-attributed table.
 */
function _placeTableRunsInParagraphs(
  flatTexts: string[],
  tableRuns: TableRunCell[][],
): ParagraphTableMembership[] | null {
  const membership: ParagraphTableMembership[] = flatTexts.map(() => ({ isTableCell: false }));
  let cursor = 0;
  for (const run of tableRuns) {
    if (run.length === 0) continue; // an empty table contributes no paragraphs
    // A run with no non-blank cell can't be anchored → bail to the exact probe.
    if (run.every((c) => c.text.trim().length === 0)) return null;
    const start = _findTableRunStart(flatTexts, run, cursor);
    if (start === -1) return null;
    for (let k = 0; k < run.length; k++) {
      membership[start + k] = {
        isTableCell: true,
        tableRowIndex: run[k].row,
        tableCellIndex: run[k].col,
      };
    }
    cursor = start + run.length;
  }
  return membership;
}

/**
 * Exact per-paragraph table-cell probe — mints a ``parentTableCellOrNullObject``
 * proxy for every body paragraph (one extra ``context.sync``). This is O(document)
 * proxies and is the slow path; it exists only as the correctness fallback for
 * {@link readCustomSpineOutline} when {@link _placeTableRunsInParagraphs} can't
 * confidently place a table from the (cheap) tables collection.
 */
async function _probeParagraphTableMembership(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
): Promise<ParagraphTableMembership[]> {
  const parentTableCells = paragraphs.items.map((p: Word.Paragraph) => p.parentTableCellOrNullObject);
  for (const c of parentTableCells) {
    // rowIndex/cellIndex let the pure builder reconstruct each section's data
    // tables as markdown. Read only when the cell is real (isNullObject false).
    c.load("isNullObject,rowIndex,cellIndex");
  }
  await context.sync();
  return parentTableCells.map((cell) => {
    const isTableCell = !cell.isNullObject;
    return {
      isTableCell,
      tableRowIndex: isTableCell ? cell.rowIndex : undefined,
      tableCellIndex: isTableCell ? cell.cellIndex : undefined,
    };
  });
}

/**
 * Read the tables collection and build, per table, its paragraphs in Word's
 * row-major flatten order (see {@link TableRunCell}). Mints proxies only for the
 * document's TABLE cells/paragraphs (typically a handful), never one per body
 * paragraph — this is what makes the fast path cheap. Best-effort: returns
 * ``null`` on any error so the caller falls back to the exact per-paragraph probe.
 */
async function _readTableRuns(
  context: Word.RequestContext,
  tables: Word.TableCollection,
): Promise<TableRunCell[][] | null> {
  try {
    for (const t of tables.items) t.rows.load("items");
    await context.sync();

    for (const t of tables.items) {
      for (const row of t.rows.items) row.cells.load("items");
    }
    await context.sync();

    for (const t of tables.items) {
      for (const row of t.rows.items) {
        for (const cell of row.cells.items) {
          cell.load("rowIndex,cellIndex");
          cell.body.paragraphs.load("text");
        }
      }
    }
    await context.sync();

    return tables.items.map((t) => {
      const run: TableRunCell[] = [];
      for (const row of t.rows.items) {
        for (const cell of row.cells.items) {
          for (const para of cell.body.paragraphs.items) {
            run.push({ row: cell.rowIndex, col: cell.cellIndex, text: para.text });
          }
        }
      }
      return run;
    });
  } catch (err) {
    console.warn("[wordWriter] readCustomSpineOutline: bulk table read failed, using per-paragraph probe:", err);
    return null;
  }
}

/**
 * Read the live Word document's heading hierarchy into a custom spine outline.
 * Used by the "build on an existing document" flow. Returns document-ordered
 * sections; ``[]`` when Office is unavailable, on error, or when the body has
 * no headings (nothing to build a spine from).
 *
 * Distinct from {@link readDocumentOutline}, which is a lightweight mount-time
 * classifier: this one builds the full custom spine (synthetic ids, rolled-up
 * content status) the "build on an existing document" run is created from.
 *
 * Table-cell membership (which flat paragraphs sit in a table, and their
 * row/col) is recovered from the TABLES collection — a few proxies — rather than
 * a ``parentTableCellOrNullObject`` probe per body paragraph, which minted
 * O(document) proxies and hung Word-for-the-web for minutes on a large custom
 * document (the "Starting regulatory review…" audit hang; same shape as the
 * 2026-07-18 hydrate hang). The exact per-paragraph probe remains as a fallback
 * when a table can't be confidently placed, so the outline is byte-identical.
 */
export async function readCustomSpineOutline(): Promise<CustomSpineSection[]> {
  if (!isOfficeAvailable()) return [];
  try {
    return await runWord("readCustomSpineOutline", async (context) => {
      const paragraphs = context.document.body.paragraphs;
      const tables = context.document.body.tables;
      paragraphs.load("text,style,styleBuiltIn");
      tables.load("items");
      await context.sync();

      const flatTexts = paragraphs.items.map((p) => p.text);

      // Table-cell membership per flat paragraph. Fast path: recover it from the
      // (few) tables in the document; no table → every paragraph is body prose.
      // Fall back to the exact per-paragraph probe only when a table can't be
      // confidently placed — so the outline is identical either way.
      let membership: ParagraphTableMembership[] | null =
        tables.items.length === 0 ? flatTexts.map(() => ({ isTableCell: false })) : null;
      if (membership === null) {
        const tableRuns = await _readTableRuns(context, tables);
        if (tableRuns !== null) membership = _placeTableRunsInParagraphs(flatTexts, tableRuns);
      }
      if (membership === null) {
        membership = await _probeParagraphTableMembership(context, paragraphs);
      }

      const snapshots: ParagraphProbeSnapshot[] = paragraphs.items.map((para, i) => ({
        text: flatTexts[i],
        style: para.style || "",
        styleBuiltIn: para.styleBuiltIn || "",
        isTableCell: membership[i].isTableCell,
        tableRowIndex: membership[i].tableRowIndex,
        tableCellIndex: membership[i].tableCellIndex,
      }));
      return _outlineToCustomSpine(_buildOutlineFromParagraphs(snapshots));
    });
  } catch (err) {
    console.warn("[wordWriter] readDocumentOutline failed (non-fatal):", err);
    return [];
  }
}

/** Shape stored in the ``AsthrSessionContext`` custom property. */
export interface DocSessionContext {
  libraryId: string;
  libraryName: string;
  docCode: string;
  docName: string;
  backendDocType: string;
  workspaceId?: string | null;
}

/**
 * True when ``id`` is a plausible Sangnan library id (the value the
 * backend forwards verbatim to ``/api_v2/library/{id}/…``), false when
 * it's empty or a client-minted Word **document** id.
 *
 * Word doc ids are always minted with the ``doc-`` prefix (see
 * ``getDocumentId`` / ``rotateDocumentId`` in this module), whereas
 * Sangnan library ids are UUIDs. Passing a ``doc-…`` id where a library
 * id is expected makes Sangnan reject the request with HTTP 400 — the
 * value must never be stored as ``libraryId`` or sent as ``library_id``.
 * The check is deliberately narrow (reject the ``doc-`` prefix, not
 * "must be a UUID") so it can't false-reject a legitimately-shaped
 * library id from a future deployment.
 */
export function isValidLibraryId(id: string | null | undefined): id is string {
  const trimmed = (id ?? "").trim();
  if (!trimmed) return false;
  return !trimmed.toLowerCase().startsWith("doc-");
}

/**
 * Persist the active selection context to a single Word custom property
 * (``AsthrSessionContext``) as a JSON blob. Written on every selection
 * change so the addin can restore the selection from the document itself
 * on reload — even when the MongoDB session is missing or its
 * library/doctype fields are null (debounce-clobber / first-run edge cases).
 *
 * Best-effort: failures are logged but never surfaced to the writer.
 */
export async function saveDocSelectionProps(ctx: DocSessionContext): Promise<void> {
  if (!isOfficeAvailable()) return;
  // Never persist a Word doc id as the library id — a ``doc-…`` value here
  // resurfaces on reload and gets sent to Sangnan as ``library_id`` (HTTP 400).
  // Strip it to "" so a reload treats it as "no library configured" instead.
  const safeCtx: DocSessionContext = isValidLibraryId(ctx.libraryId)
    ? ctx
    : { ...ctx, libraryId: "" };
  if (safeCtx.libraryId !== ctx.libraryId) {
    console.warn("[wordWriter] dropping non-library libraryId from doc-selection props:", ctx.libraryId);
  }
  try {
    await runWord("saveDocSelectionProps", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();

      for (const item of props.items) {
        if (item.key === ASTHRA_SESSION_CONTEXT_KEY) {
          item.delete();
        }
      }
      props.add(ASTHRA_SESSION_CONTEXT_KEY, JSON.stringify(safeCtx));
      await context.sync();
    });
  } catch (err) {
    console.warn("[wordWriter] saveDocSelectionProps failed (non-fatal):", err);
  }
}

/**
 * Read the persisted selection context from Word custom properties.
 * Returns ``null`` when not set, unparseable, or Office.js unavailable.
 * Never throws.
 */
export async function loadDocSelectionProps(): Promise<DocSessionContext | null> {
  if (!isOfficeAvailable()) return null;
  try {
    return await runWord("loadDocSelectionProps", async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();

      for (const item of props.items) {
        if (item.key === ASTHRA_SESSION_CONTEXT_KEY) {
          try {
            return JSON.parse(item.value as string) as DocSessionContext;
          } catch {
            return null;
          }
        }
      }
      return null;
    });
  } catch (err) {
    console.warn("[wordWriter] loadDocSelectionProps failed (non-fatal):", err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Bookmark helpers — ephemeral anchors for content insertion
// ---------------------------------------------------------------------------

/**
 * Generate a deterministic bookmark name from a section number.
 * Used during scaffold to tag headings, and during insert to find them.
 *
 * Bookmark names in Word: max 40 chars, no spaces, start with letter.
 * Examples: ASTHRA_SEC_3, ASTHRA_SEC_9_2, ASTHRA_SEC_12_1_1
 */
function _sectionBookmarkName(sectionNumber: string): string {
  return sectionBookmarkName(sectionNumber);
}

/** True when ``name`` obeys Word's bookmark-name rules: starts with a letter or
 *  underscore, contains only letters/digits/underscores, and is ≤ 40 chars.
 *  ``getBookmarkRangeOrNullObject`` throws ``RichApi.Error InvalidArgument`` (not
 *  a null object) when handed a name that breaks these rules — e.g. a section
 *  number carrying a hyphen or other punctuation. Callers must check this before
 *  querying so an out-of-spec name degrades to "not found" instead of crashing
 *  the whole scan. An invalid name can never match a real bookmark anyway. */
function _isValidBookmarkName(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && name.length <= 40;
}

/** Encode a dotted section number (``9.2``) to its Word bookmark name
 *  (``ASTHRA_SEC_9_2``). Exposed so post-write services — citation-marker
 *  wrapping, context-walker — can resolve a section's range without
 *  duplicating the encoding. The private ``_sectionBookmarkName`` keeps
 *  the call site in this module short. */
export function sectionBookmarkName(sectionNumber: string): string {
  return `${ASTHRA_SEC_PREFIX}${sectionNumber.replace(/\./g, "_")}`;
}

/** Bookmark name prefix used by ``_sectionBookmarkName``. Exported so the
 *  light cursor-section detector can filter the bookmark name list returned
 *  by Word.js without hard-coding the literal in two places. Mirrors the
 *  decoder in ``sectionFromBookmark.ts``. */
export const ASTHRA_SEC_PREFIX = "ASTHRA_SEC_";

/** Decode an ``ASTHRA_SEC_<number-with-underscores>`` bookmark name back
 *  into a dotted section number. Returns null if the name doesn't match.
 *  Kept here so callers don't reimplement the encoding/decoding twice. */
export function decodeSectionBookmarkName(name: string): string | null {
  if (!name.startsWith(ASTHRA_SEC_PREFIX)) return null;
  return name.slice(ASTHRA_SEC_PREFIX.length).replace(/_/g, ".");
}

// ---------------------------------------------------------------------------
// 1. Scaffold: Create ICH E3 headings in a blank document
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Draft content flags — shared registry from /api/flags (SSOT)
// ---------------------------------------------------------------------------

/** A single flag from the shared registry (flags.yaml). */
interface DraftFlag {
  id: string;
  prefix: string;
  style: {
    italic?: boolean;
    color?: string; // hex, e.g. "#808080"
    bold?: boolean;
    highlight?: string | null; // e.g. "yellow" or null
  };
}

/** Cached flags from /api/flags. Loaded once, sorted by prefix length
 *  descending (longest match first). Both scaffold and updateTitlePageFields
 *  use this instead of inline startsWith checks. */
let _flagsCache: DraftFlag[] | null = null;

async function _loadFlags(): Promise<DraftFlag[]> {
  if (_flagsCache) return _flagsCache;
  try {
    const resp = await fetch(
      `${(import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE || "/api"}/flags`,
    );
    if (resp.ok) {
      const data = await resp.json();
      _flagsCache = (data.flags || []) as DraftFlag[];
      return _flagsCache;
    }
  } catch {
    // Fallback: if API is unavailable, use empty cache (no flag styling)
    console.warn("[wordWriter] Could not load flags from /api/flags");
  }
  _flagsCache = [];
  return _flagsCache;
}

/** Match a text value against the flags registry (longest prefix wins). */
function _matchFlag(text: string, flags: DraftFlag[]): DraftFlag | null {
  if (!text) return null;
  for (const flag of flags) {
    if (flag.prefix && text.startsWith(flag.prefix)) return flag;
  }
  return null;
}

/** Apply a flag's style to a Word font range. Shared by all renderers. */
function _applyFlagStyle(range: Word.Range | { font: Word.Font }, flag: DraftFlag): void {
  const s = flag.style;
  if (s.italic) range.font.italic = true;
  if (s.color) range.font.color = s.color;
  if (s.bold) range.font.bold = true;
  // Note: highlight is not directly supported on Word.Range.font in all
  // Office JS versions. Omitted for now — can be added when needed.
}

/** Title page formatting spec — read from doc-type YAML's title_page.style.
 *  SSOT: both python-docx (word_writer.py) and Office JS (this file) read
 *  from the same YAML block via the /api/doc-types endpoint. When a value
 *  is undefined, both renderers use the same default. */
export interface TitlePageStyle {
  font_family?: string;
  color_placeholder?: string;
  title_size?: number; // pt
  subtitle_size?: number; // pt
  confidentiality_size?: number; // pt
  table_font_size?: number; // pt
  label_column_width?: number; // inches (used by python-docx; Office JS uses autoFitWindow)
  value_column_width?: number; // inches
  spacers_before_title?: number;
  spacers_after_subtitle?: number;
  spacer_font_size?: number; // pt
}

/** Defaults matching the audit baseline — same values both renderers
 *  fall back to when the YAML doesn't specify a value. Changing a
 *  default here changes it for ALL doc types that don't override. */
const DEFAULT_TP_STYLE: Required<TitlePageStyle> = {
  font_family: "Times New Roman",
  color_placeholder: "#808080",
  title_size: 18,
  subtitle_size: 11,
  confidentiality_size: 9,
  table_font_size: 11,
  label_column_width: 2.5,
  value_column_width: 4.0,
  spacers_before_title: 4,
  spacers_after_subtitle: 2,
  spacer_font_size: 6,
};

/** Heading text for a scaffolded section, given the numbering regime.
 *
 *  - `suppress` (heading_numbering: none) → title only, no number.
 *  - `native` (Word multilevel list carries the number) → title only.
 *  - otherwise (literal fallback) → "3.2.P.1  Title" (two-space separator,
 *    matching the server's word_writer.py render).
 *
 *  Pure so the numbering decision can be unit-tested without a Word runtime.
 *  `suppress` takes precedence over `native`. */
function _scaffoldHeadingText(
  number: string,
  title: string,
  opts: { native: boolean; suppress: boolean },
): string {
  return opts.native || opts.suppress ? title : `${number}  ${title}`;
}

/** Options for scaffolding a document with a specific doc-type skeleton. */
export interface ScaffoldOptions {
  /** Skeleton headings — defaults to ICH_E3_SKELETON if not provided. */
  skeleton?: SectionHeading[];
  /** Title page fields — if provided, creates a pre-populated metadata
   *  table on the title page. If omitted or empty, the title page starts
   *  blank and the agent creates the table dynamically via submit_draft.
   *  Zero-translation: the label strings become the left column of the
   *  table verbatim. */
  titlePageFields?: Array<{ label: string; fill: string }>;
  /** Main title text — defaults to "CLINICAL STUDY REPORT". */
  documentTitle?: string;
  /** Confidentiality note — displayed centered below the title page
   *  metadata. Read from the doc-type YAML's title_page.confidentiality
   *  (the caller resolves the string-or-structured mapping via
   *  ``resolveConfidentiality``). Tri-state:
   *    • string    → render this statement;
   *    • ``null``   → doc-type explicitly declares no statement → OMIT the
   *                   line (M11 title-page HARD RULE);
   *    • ``undefined`` → not configured → fall back to the generic regulatory
   *                   statement (backward compat, e.g. CTD_M2 / legacy CSR). */
  confidentiality?: string | null;
  /** Formatting spec from the doc-type YAML's title_page.style block.
   *  Both renderers consume this; the YAML is the SSOT. */
  style?: TitlePageStyle;
  /** Force a full body clear before scaffolding. Default behaviour skips
   *  scaffolding when the body already has substantial content (so a live
   *  ``gate_start`` after the user typed something doesn't wipe their text).
   *  Session-refresh restore needs the opposite: re-project the backend SSOT
   *  onto the live doc unconditionally. */
  forceClear?: boolean;
  /** Carry heading numbers as NATIVE Word multilevel-list numbering
   *  (title-only heading text + Word List API) instead of literal
   *  ``"9.1  Title"`` strings — the same official numbering the server
   *  renders (csr_sim/heading_numbering.py). Applied only when the skeleton
   *  passes the shared positional validator (_planNativeNumbering) AND the
   *  host supports WordApi 1.3 lists; otherwise the literal fallback is
   *  used. Opt-in so the legacy taskpane caller (documentBridge) keeps its
   *  historical literal behavior. */
  nativeNumbering?: boolean;
  /** Suppress the section-number prefix entirely — headings render as the
   *  plain title with no leading "3.2.P.1" and no native list numbering.
   *  Mirrors the server's ``heading_numbering: none`` doc-type mode. The
   *  section number is still used to plant the ASTHRA_SEC_* bookmark (spine
   *  identity / navigation), it is simply not drawn. Takes precedence over
   *  ``nativeNumbering``. */
  suppressHeadingNumbers?: boolean;
}

/**
 * Convert API skeleton tuples `[section_number, title, level]` to SectionHeading[].
 * Use with the `skeleton` field from `getDocTypeConfig()`.
 */
export function skeletonFromTuples(tuples: [string, string, number][]): SectionHeading[] {
  return tuples.map(([number, title, level]) => ({
    number,
    title,
    level: (level <= 1 ? 1 : level === 2 ? 2 : 3) as 1 | 2 | 3,
  }));
}

/**
 * Insert heading skeleton into the current Word document.
 * Supports CSR (default) and other doc types (CER, PSUR, etc.) via options.
 * Only scaffolds if the document body is empty or very short.
 *
 * @returns The number of headings inserted, or 0 if skipped/unavailable.
 */

/**
 * Wipe the live Word document body so the writer can start fresh.
 *
 * SRP: this helper does ONE thing — clear the document. It does not
 * re-scaffold; the caller decides whether to follow up with
 * scaffoldDocument(). Decoupling lets the Reset button be reused for
 * "wipe and re-scaffold" or "wipe and leave blank" without conditional
 * logic in this helper.
 *
 * Returns true if the body was cleared, false if Office JS is unavailable
 * (e.g. browser preview, dev mode without Word).
 */
let clearInFlight = false;

const CLEAR_CHUNK_SIZE = 25;

function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Built-in Heading style name for a dotted section number, keyed on its depth:
 * ``"5"`` → ``"Heading1"``, ``"9.2"`` → ``"Heading2"``, ``"9.2.1"`` → ``"Heading3"``.
 * Capped at ``Heading9`` (Word's deepest built-in heading). Pure — exported via
 * ``_testing``.
 *
 * Used to re-apply heading appearance AFTER a no-``importStyles`` insert: the
 * server's custom ``AsthraHeadingN`` styles aren't imported (importStyles is
 * omitted because it throws GeneralException / "getTypeName is not a function"
 * on Word-for-the-web — OfficeDev/office-js #4463, #4682, #4908), so headings
 * would otherwise render as plain body text. Mapping each to the built-in
 * ``HeadingN`` — which ``_applyStyleSpecToContext`` normalizes to the server
 * spec — restores the correct look. The section number itself is literal text
 * baked in by the server, so it survives regardless.
 */
function _builtInHeadingStyleForNumber(number: string): string {
  const depth = number.split(".").filter(Boolean).length;
  const level = Math.min(Math.max(depth, 1), 9);
  return `Heading${level}`;
}

/**
 * Reset the live document to a first-load-equivalent clean slate on an existing
 * Word context (caller owns the surrounding Word.run). Deletes content controls
 * FIRST (outside-in) — CSR drafts nest tables inside SDTs, so dropping the SDT
 * removes its tables and avoids the conflict of the table-sweep deleting
 * something a CC-delete already removed — then sweeps residual top-level tables,
 * then clears the body (which also drops any body bookmarks).
 *
 * NOTE: does NOT touch styles. An earlier version deleted the custom Asthra
 * styles here to dodge a re-import style collision, but Word-for-web's
 * getStyles() doesn't even enumerate them (the delete found zero), and churning
 * styles before insertFileFromBase64 is exactly what degrades the web insert
 * (OfficeDev/office-js #4463). The real fix is to omit importStyles on the
 * insert (see hydrateLiveDocFromBase64) — no style deletion needed.
 */
async function _resetLiveDocForReimport(
  context: Word.RequestContext,
): Promise<{ contentControlsRemoved: number; tablesRemoved: number }> {
  const body = context.document.body;

  // Step 1: delete content controls FIRST (outside-in, chunked).
  const ccs = body.contentControls;
  ccs.load("items");
  await context.sync();
  const contentControlsRemoved = ccs.items.length;
  for (let i = ccs.items.length - 1; i >= 0; i -= CLEAR_CHUNK_SIZE) {
    const start = Math.max(0, i - CLEAR_CHUNK_SIZE + 1);
    for (let j = i; j >= start; j--) {
      try {
        ccs.items[j].delete(false); // false = delete the content too
      } catch {
        /* some CCs are not deletable; ignore */
      }
    }
    await context.sync();
    await yieldToUi();
  }

  // Step 2: sweep any tables that survived (top-level tables not wrapped in a
  // CC). body.clear() does NOT always remove tables — they can persist as
  // orphan structures.
  const tables = body.tables;
  tables.load("items");
  await context.sync();
  const tablesRemoved = tables.items.length;
  for (let i = tables.items.length - 1; i >= 0; i -= CLEAR_CHUNK_SIZE) {
    const start = Math.max(0, i - CLEAR_CHUNK_SIZE + 1);
    for (let j = i; j >= start; j--) {
      try {
        tables.items[j].delete();
      } catch {
        /* table may have been removed via parent CC; ignore */
      }
    }
    await context.sync();
    await yieldToUi();
  }

  // Step 3: clear remaining body text (also drops body bookmarks).
  body.clear();
  await context.sync();

  return { contentControlsRemoved, tablesRemoved };
}

export async function clearDocument(): Promise<boolean> {
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — cannot clear document");
    return false;
  }
  // Re-entry guard: a second Reset click while the first clear is still
  // chewing would race on the same Office.js context and compound the hang.
  if (clearInFlight) {
    console.warn("[wordWriter] clearDocument already in flight — ignoring duplicate call");
    return false;
  }
  clearInFlight = true;
  try {
    return await runWord("clearDocument", async (context) => {
      // Reset via the shared helper. Reset (this path) preserves its prior
      // behavior — CC-delete → table-sweep → body.clear(), no style deletion.
      const { contentControlsRemoved, tablesRemoved } = await _resetLiveDocForReimport(context);
      console.log(
        `[wordWriter] clearDocument complete: removed ${contentControlsRemoved} content control(s), ${tablesRemoved} residual table(s), and cleared body`,
      );
      return true;
    });
  } finally {
    clearInFlight = false;
  }
}

/**
 * Seed an empty document with a lightweight status message for batch mode.
 * This runs when the pipeline starts in insertion_mode="batch" so the
 * writer sees immediate feedback even though section content is not streamed
 * into Word until completion.
 *
 * Best-effort: no-op when Office.js is unavailable and never throws.
 */
export async function populateBatchPlaceholder(docTypeLabel: string): Promise<void> {
  if (!isOfficeAvailable()) return;

  try {
    await runWord("populateBatchPlaceholder", async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();

      // Never overwrite existing user content.
      if (body.text.trim().length > 0) return;

      const title = body.insertParagraph(`Drafting your ${docTypeLabel}`, "End");
      title.alignment = "Centered";
      title.font.name = "Times New Roman";
      title.font.size = 16;
      title.font.bold = true;
      title.font.color = brand.colorPrimary;

      const spacer = body.insertParagraph("", "End");
      spacer.font.size = 10;

      const statusLine1 = body.insertParagraph(
        "The full document will assemble automatically when the run finishes.",
        "End",
      );
      statusLine1.alignment = "Centered";
      statusLine1.font.name = "Times New Roman";
      statusLine1.font.size = 11;
      statusLine1.font.bold = true;
      statusLine1.font.color = "#000000";

      const statusLine2 = body.insertParagraph(
        'You can close this window — the run keeps going on the server. Track live progress in the side panel, then Load or Open the document when it\'s ready from "Drafts" tab.',
        "End",
      );
      statusLine2.alignment = "Centered";
      statusLine2.font.name = "Times New Roman";
      statusLine2.font.size = 11;
      statusLine2.font.color = "#333333";

      await context.sync();
    });
  } catch (err) {
    console.warn("[wordWriter] populateBatchPlaceholder failed (non-fatal):", err);
  }
}

export async function scaffoldDocument(options?: ScaffoldOptions): Promise<number> {
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — skipping scaffold");
    return 0;
  }

  return runWord("scaffoldDocument", async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();

    // Only scaffold if document is essentially empty — unless the caller
    // asked for a forceClear (session-refresh restore re-projects the
    // backend SSOT onto whatever the live doc currently shows).
    const currentText = body.text.trim();
    if (!options?.forceClear && currentText.length > 200) {
      console.log("[wordWriter] Document already has content — skipping scaffold");
      return 0;
    }

    // Clear any placeholder content (or all content when forceClear).
    if (currentText.length > 0) {
      body.clear();
      await context.sync();
    }

    // Read formatting from the doc-type YAML's title_page.style block.
    // Merge with defaults so every value is defined.
    const sty = { ...DEFAULT_TP_STYLE, ...options?.style };

    // Configure document styles for regulatory formatting
    const normalStyle = context.document.getStyles().getByNameOrNullObject("Normal");
    normalStyle.load("font");
    await context.sync();
    if (!normalStyle.isNullObject) {
      normalStyle.font.name = sty.font_family;
      normalStyle.font.size = sty.table_font_size;
    }

    // Resolve skeleton and title page from options or defaults
    const skeleton: SectionHeading[] = options?.skeleton ?? ICH_E3_SKELETON;
    const documentTitle = options?.documentTitle ?? "CLINICAL STUDY REPORT";

    // Title page metadata fields — if provided by the doc-type config,
    // scaffold creates a pre-populated table. If NOT provided (CER since
    // the zero-translation refactor — cer.yaml no longer has a fields
    // registry), the title page starts BLANK and the agent creates the
    // table dynamically via submit_draft(title_fields={...}).
    //
    // Same principle as section headings: scaffold creates the structure
    // (centered title + confidentiality note), agent fills the content.
    const titlePageFields: Array<{ label: string; fill: string }> = options?.titlePageFields ?? [];

    // --- Title page (before section headings) ---

    // Document type centered title (static chrome). Guard the empty case:
    // ``documentTitle`` can arrive as "" (loadRunIntoLiveDoc falls back to ""
    // when a doc-type declares neither a title_page.title banner nor a
    // display_name), and Office.js ``insertParagraph("")`` throws
    // InvalidArgument — which would abort the whole Load.
    if (documentTitle.trim().length > 0) {
      const csrTitle = body.insertParagraph(documentTitle, "End");
      csrTitle.alignment = "Centered";
      csrTitle.font.name = sty.font_family;
      csrTitle.font.size = sty.title_size;
      csrTitle.font.bold = true;
    }

    // Only create the pre-populated field table if the doc-type config
    // provided a fields list (CSR legacy path). For CER and other doc
    // types using the zero-translation design, the table is created
    // dynamically by updateTitlePageFields when the agent submits.
    if (titlePageFields.length > 0) {
      // Study title placeholder (centered, bold) — CSR legacy
      const studyTitlePara = body.insertParagraph("[PENDING: Study Title]", "End");
      studyTitlePara.alignment = "Centered";
      studyTitlePara.font.name = sty.font_family;
      studyTitlePara.font.size = sty.title_size - 4; // slightly smaller than main title
      studyTitlePara.font.bold = true;

      // Spacer
      const spacerTop = body.insertParagraph("", "End");
      spacerTop.font.size = sty.spacer_font_size;

      // Metadata table (borderless, left-aligned labels)
      const table = body.insertTable(titlePageFields.length, 2, "End");

      for (let r = 0; r < titlePageFields.length; r++) {
        const { label, fill } = titlePageFields[r];

        // Label cell. Office.js ``insertText("")`` throws InvalidArgument, so
        // only write a non-empty label — an empty cell is a valid outcome.
        const labelCell = table.getCell(r, 0);
        const labelPara = labelCell.body.paragraphs.getFirst();
        labelPara.clear();
        if (label && label.length > 0) {
          labelPara.insertText(label, "Start");
        }
        labelPara.font.name = sty.font_family;
        labelPara.font.size = sty.table_font_size;

        // Value cell. Same empty-string guard; skip flag styling when the cell
        // is left blank (no range to style, and no flag can match "").
        const valueCell = table.getCell(r, 1);
        const valuePara = valueCell.body.paragraphs.getFirst();
        valuePara.clear();
        if (fill && fill.length > 0) {
          const valueRange = valuePara.insertText(fill, "Start");
          const flag = _matchFlag(fill, await _loadFlags());
          if (flag) {
            _applyFlagStyle(valueRange, flag);
          }
        }
        valuePara.font.name = sty.font_family;
        valuePara.font.size = sty.table_font_size;
      }

      // Remove table borders
      table.style = "Table Grid";
      table.getBorder("Top").type = "None";
      table.getBorder("Bottom").type = "None";
      table.getBorder("Left").type = "None";
      table.getBorder("Right").type = "None";
      table.getBorder("InsideHorizontal").type = "None";
      table.getBorder("InsideVertical").type = "None";
    }

    // Confidentiality statement — from doc-type config, not hardcoded.
    // Tri-state (see ScaffoldOptions.confidentiality): null = explicit omit
    // (M11 HARD RULE), undefined = not configured → generic default, string =
    // render verbatim. Guarding on a non-empty string also prevents Office.js
    // ``insertParagraph`` from throwing InvalidArgument on a non-string arg.
    const confidentialityText =
      options?.confidentiality === null
        ? undefined
        : options?.confidentiality ||
          "This document is confidential and is intended solely for the use of regulatory authorities.";
    if (typeof confidentialityText === "string" && confidentialityText.trim().length > 0) {
      const spacerBottom = body.insertParagraph("", "End");
      spacerBottom.font.size = sty.spacer_font_size;
      const confStatement = body.insertParagraph(confidentialityText, "End");
      confStatement.alignment = "Centered";
      confStatement.font.name = sty.font_family;
      confStatement.font.size = sty.confidentiality_size;
      confStatement.font.italic = true;
    }

    // --- Section headings with page breaks for Level 1 ---

    // Native numbering gate: mirror of the server's plan_native_numbering —
    // title-only heading text with the number carried by a Word multilevel
    // list, so scaffolded docs (live SSE drafting, per-section Load
    // fallback) match the server-rendered .docx instead of diverging into
    // literal-string numbers. Decided UP FRONT (before any text is written)
    // so an unsupported host simply keeps the literal regime — no
    // mixed-state doc.
    // heading_numbering: none → title-only headings, no number prefix and no
    // native list numbering. The number still plants the ASTHRA_SEC_* bookmark
    // below. Takes precedence over nativeNumbering.
    const suppressNumbers = options?.suppressHeadingNumbers === true;
    const nativeStart =
      options?.nativeNumbering && !suppressNumbers
        ? _planNativeNumbering(skeleton.map((h) => [h.number, h.level] as [string, number]))
        : null;
    const native = nativeStart !== null && _supportsListApi();
    const nativeEntries: Array<{ para: Word.Paragraph; number: string }> = [];

    for (const heading of skeleton) {
      // No skip logic needed. The title-page chrome is rendered above
      // from the doc-type YAML's title_page: block (SSOT). Every row
      // in `skeleton` is a real section heading that should render.
      const headingText = _scaffoldHeadingText(heading.number, heading.title, { native, suppress: suppressNumbers });
      const para = body.insertParagraph(headingText, "End");
      if (native) nativeEntries.push({ para, number: heading.number });

      // Tag the heading with a bookmark for reliable content insertion.
      // The bookmark is an ephemeral anchor — used at insertion time,
      // doesn't matter if corrupted later. ``insertBookmark`` throws
      // InvalidArgument on an out-of-spec name (a section number carrying a
      // hyphen/punctuation, or one long enough to exceed Word's 40-char limit),
      // so validate first — mirroring the reverse-lookup guard at
      // getBookmarkRangeOrNullObject. An invalid anchor is simply skipped; the
      // text-match strategy still resolves the section at insertion time.
      const bookmarkName = _sectionBookmarkName(heading.number);
      if (_isValidBookmarkName(bookmarkName)) {
        para.getRange("Whole").insertBookmark(bookmarkName);
      }

      if (heading.level === 1) {
        para.styleBuiltIn = "Heading1";
        para.font.name = "Times New Roman";
        para.font.size = 14;
        para.font.bold = true;
        para.font.color = "#000000";
        para.spaceAfter = 6;
        para.insertBreak("Page", "Before");
      } else if (heading.level === 2) {
        para.styleBuiltIn = "Heading2";
        para.font.name = "Times New Roman";
        para.font.size = 12;
        para.font.bold = true;
        para.font.color = "#000000";
      } else {
        para.styleBuiltIn = "Heading3";
        para.font.name = "Times New Roman";
        para.font.size = 11;
        para.font.bold = true;
        para.font.color = "#000000";
      }
    }

    await context.sync();

    if (native) {
      // Build the multilevel list AFTER the headings exist in the doc (the
      // sync above realizes them). Non-fatal by construction: a failed
      // attach leaves title-only headings, which the convergence pass tops
      // up with literal numbers so a visible number is guaranteed either
      // way.
      try {
        const attached = await _applyIchHeadingList(context, nativeEntries, nativeStart);
        console.log(`[wordWriter] scaffold: native list attach ${attached}/${nativeEntries.length}`);
      } catch (err) {
        console.warn(`[wordWriter] scaffold: native-numbering attach failed (non-fatal): ${_wordErrDetail(err)}`);
      }
      try {
        await _ensureVisibleHeadingNumbers(context, nativeEntries);
      } catch (err) {
        console.warn(`[wordWriter] scaffold: numbering convergence pass failed (non-fatal): ${_wordErrDetail(err)}`);
      }
    }

    console.log(
      `[wordWriter] Scaffolded ${skeleton.length} headings (numbering=${
        suppressNumbers ? "none" : native ? "native" : "literal"
      })`,
    );
    return skeleton.length;
  });
}

/**
 * Extract the dotted section number from a rendered heading paragraph's text
 * (e.g. ``"9.2  Adverse Events"`` → ``"9.2"``, ``"3.2.S  Drug Substance"`` →
 * ``"3.2.S"``). Returns null when the text doesn't start with a section number.
 *
 * Broader than {@link _parseSectionNumber} (which is numeric-only) because CMC
 * skeletons carry letter-bearing numbers like ``3.2.S.1``. Pure + exported via
 * ``_testing`` so the bookmark-replant grammar is unit-tested without Office.
 */
/**
 * Check if a paragraph is a heading using either built-in (Heading1/2/3) or
 * custom AsthraHeading1/2/3 styles. Returns true if the paragraph's style
 * contains "heading" in either styleBuiltIn or the display style name.
 */
function _isHeadingParagraph(para: Word.Paragraph): boolean {
  return _isHeadingStyle(para.styleBuiltIn, para.style);
}

function _extractHeadingNumber(headingText: string): string | null {
  const match = headingText.trim().match(/^(\d+(?:\.[\dA-Za-z]+)*)[\s.]+\S/);
  return match ? match[1] : null;
}

/**
 * Derive a section number from a heading's rendered list label
 * (``ListItem.listString``). Headings styled AsthraHeading1/2/3 carry their
 * numbers via multilevel LIST NUMBERING, which Word renders but does NOT
 * include in ``paragraph.text`` — so ``_extractHeadingNumber`` misses them
 * and the caller must fall back to the list label ("7.", "9.2") instead.
 */
function _numberFromListString(listString: string | null | undefined): string | null {
  if (!listString) return null;
  const trimmed = listString.trim().replace(/\.$/, "");
  return /^\d+(?:\.[\dA-Za-z]+)*$/.test(trimmed) ? trimmed : null;
}

// ---------------------------------------------------------------------------
// Live-document normalization — make the live Word session match the
// server-rendered .docx so a Loaded doc is pixel-identical to the Downloaded
// one. See src/csr_sim/live_doc_style_spec.json (shipped via the doc-type
// config payload) — the single source of truth both renderers read.
//
// WHY this is needed: insertFileFromBase64 imports the file's BODY, but when a
// style name (Normal, Heading 1..3) exists in BOTH the file and the live doc,
// the live doc's definition WINS. So we pre-set the live doc's built-in styles
// to the spec; after that the collision resolves to identical values.
//
// SCOPE NOTE: the Word JS API has NO setter for page size / margins /
// orientation (Word.Section exposes only body + getHeader/getFooter). Page
// geometry therefore rides in on the inserted file's sectPr — which is the
// same file the user downloads, so it matches by construction. Header/footer
// CONTENT is settable, so we (re)apply it from the spec as reinforcement
// (idempotent: clear-then-set, identical text to the server's header).
// ---------------------------------------------------------------------------

interface StylePlanEntry {
  styleName: string; // built-in display name, e.g. "Normal", "Heading 1"
  font: string;
  sizePt: number;
  bold?: boolean;
  colorHex?: string; // "#RRGGBB"
  spaceBeforePt?: number;
  spaceAfterPt?: number;
  lineSpacingPt?: number; // EXACT points (Office.js has no multiple setter)
}

/** Derive the per-style settings to apply from the shared spec. Pure — the
 *  drift guard: this MUST read every value from {@link spec}, never a literal,
 *  so the live doc and the download stay in lockstep. */
function _styleSpecToStylePlan(spec: LiveDocStyleSpec): StylePlanEntry[] {
  const plan: StylePlanEntry[] = [
    {
      styleName: "Normal",
      font: spec.normal.font,
      sizePt: spec.normal.size_pt,
      spaceAfterPt: spec.normal.space_after_pt,
      lineSpacingPt: spec.normal.line_spacing_pt,
    },
  ];
  for (const h of spec.headings) {
    plan.push({
      styleName: `Heading ${h.level}`,
      font: spec.heading_common.font,
      sizePt: h.size_pt,
      bold: spec.heading_common.bold,
      colorHex: `#${spec.heading_common.color}`,
      spaceBeforePt: h.space_before_pt,
      spaceAfterPt: spec.heading_common.space_after_pt,
    });
  }
  return plan;
}

type SpecAlignment = "left" | "center" | "right";

interface HeaderFooterPlan {
  headerText: string;
  headerAlignment: SpecAlignment;
  headerSizePt: number;
  headerColorHex: string;
  footerAlignment: SpecAlignment;
  footerSizePt: number;
  footerColorHex: string;
}

/** Derive header/footer settings from the shared spec. Pure and Word-free
 *  (alignment stays a spec string; the Word.Alignment mapping happens in the
 *  apply step) so it is unit-testable without an Office host. */
function _pageSpecToHeaderFooterPlan(spec: LiveDocStyleSpec, headerText: string): HeaderFooterPlan {
  return {
    headerText,
    headerAlignment: spec.header.alignment,
    headerSizePt: spec.header.size_pt,
    headerColorHex: `#${spec.header.color}`,
    footerAlignment: spec.footer.alignment,
    footerSizePt: spec.footer.size_pt,
    footerColorHex: `#${spec.footer.color}`,
  };
}

/** Map a spec alignment string to the Word enum. Called only inside a live
 *  Word context (keeps the top of this module free of runtime Word refs). */
function _toWordAlignment(alignment: SpecAlignment): Word.Alignment {
  switch (alignment) {
    case "left":
      return Word.Alignment.left;
    case "center":
      return Word.Alignment.centered;
    default:
      return Word.Alignment.right;
  }
}

/** Apply the style spec to the live doc's built-in styles on an existing
 *  Word context (caller owns the surrounding Word.run). */
async function _applyStyleSpecToContext(context: Word.RequestContext, spec: LiveDocStyleSpec): Promise<void> {
  const styles = context.document.getStyles();
  const plan = _styleSpecToStylePlan(spec);
  const handles = plan.map((entry) => ({
    entry,
    style: styles.getByNameOrNullObject(entry.styleName),
  }));
  for (const h of handles) h.style.load("isNullObject");
  await context.sync();

  for (const { entry, style } of handles) {
    if (style.isNullObject) continue; // built-in not present (shouldn't happen)
    style.font.name = entry.font;
    style.font.size = entry.sizePt;
    if (entry.bold !== undefined) style.font.bold = entry.bold;
    if (entry.colorHex) style.font.color = entry.colorHex;
    const pf = style.paragraphFormat;
    if (entry.spaceBeforePt !== undefined) pf.spaceBefore = entry.spaceBeforePt;
    if (entry.spaceAfterPt !== undefined) pf.spaceAfter = entry.spaceAfterPt;
    if (entry.lineSpacingPt !== undefined) pf.lineSpacing = entry.lineSpacingPt;
  }
  await context.sync();
}

/** Apply header/footer content from the spec on an existing Word context.
 *  Best-effort: page geometry is not settable via the Word JS API, so this
 *  only governs header/footer text + run styling (idempotent clear-then-set).
 *  The page-number field is attempted separately and tolerated if the host
 *  lacks insertField. */
async function _applyHeaderFooterToContext(
  context: Word.RequestContext,
  spec: LiveDocStyleSpec,
  headerText: string,
): Promise<void> {
  const plan = _pageSpecToHeaderFooterPlan(spec, headerText);
  const section = context.document.sections.getFirst();

  const header = section.getHeader(Word.HeaderFooterType.primary);
  header.clear();
  const hp = header.insertParagraph(plan.headerText, Word.InsertLocation.start);
  hp.alignment = _toWordAlignment(plan.headerAlignment);
  hp.font.size = plan.headerSizePt;
  hp.font.color = plan.headerColorHex;

  const footer = section.getFooter(Word.HeaderFooterType.primary);
  footer.clear();
  const fp = footer.insertParagraph("", Word.InsertLocation.start);
  fp.alignment = _toWordAlignment(plan.footerAlignment);
  fp.font.size = plan.footerSizePt;
  fp.font.color = plan.footerColorHex;
  await context.sync();

  // Page-number field — separate sync so an unsupported insertField on older
  // hosts leaves the styled (text-only) footer intact rather than aborting.
  try {
    fp.getRange(Word.RangeLocation.end).insertField(Word.InsertLocation.end, Word.FieldType.page);
    await context.sync();
  } catch (err) {
    console.warn("[wordWriter] footer PAGE field insert unsupported on this host (non-fatal):", err);
  }
}

/**
 * Normalize the live document's built-in styles to the shared spec so a
 * subsequent whole-file insert renders identically to the download. Standalone
 * wrapper (own Word.run) — also called inline by {@link hydrateLiveDocFromBase64}.
 */
export async function normalizeLiveDocStyles(spec: LiveDocStyleSpec): Promise<void> {
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — skipping style normalize");
    return;
  }
  await runWord("normalizeLiveDocStyles", (context) => _applyStyleSpecToContext(context, spec));
}

/**
 * Apply header/footer content from the shared spec to the live document.
 * Page geometry is not settable via the Word JS API (it rides in on the
 * inserted file's sectPr); this governs header/footer only.
 */
export async function normalizeLiveDocPageSetup(spec: LiveDocStyleSpec, headerText: string): Promise<void> {
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — skipping page-setup normalize");
    return;
  }
  await runWord("normalizeLiveDocPageSetup", (context) => _applyHeaderFooterToContext(context, spec, headerText));
}

/**
 * Hydrate the live document by inserting the server-rendered ``.docx`` whole,
 * then re-planting the ``ASTHRA_SEC_*`` section bookmarks the add-in relies on
 * for content insertion and citation/chat-edit anchoring.
 *
 * This is the RESTORE path (a terminal run whose bundle exists). It makes the
 * live document byte-identical to the downloaded file by construction, instead
 * of re-deriving the body from JSON markdown client-side (the old
 * ``loadRunIntoLiveDoc`` path, which could drift or silently write nothing).
 *
 * The server export uses python-docx ``add_heading`` (Heading 1/2/3 styles)
 * but emits NO bookmarks, so step 3 walks the rendered headings and plants the
 * bookmarks using the same {@link _sectionBookmarkName} encoding the scaffold
 * uses — keeping the naming convention single-sourced in TypeScript.
 *
 * @param base64Docx the .docx encoded as base64 (no data-URI prefix).
 * @param opts.styleSpec the shared live-doc style spec (from the doc-type
 *   config). When provided, the live doc's built-in styles are normalized to
 *   it BEFORE the insert so the style collision resolves to identical values —
 *   without it, body/heading spacing drifts from the download.
 * @param opts.headerText the page-header string (composed by the caller as
 *   ``{header_prefix} — {project}``, matching the server). Applied AFTER the
 *   insert. Requires styleSpec.
 * @param opts.skeleton the doc-type skeleton tuples `[number, title, level]`
 *   (from ``getDocTypeConfig().skeleton``). Used as the LAST recovery tier for
 *   a heading's section number (title match) and to rebuild native list
 *   numbering when the insert dropped it — see STEP3.5.
 * @returns sectionsWritten (heading count, satisfies the restore marker gate),
 *   bookmarksPlanted, and plantedSections (the dotted numbers that got a
 *   bookmark, so the caller can re-anchor citations for exactly those).
 */
export async function hydrateLiveDocFromBase64(
  base64Docx: string,
  opts?: {
    styleSpec?: LiveDocStyleSpec;
    headerText?: string;
    skeleton?: [string, string, number][];
    /** "none" mirrors the server's ``heading_numbering: none`` doc-type mode:
     *  the loaded .docx already carries title-only headings, so skip the
     *  numbering rebuild + convergence pass (they would re-add a visible
     *  number). Section numbers are still resolved from the skeleton title-map
     *  and their ASTHRA_SEC_* bookmarks planted — spine identity is unaffected. */
    headingNumbering?: "auto" | "none";
  },
): Promise<{
  sectionsWritten: number;
  bookmarksPlanted: number;
  plantedSections: string[];
}> {
  console.log(`[wordWriter] hydrateLiveDocFromBase64 called base64Length=${base64Docx.length}`);
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — skipping base64 hydrate");
    return { sectionsWritten: 0, bookmarksPlanted: 0, plantedSections: [] };
  }
  if (base64Docx.length === 0) {
    console.warn(
      "[wordWriter] hydrateLiveDocFromBase64 called with EMPTY base64 string — aborting before clearing the document body to avoid wiping content with nothing to replace it",
    );
    return { sectionsWritten: 0, bookmarksPlanted: 0, plantedSections: [] };
  }

  // Mark the live doc as loading/settling for the whole hydrate (insert +
  // STEP3/3.5 numbering rebuild). Background body-mutating work — the
  // regulatory-audit re-projection — stands down until this settles, so it
  // can't race the still-settling doc and blank a large region.
  beginLiveDocLoad();
  try {
    return await runWord("hydrateLiveDocFromBase64", async (context) => {
    // Per-step wall-clock so a slow Load (Word web degrades on repeated
    // whole-document Replace inserts) is attributable from the console log.
    const tHydrateStart = Date.now();
    // Track Changes OFF — the whole-document insert must not be recorded as
    // tracked revisions (mirrors insertSectionContent).
    try {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();
      if ((doc.changeTrackingMode as string) === "TrackAll") {
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
        await context.sync();
        console.log("[wordWriter] hydrateLiveDocFromBase64: change tracking was TrackAll — switched off");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.log(
        `[wordWriter] hydrateLiveDocFromBase64: change-tracking check failed (non-critical) error="${message}"`,
      );
    }

    // Step 0: normalize the live doc's built-in styles to the shared spec
    // BEFORE the insert, so same-named style collisions (Normal, Heading 1..3)
    // resolve to the server's exact values. This is what makes the inserted
    // body+headings render identically to the downloaded .docx.
    if (opts?.styleSpec) {
      try {
        await _applyStyleSpecToContext(context, opts.styleSpec);
      } catch (err) {
        console.warn("[wordWriter] hydrateLiveDocFromBase64: style normalize failed (continuing):", err);
      }
    }

    const body = context.document.body;

    // Step 1: reset the live doc to a first-load-equivalent clean slate — delete
    // content controls (citation/section SDTs), sweep residual tables, clear the
    // body. This is what makes a SECOND "Load doc" merge into a clean target.
    const resetCounts = await _resetLiveDocForReimport(context);
    console.log(
      `[wordWriter] hydrateLiveDocFromBase64: STEP1 reset done contentControls=${resetCounts.contentControlsRemoved} tables=${resetCounts.tablesRemoved} elapsedMs=${Date.now() - tHydrateStart}`,
    );

    // Step 2: insert the server-rendered document whole. Replace on the document
    // drops in the complete file (title page + headings + content + tables +
    // images) in one operation.
    // We DELIBERATELY omit the InsertFileOptions { importStyles: true } argument.
    // On Word-for-the-web that option throws GeneralException /
    // "b.getTypeName is not a function" on a re-load (styles accumulate across
    // repeated Replace inserts), which partially inserts then fails — leaving a
    // truncated doc (OfficeDev/office-js #4463, #4682, #4908). Omitting it is the
    // documented workaround. The custom AsthraHeadingN styles are therefore NOT
    // imported; STEP3 re-applies the built-in HeadingN style (normalized to the
    // server spec in STEP0) to each heading so appearance is preserved. The ICH
    // section numbers ride in as direct paragraph list numPr (native numbering)
    // — Word web can drop that on this insert, so STEP3.5 rebuilds the list
    // when it didn't survive.
    context.document.insertFileFromBase64(base64Docx, Word.InsertLocation.replace);
    try {
      await context.sync();
      console.log(
        `[wordWriter] hydrateLiveDocFromBase64: STEP2 insertFileFromBase64 sync succeeded elapsedMs=${Date.now() - tHydrateStart}`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code =
        typeof err === "object" && err !== null && "code" in err ? String((err as { code: unknown }).code) : "unknown";
      console.error(
        `[wordWriter] hydrateLiveDocFromBase64: STEP2 insertFileFromBase64 FAILED code=${code} error="${message}"`,
      );
      throw err;
    }

    // Step 2b: (re)apply header/footer from the spec AFTER the insert. Page
    // geometry rides in on the file's sectPr (no Word JS setter exists); this
    // governs only the header/footer content, which we set idempotently so it
    // matches the server whether or not the insert imported the file's header.
    if (opts?.styleSpec && opts?.headerText) {
      try {
        await _applyHeaderFooterToContext(context, opts.styleSpec, opts.headerText);
      } catch (err) {
        console.warn("[wordWriter] hydrateLiveDocFromBase64: header/footer normalize failed (non-fatal):", err);
      }
    }

    // Step 3: re-plant ASTHRA_SEC_* bookmarks by walking the rendered headings.
    const paragraphs = body.paragraphs;
    paragraphs.load("text,style,styleBuiltIn");
    await context.sync();
    console.log(`[wordWriter] hydrateLiveDocFromBase64: STEP3 post-insert paragraphCount=${paragraphs.items.length}`);

    // PERF: mint per-paragraph proxies ONLY for heading-styled paragraphs.
    // parentTableCellOrNullObject / listItemOrNullObject each create a
    // tracked proxy, and doing that for EVERY paragraph put O(document)
    // objects into a single sync — thousands on a fully-drafted CSR, which
    // Word-for-the-web served in minutes while the page stayed blank (the
    // "title page + empty pages, content pops in after ~2min" Load hang,
    // 2026-07-18). Headings are ~two orders of magnitude fewer; the style
    // props needed to find them were bulk-loaded in one command above.
    //
    // Server headings (AsthraHeading1/2/3) carry their numbers via
    // multilevel LIST numbering — the number is rendered but absent from
    // paragraph.text, so the list label is the only place to recover it
    // from.
    const headingIndexes: number[] = [];
    for (let i = 0; i < paragraphs.items.length; i++) {
      if (_isHeadingParagraph(paragraphs.items[i])) headingIndexes.push(i);
    }
    const parentCellByIndex = new Map<number, Word.TableCell>();
    const listItemByIndex = new Map<number, Word.ListItem>();
    for (const i of headingIndexes) {
      const cell = paragraphs.items[i].parentTableCellOrNullObject;
      cell.load("isNullObject");
      parentCellByIndex.set(i, cell);
      const li = paragraphs.items[i].listItemOrNullObject;
      li.load("isNullObject,listString");
      listItemByIndex.set(i, li);
    }
    await context.sync();
    console.log(
      `[wordWriter] hydrateLiveDocFromBase64: STEP3 heading walk headings=${headingIndexes.length}/${paragraphs.items.length} elapsedMs=${Date.now() - tHydrateStart}`,
    );

    // Last recovery tier: skeleton title → section number (mirrors the
    // server-side recovery in docservice/routes/blocks.py). Needed when the
    // number is neither in the text (native-numbered docs are title-only) nor
    // in the list label (Word web can drop the numbering on the insert).
    const skeletonTitleMap = _skeletonTitleMap(opts?.skeleton);

    let headingCount = 0;
    const plantedSections: string[] = [];
    // STEP3.5 bookkeeping — which headings resolved a number, and whether the
    // native numbering survived the insert. CRITICAL: "survived" means the
    // list label actually RENDERS a number (fromList) — NOT that
    // listItemOrNullObject is non-null. Word web keeps each paragraph's numPr
    // (body content) while dropping the numbering DEFINITIONS, so paragraphs
    // still resolve as ListItem objects with a dangling reference and an
    // empty label; counting those as "survived" is what made the rebuild
    // never fire (the no-numbers-on-Load bug, 2026-07-18).
    const numberedHeadings: Array<{ index: number; number: string }> = [];
    let textNumberedCount = 0;
    let renderedListCount = 0;

    for (const i of headingIndexes) {
      const parentCell = parentCellByIndex.get(i);
      if (parentCell && !parentCell.isNullObject) continue; // skip title-page table cells
      const para = paragraphs.items[i];

      headingCount += 1;
      const listItem = listItemByIndex.get(i);
      const { number, fromText, fromList } = _resolveHeadingNumberForHydrate(
        para.text as string,
        listItem && !listItem.isNullObject ? listItem.listString : null,
        skeletonTitleMap,
      );
      if (!number) continue; // un-numbered heading (e.g. provenance appendix)

      if (fromText) textNumberedCount += 1;
      if (fromList) renderedListCount += 1;
      numberedHeadings.push({ index: i, number });

      // importStyles was omitted in STEP2, so the server's custom AsthraHeadingN
      // style isn't in the live doc and this heading would render as plain body
      // text. Re-apply the built-in HeadingN (which AsthraHeadingN is basedOn,
      // and which STEP0 normalized to the server spec) so it looks right. The
      // literal ICH number in the text is unaffected. This also restores
      // styleBuiltIn so downstream heading detection (_getHeadingLevel, TOC,
      // cursor nav) keeps working. Errors surface at the sync below (guarded).
      para.styleBuiltIn = _builtInHeadingStyleForNumber(number) as Word.BuiltInStyleName;

      const bookmarkName = _sectionBookmarkName(number);
      para.getRange("Whole").insertBookmark(bookmarkName);
      plantedSections.push(number);
    }

    // Best-effort: the document is already inserted above, so a bookmark that
    // fails to plant (invalid range, duplicate name) must not abort the load —
    // these are ephemeral anchors only. insertBookmark errors surface here at
    // sync time (not at the queued call), so the guard must wrap the sync.
    try {
      await context.sync();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(`[wordWriter] hydrateLiveDocFromBase64: bookmark planting failed (non-fatal): ${message}`);
    }

    console.log(
      `[wordWriter] hydrateLiveDocFromBase64: planted ${plantedSections.length}/${headingCount} section bookmark(s)`,
    );

    // Step 3.5: ALWAYS re-establish native list numbering. The server emits
    // headings as title-only text + direct list numPr
    // (csr_sim/heading_numbering.py), but whether Word-for-the-web wires the
    // file's numbering DEFINITIONS after the no-importStyles
    // insertFileFromBase64 varies per insert — worse, a label that REPORTS
    // as rendered at this census can still drop afterwards, so "survived" is
    // a false signal and gating the rebuild on it reproduced the "no numbers
    // on first Load" report. Rebuilding a healthy doc is safe now that
    // _applyIchHeadingList detaches every live list item before startNewList
    // (the InvalidArgument that wrecked the first heading — 2026-07-18 field
    // log — fired only when startNewList ran over live list items), so the
    // detach-then-rebuild onto one fresh Word List runs unconditionally. A
    // literal-numbered doc still vetoes (rebuilding would double its
    // numbers). The convergence pass then guarantees a visible number on
    // every resolved heading (literal text as last resort).
    // heading_numbering: none → the server rendered title-only headings on
    // purpose. Skip the rebuild + convergence pass entirely (they would draw a
    // number back in). Bookmarks were already planted above from the
    // title-map-resolved number, so spine identity / navigation is intact.
    const suppressNumbers = opts?.headingNumbering === "none";
    const decision = suppressNumbers
      ? "skip"
      : _numberingRebuildDecision({
          resolved: numberedHeadings.length,
          textNumbered: textNumberedCount,
          renderedList: renderedListCount,
        });
    console.log(
      `[wordWriter] hydrate numbering: resolved=${numberedHeadings.length}/${headingCount} ` +
        `textNumbered=${textNumberedCount} renderedList=${renderedListCount} decision=${decision} ` +
        `suppress=${suppressNumbers} elapsedMs=${Date.now() - tHydrateStart}`,
    );
    if (decision === "rebuild") {
      // Fresh paragraph proxies for the rebuild: STEP3 mutated every heading
      // (styleBuiltIn rewrite + bookmark insert) after the census load, and
      // re-minting ListItems from the pre-mutation proxies threw
      // GeneralException at ListItem._onAccess via _getObjectByReferenceId —
      // a stale-reference batch poison that killed the whole rebuild AND the
      // convergence pass (2026-07-19 field log). Indexes are stable: restyle
      // and bookmarks add no paragraphs.
      let rebuildItems = paragraphs.items;
      try {
        const fresh = context.document.body.paragraphs;
        fresh.load("items");
        await context.sync();
        if (fresh.items.length === paragraphs.items.length) {
          rebuildItems = fresh.items;
        } else {
          console.warn(
            `[wordWriter] hydrate numbering: paragraph count changed ${paragraphs.items.length}→${fresh.items.length} — using original proxies`,
          );
        }
      } catch (err) {
        console.warn(
          `[wordWriter] hydrate numbering: fresh paragraph refetch failed — using original proxies: ${_wordErrDetail(err)}`,
        );
      }
      console.log(
        `[wordWriter] hydrate numbering: rebuild proxies ${rebuildItems === paragraphs.items ? "original" : "fresh"} elapsedMs=${Date.now() - tHydrateStart}`,
      );
      const nativeEntries = numberedHeadings.map(({ index, number }) => ({
        para: rebuildItems[index],
        number,
      }));
      try {
        const attached = await _applyIchHeadingList(context, nativeEntries, _skeletonTopStart(opts?.skeleton));
        console.log(
          `[wordWriter] hydrate: native list attach ${attached}/${nativeEntries.length} elapsedMs=${Date.now() - tHydrateStart}`,
        );
      } catch (err) {
        console.warn(
          `[wordWriter] hydrateLiveDocFromBase64: native-numbering rebuild failed (non-fatal): ${_wordErrDetail(err)}`,
        );
      }
      try {
        await _ensureVisibleHeadingNumbers(context, nativeEntries);
      } catch (err) {
        console.warn(
          `[wordWriter] hydrateLiveDocFromBase64: numbering convergence pass failed (non-fatal): ${_wordErrDetail(err)}`,
        );
      }
    }
    console.log(`[wordWriter] hydrateLiveDocFromBase64: done totalMs=${Date.now() - tHydrateStart}`);

    // sectionsWritten reflects headings present so the restore marker gate
    // (sectionsWritten > 0) passes whenever the doc actually has structure.
    return {
      sectionsWritten: headingCount,
      bookmarksPlanted: plantedSections.length,
      plantedSections,
    };
    });
  } finally {
    endLiveDocLoad();
  }
}

/**
 * Casefolded ``title → section_number`` map from doc-type skeleton tuples
 * ``[number, title, level]``. Pure — exported via ``_testing``.
 */
function _skeletonTitleMap(skeleton?: [string, string, number][]): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of skeleton ?? []) {
    const num = entry?.[0];
    const title = entry?.[1];
    if (num != null && title) map.set(String(title).trim().toLowerCase(), String(num));
  }
  return map;
}

/**
 * Three-tier section-number recovery for a hydrated heading paragraph:
 * literal text prefix (legacy literal-numbered docs) → rendered list label
 * (native numbering that survived the insert) → skeleton title match (native
 * numbering that Word web DROPPED on the insert). ``fromText`` tells the
 * caller the doc is literal-numbered, which vetoes the STEP3.5 list rebuild
 * (rebuilding would double the numbers); ``fromList`` means the label
 * actually RENDERS — the only valid "numbering survived" signal (a paragraph
 * whose numPr survived with a dangling definition still resolves as a
 * ListItem, but shows nothing). Pure — exported via ``_testing``.
 */
function _resolveHeadingNumberForHydrate(
  text: string,
  listString: string | null | undefined,
  skeletonTitleMap: Map<string, string>,
): { number: string | null; fromText: boolean; fromList: boolean } {
  const textNumber = _extractHeadingNumber(text);
  if (textNumber) return { number: textNumber, fromText: true, fromList: false };
  const listNumber = _numberFromListString(listString);
  if (listNumber) {
    const skeletonNumber = skeletonTitleMap.get(text.trim().toLowerCase());
    if (skeletonNumber && skeletonNumber !== listNumber) {
      // Rendered label DISAGREES with the skeleton — e.g. a heading left
      // attached to a half-configured junk list by a previously-failed
      // rebuild. Trust the skeleton and report fromList=false so the
      // caller's survival census counts this heading as rebuild-needed.
      return { number: skeletonNumber, fromText: false, fromList: false };
    }
    return { number: listNumber, fromText: false, fromList: true };
  }
  return {
    number: skeletonTitleMap.get(text.trim().toLowerCase()) ?? null,
    fromText: false,
    fromList: false,
  };
}

/**
 * STEP3.5 gate — pure, exported via ``_testing``:
 * - nothing resolved → nothing to number,
 * - any literal text number → legacy literal doc, a rebuild would DOUBLE
 *   the numbers,
 * - otherwise → ALWAYS rebuild from a detached clean slate. Word-web's
 *   rendered-label census is a false signal: a label that reports as
 *   rendered right after the insert can still drop afterwards, so a
 *   "numbering survived" skip left docs with dangling numPr and no visible
 *   numbers. Re-attaching a healthy doc is idempotent because
 *   ``_applyIchHeadingList`` detaches every live list item before
 *   ``startNewList`` (the InvalidArgument of the 2026-07-18 field log fired
 *   only when startNewList ran over live list items).
 *
 * ``renderedList`` no longer gates anything but stays in the census log as
 * a diagnostic (it counts only labels that RENDER and MATCH — a dangling
 * numPr still resolves as a ListItem with an empty label and must not
 * count).
 */
function _numberingRebuildDecision(counts: { resolved: number; textNumbered: number; renderedList: number }):
  | "rebuild"
  | "skip-literal-doc"
  | "skip-none-resolved" {
  if (counts.resolved === 0) return "skip-none-resolved";
  if (counts.textNumbered > 0) return "skip-literal-doc";
  return "rebuild";
}

/** Word List API (WordApi 1.3) availability — the native-numbering
 *  builders no-op without it and the literal fallback takes over. */
function _supportsListApi(): boolean {
  return typeof Office !== "undefined" && Boolean(Office.context?.requirements?.isSetSupported?.("WordApi", "1.3"));
}

/** RichApi errors carry the failing statement in ``debugInfo`` — include it
 *  when logging so a field failure names the exact call instead of the
 *  generic "argument is not valid" message. */
function _wordErrDetail(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  const debugInfo = (err as { debugInfo?: unknown } | null | undefined)?.debugInfo;
  try {
    return debugInfo ? `${message} debugInfo=${JSON.stringify(debugInfo)}` : message;
  } catch {
    return message;
  }
}

/**
 * TS mirror of ``csr_sim/heading_numbering.validate_sequence`` /
 * ``plan_native_numbering`` — the shared gate for native numbering.
 * Simulates Word's positional multilevel counter over ``[number, level]``
 * pairs and returns the required level-1 ``w:start`` when the counter
 * reproduces every declared number exactly, else null (→ literal
 * fallback: IB's letter-based "A.x", CER's zero-based sub-levels, gapped
 * skeletons). Keeping the two implementations in exact agreement is what
 * lets scaffolded docs and server-rendered docs carry identical numbering.
 * Pure — exported via ``_testing`` (parity-tested against the Python
 * fixtures).
 */
function _planNativeNumbering(entries?: Array<[string, number]>): number | null {
  if (!entries || entries.length === 0) return null;
  const firstSn = String(entries[0][0]).trim();
  if (Math.trunc(entries[0][1]) !== 1 || !/^\d+$/.test(firstSn)) return null;
  const topStart = Number.parseInt(firstSn, 10);
  const MAX_LEVELS = 9;
  const counters: number[] = new Array(MAX_LEVELS).fill(0);
  counters[0] = topStart - 1;
  for (const [sn, level] of entries) {
    const ilvl = Math.trunc(Number(level)) - 1;
    if (!Number.isInteger(ilvl) || ilvl < 0 || ilvl >= MAX_LEVELS) return null;
    counters[ilvl] += 1;
    for (let i = ilvl + 1; i < MAX_LEVELS; i++) counters[i] = 0;
    if (counters.slice(0, ilvl + 1).join(".") !== String(sn).trim()) return null;
  }
  return topStart;
}

/**
 * Level-1 counter start from the doc-type skeleton's first entry (CSR: 2 —
 * the title page is the unnumbered cover and is omitted from the skeleton).
 * Null when the skeleton is absent or non-numeric. Pure — exported via
 * ``_testing``.
 */
function _skeletonTopStart(skeleton?: [string, string, number][]): number | null {
  const first = skeleton?.[0]?.[0];
  if (first == null) return null;
  const parsed = Number.parseInt(String(first).split(".")[0], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Establish the ICH multilevel list on heading paragraphs via the Word List
 * API — the SHARED native-numbering builder behind both the hydrate STEP3.5
 * re-attach (Word web drops/ignores the inserted file's numbering
 * definitions nondeterministically) and the scaffold path (live SSE
 * drafting / per-section Load fallback), so every Load regime renders the
 * same official Word numbering.
 *
 * Mirrors ``csr_sim/heading_numbering.abstract_num_xml``: decimal
 * "%1", "%1.%2", … labels with the level-1 counter seeded from the first
 * heading's top-level number. Word's positional counter reproduces the ICH
 * numbers because callers only enable native numbering for sequences the
 * shared validator (``_planNativeNumbering`` / server
 * ``plan_native_numbering``) accepted.
 *
 * In hydrate, runs AFTER the STEP3 ``styleBuiltIn`` re-assignment so a
 * style-triggered strip can't undo it. Known cosmetic difference vs the
 * download: the List API renders a tab suffix after the label (OOXML's
 * ``w:suff=space`` isn't settable here).
 *
 * When the host lacks the List API (WordApi 1.3) this attaches nothing —
 * the caller's convergence pass (_ensureVisibleHeadingNumbers) prepends
 * literal numbers instead. ``attachToList`` runs in chunks with per-chunk
 * guards so one bad paragraph or an oversized batch can't zero out the
 * whole rebuild; the convergence pass tops up whatever a failed chunk left
 * bare. Returns the number of paragraphs attached.
 */
async function _applyIchHeadingList(
  context: Word.RequestContext,
  entries: Array<{ para: Word.Paragraph; number: string }>,
  topStartHint: number | null,
): Promise<number> {
  if (entries.length === 0) return 0;
  if (!_supportsListApi()) {
    console.warn("[wordWriter] WordApi 1.3 lists unsupported — leaving numbering to the literal fallback");
    return 0;
  }

  const ilvlOf = (number: string) => Math.min(number.split(".").length - 1, 8);

  // Clean slate FIRST: detach every entry that is currently a list item — a
  // rebuild-needed heading may carry either a dangling numPr (Word web
  // dropped the definitions) or membership in a junk list (a previously
  // failed rebuild). ``startNewList`` on a paragraph that is already a live
  // list item threw InvalidArgument on Word web (2026-07-18 field log), so
  // the rebuild only ever starts from non-list paragraphs.
  //
  // The membership census is chunked and guarded: a stale/dangling ListItem
  // throws GeneralException on ACCESS (ListItem._onAccess, 2026-07-19 field
  // log), and one bad item voids every load sharing its sync. A chunk that
  // fails retries per item; an item that still throws is "unknown" and gets
  // a solo guarded detach below (detachFromList throws on a non-list
  // paragraph, so unknowns can't ride in a shared batch).
  const CHUNK = 40;
  const membership: Array<"list" | "none" | "unknown"> = new Array(entries.length).fill("unknown");
  for (let start = 0; start < entries.length; start += CHUNK) {
    const end = Math.min(start + CHUNK, entries.length);
    try {
      const items: Word.ListItem[] = [];
      for (let k = start; k < end; k++) {
        const li = entries[k].para.listItemOrNullObject;
        li.load("isNullObject");
        items.push(li);
      }
      await context.sync();
      for (let k = start; k < end; k++) {
        membership[k] = items[k - start].isNullObject ? "none" : "list";
      }
    } catch (err) {
      console.warn(
        `[wordWriter] native numbering: census chunk @${start} failed — retrying per item: ${_wordErrDetail(err)}`,
      );
      for (let k = start; k < end; k++) {
        try {
          const li = entries[k].para.listItemOrNullObject;
          li.load("isNullObject");
          await context.sync();
          membership[k] = li.isNullObject ? "none" : "list";
        } catch {
          membership[k] = "unknown";
        }
      }
    }
  }

  let detachedCount = 0;
  let skippedCount = 0;
  const unknownCount = membership.filter((m) => m === "unknown").length;
  const listIndexes: number[] = [];
  for (let k = 0; k < entries.length; k++) {
    if (membership[k] === "list") listIndexes.push(k);
  }
  for (let start = 0; start < listIndexes.length; start += CHUNK) {
    const chunk = listIndexes.slice(start, start + CHUNK);
    try {
      for (const k of chunk) entries[k].para.detachFromList();
      await context.sync();
      detachedCount += chunk.length;
    } catch (err) {
      console.warn(
        `[wordWriter] native numbering: detach chunk @${start} failed — retrying per item: ${_wordErrDetail(err)}`,
      );
      for (const k of chunk) {
        try {
          entries[k].para.detachFromList();
          await context.sync();
          detachedCount += 1;
        } catch {
          skippedCount += 1;
        }
      }
    }
  }
  for (let k = 0; k < entries.length; k++) {
    if (membership[k] !== "unknown") continue;
    try {
      entries[k].para.detachFromList();
      await context.sync();
      detachedCount += 1;
    } catch {
      skippedCount += 1;
    }
  }
  console.log(
    `[wordWriter] native numbering: detached ${detachedCount}, unknown ${unknownCount}, skipped ${skippedCount} of ${entries.length} pre-rebuild`,
  );

  const first = entries[0];
  const list = first.para.startNewList();
  list.load("id");
  await context.sync();

  // Level labels "%1", "%1.%2", … — the format array mixes level indices and
  // literal separators (e.g. [0, ".", 1] renders "2.3"). Only configure the
  // levels this document actually uses — smaller surface for host-side
  // argument validation.
  const maxIlvl = entries.reduce((m, e) => Math.max(m, ilvlOf(e.number)), 0);
  for (let lvl = 0; lvl <= maxIlvl; lvl++) {
    const format: Array<string | number> = [];
    for (let j = 0; j <= lvl; j++) {
      if (j > 0) format.push(".");
      format.push(j);
    }
    list.setLevelNumbering(lvl, Word.ListNumbering.arabic, format);
  }
  await context.sync();

  // Counter seed: the skeleton's first top-level number (CSR: 2) — fall back
  // to the first entry's top component when no hint was passed.
  const topStart = topStartHint ?? Number.parseInt(first.number.split(".")[0], 10);
  if (Number.isFinite(topStart) && topStart !== 1) {
    list.setLevelStartingNumber(0, topStart);
  }
  // startNewList joined the first paragraph at level 0; move it to its own
  // depth (top-level headings are depth 1 → level 0, so usually a no-op).
  const firstIlvl = ilvlOf(first.number);
  if (firstIlvl > 0) {
    first.para.listItemOrNullObject.level = firstIlvl;
  }
  await context.sync();

  // Attach the rest in chunks — one giant sync over 100+ paragraphs is where
  // Word web request-size limits would bite, and a single bad paragraph must
  // not void the whole rebuild.
  let attached = 1; // the startNewList paragraph
  for (let start = 1; start < entries.length; start += CHUNK) {
    const chunk = entries.slice(start, start + CHUNK);
    try {
      for (const { para, number } of chunk) {
        para.attachToList(list.id, ilvlOf(number));
      }
      await context.sync();
      attached += chunk.length;
    } catch (err) {
      console.warn(
        `[wordWriter] native numbering: attachToList chunk @${start} failed (${chunk.length} heading(s)) — continuing: ${_wordErrDetail(err)}`,
      );
    }
  }
  console.log(
    `[wordWriter] native numbering: attached ${attached}/${entries.length} heading(s) (listId=${list.id})`,
  );
  return attached;
}

/**
 * Convergence pass: after a native-list attach attempt, re-read every
 * resolved heading's list label and prepend the literal ``"{number}  "`` to
 * any heading that STILL renders no number and doesn't already carry one in
 * its text. Guarantees visible numbers on every Load; the server compile
 * strips matched literal prefixes on the next round-trip, healing the doc
 * back to native numbering.
 *
 * Before prepending, a heading that is still a (dangling) list item is
 * detached from its dead list — left in place, that numPr can resurface as
 * a rendered native label when the document is reopened, doubling the
 * literal number we're about to add.
 *
 * Every read here is chunked and guarded: a stale/dangling ListItem throws
 * GeneralException on ACCESS (ListItem._onAccess, 2026-07-19 field log) and
 * voids its whole sync batch — an unreadable heading is treated as BARE so
 * this pass can never again die wholesale and leave titles unnumbered.
 */
async function _ensureVisibleHeadingNumbers(
  context: Word.RequestContext,
  entries: Array<{ para: Word.Paragraph; number: string }>,
): Promise<void> {
  if (entries.length === 0) return;
  const CHUNK = 40;
  // Per entry: rendered label (null = bare), whether a ListItem is present
  // ("unknown" when even reading it throws), and the paragraph text (null
  // when unreadable → prepend without the already-numbered check).
  const labels: Array<string | null> = new Array(entries.length).fill(null);
  const listish: Array<"list" | "none" | "unknown"> = new Array(entries.length).fill("unknown");
  const texts: Array<string | null> = new Array(entries.length).fill(null);
  let unreadableCount = 0;
  for (let start = 0; start < entries.length; start += CHUNK) {
    const end = Math.min(start + CHUNK, entries.length);
    try {
      const items: Word.ListItem[] = [];
      for (let k = start; k < end; k++) {
        const li = entries[k].para.listItemOrNullObject;
        li.load("isNullObject,listString");
        items.push(li);
        entries[k].para.load("text");
      }
      await context.sync();
      for (let k = start; k < end; k++) {
        const li = items[k - start];
        listish[k] = li.isNullObject ? "none" : "list";
        labels[k] = li.isNullObject ? null : _numberFromListString(li.listString);
        texts[k] = entries[k].para.text as string;
      }
    } catch (err) {
      console.warn(
        `[wordWriter] numbering convergence: read chunk @${start} failed — retrying per item: ${_wordErrDetail(err)}`,
      );
      for (let k = start; k < end; k++) {
        try {
          const li = entries[k].para.listItemOrNullObject;
          li.load("isNullObject,listString");
          entries[k].para.load("text");
          await context.sync();
          listish[k] = li.isNullObject ? "none" : "list";
          labels[k] = li.isNullObject ? null : _numberFromListString(li.listString);
          texts[k] = entries[k].para.text as string;
        } catch {
          unreadableCount += 1; // stays "unknown"/bare
        }
      }
    }
  }

  const bare: number[] = [];
  for (let k = 0; k < entries.length; k++) {
    if (!labels[k]) bare.push(k);
  }
  if (bare.length === 0) {
    console.log("[wordWriter] numbering convergence: every heading renders a label — nothing to do");
    return;
  }

  // Strip dangling numPr first (guarded chunks with per-item retry, so a
  // detach failure can't void the literal prepends below — a heading that
  // keeps its dead numPr may double on reopen, but a visible number NOW
  // wins over that hypothetical).
  let detachedCount = 0;
  let detachFailed = 0;
  const detachIdx = bare.filter((k) => listish[k] !== "none");
  for (let start = 0; start < detachIdx.length; start += CHUNK) {
    const chunk = detachIdx.slice(start, start + CHUNK);
    try {
      for (const k of chunk) entries[k].para.detachFromList();
      await context.sync();
      detachedCount += chunk.length;
    } catch (err) {
      console.warn(
        `[wordWriter] numbering convergence: detach chunk @${start} failed — retrying per item: ${_wordErrDetail(err)}`,
      );
      for (const k of chunk) {
        try {
          entries[k].para.detachFromList();
          await context.sync();
          detachedCount += 1;
        } catch {
          detachFailed += 1;
        }
      }
    }
  }

  let prepended = 0;
  for (const k of bare) {
    const { para, number } = entries[k];
    const text = texts[k];
    if (text != null && _extractHeadingNumber(text)) continue;
    para.insertText(`${number}  `, Word.InsertLocation.start);
    prepended += 1;
  }
  if (prepended > 0) {
    try {
      await context.sync();
    } catch (err) {
      console.warn(`[wordWriter] numbering convergence: prepend sync failed (non-fatal): ${_wordErrDetail(err)}`);
    }
  }
  console.log(
    `[wordWriter] numbering convergence: detached ${detachedCount} dangling list ref(s) (${detachFailed} failed), ` +
      `prepended literal numbers to ${prepended} heading(s), unreadable=${unreadableCount}`,
  );
}

// ---------------------------------------------------------------------------
// 2. Insert content under a specific heading
// ---------------------------------------------------------------------------

/**
 * Find a section heading by number and insert content after it.
 * If content already exists between this heading and the next,
 * it is replaced.
 *
 * @returns true if content was inserted, false if heading not found.
 */
export async function insertSectionContent(params: InsertContentParams): Promise<boolean> {
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — skipping insert");
    return false;
  }

  const { sectionNumber, content, sectionTitle } = params;

  return runWord("insertSectionContent", async (context) => {
    // Ensure Track Changes is OFF for pipeline content insertion.
    // Chat revisions enable it explicitly via the DocumentBridge;
    // initial generation should never produce tracked revisions.
    try {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();
      if ((doc.changeTrackingMode as string) === "TrackAll") {
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
        await context.sync();
      }
    } catch {
      // Track Changes API may not be available — non-critical
    }

    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text,style,styleBuiltIn");
    await context.sync();

    // body.paragraphs includes paragraphs inside table cells. Batch-load
    // parentTableCellOrNullObject for all paragraphs so we can exclude
    // table-cell paragraphs from every heading search and from the
    // insertion anchor. Also batch-load each paragraph's list label — under
    // native list numbering the section number is rendered but absent from
    // paragraph.text. One extra sync; all loads queued before it fires.
    const parentTableCells = paragraphs.items.map((p: Word.Paragraph) => p.parentTableCellOrNullObject);
    for (const c of parentTableCells) {
      c.load("isNullObject");
    }
    const paraListItems = paragraphs.items.map((p: Word.Paragraph) => p.listItemOrNullObject);
    for (const li of paraListItems) {
      li.load("isNullObject,listString");
    }
    await context.sync();

    // Returns true when paragraph i lives inside a table cell.
    const inTable = (i: number) => !parentTableCells[i].isNullObject;

    // Returns true when paragraph i has a heading style (built-in or custom AsthraHeading).
    const isHeadingStyle = (i: number) => _isHeadingParagraph(paragraphs.items[i]);

    // Section number of paragraph i's rendered list label (null when unlisted).
    const listNumber = (i: number) =>
      paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString);

    // --- Strategy 1: Text-prefix / list-label match first — O(n) on
    //     already-loaded data, no round trips. Far faster on large documents
    //     than bookmark search, which needs an Office JS sync per paragraph. ---
    let headingIndex = -1;
    const headingPrefix = `${sectionNumber} `;
    for (let i = 0; i < paragraphs.items.length; i++) {
      if (inTable(i)) continue;
      const text = paragraphs.items[i].text.trim();
      if (
        isHeadingStyle(i) &&
        (text.startsWith(headingPrefix) || text.startsWith(`${sectionNumber}  `) || listNumber(i) === sectionNumber)
      ) {
        headingIndex = i;
        break;
      }
    }

    // --- Strategy 2: Bookmark fallback (if heading was renamed by the
    //     writer). Batched: queue ALL compareLocationWith calls first,
    //     ONE sync, then read results — instead of N syncs in a loop. ---
    if (headingIndex === -1) {
      const bookmarkName = _sectionBookmarkName(sectionNumber);
      const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
      bookmarkRange.load("text");
      await context.sync();

      if (!bookmarkRange.isNullObject) {
        const comparisons: OfficeExtension.ClientResult<Word.LocationRelation>[] = [];
        for (let i = 0; i < paragraphs.items.length; i++) {
          const paraRange = paragraphs.items[i].getRange("Whole");
          comparisons.push(bookmarkRange.compareLocationWith(paraRange));
        }
        await context.sync();
        for (let i = 0; i < comparisons.length; i++) {
          if (inTable(i)) continue;
          const v = comparisons[i].value;
          if (v === "InsideStart" || v === "Equal" || v === "Inside") {
            headingIndex = i;
            break;
          }
        }
      }
    }

    // --- Strategy 3: Title match (CUSTOM submissions ONLY). A custom heading
    //     renders its title with no leading number and no bookmark, so Strategies
    //     1 & 2 miss. Match the writer's heading by TITLE. Strictly additive and
    //     gated on a non-empty ``sectionTitle`` — template runs pass none, so this
    //     loop never runs and the number behaviour is unchanged. First heading-
    //     styled, non-table paragraph whose title matches wins. ---
    if (headingIndex === -1 && sectionTitle) {
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (inTable(i)) continue;
        if (isHeadingStyle(i) && _headingTitleMatches(paragraphs.items[i].text, sectionTitle)) {
          headingIndex = i;
          break;
        }
      }
    }

    if (headingIndex === -1) {
      // TEMP DIAGNOSTIC (Phase 0, 2026-06 headings-only restore bug): dump what
      // headings DID load so we can tell hypothesis A (headings exist with a
      // different number namespace) from hypothesis B (body empty / unsettled —
      // no headings loaded at all). Remove with the rest of the Phase-0 probes.
      const loadedHeadings: string[] = [];
      for (let i = 0; i < paragraphs.items.length && loadedHeadings.length < 5; i++) {
        if (inTable(i)) continue;
        if (isHeadingStyle(i)) loadedHeadings.push(paragraphs.items[i].text.trim());
      }
      console.warn(`[wordWriter] Heading for section ${sectionNumber} not found (tried bookmark + text)`, {
        sectionNumber,
        totalParagraphs: paragraphs.items.length,
        firstHeadingTexts: loadedHeadings,
        anyHeadingLoaded: loadedHeadings.length > 0,
      });
      return false;
    }

    // Safety invariant: the anchor paragraph must never be inside a table.
    // If either strategy resolved to a table-cell paragraph (e.g. a bookmark
    // anchored to the wrong location), abort rather than corrupt table content.
    if (inTable(headingIndex)) {
      console.error(`[wordWriter] §${sectionNumber} heading resolved to a table-cell paragraph — aborting insert`);
      return false;
    }

    // Find the next heading (ANY level) to determine the deletion +
    // insertion range. Stopping at the next heading regardless of
    // level preserves sub-structure: inserting content under "3.2.S"
    // (level 2) lands BEFORE the first child "3.2.S.1" (level 3)
    // rather than blowing away every nested heading until the next
    // sibling at the same level.
    //
    // # DECISION (2026-04-28): boundary is "next heading", not
    // #   "next heading at same-or-higher level".
    // # WHY: deeply-nested skeletons (CMC Module 3 has up to
    // #   level-4 headings: 3.2.S → 3.2.S.1 → 3.2.S.1.2 Structure)
    // #   broke under the old rule. Inserting DS-01's draft for
    // #   "3.2.S" deleted all of 3.2.S.1, 3.2.S.1.1, 3.2.S.1.2,
    // #   3.2.S.1.3, 3.2.S.2 ... because none of them is at level
    // #   <= 2. Subsequent modules (DS-02 → "3.2.S.1.2 Structure")
    // #   then failed silently with "heading not found".
    // # COMPAT: CSR / CER / PSUR runs unaffected — those drafts
    // #   are short prose blocks that fit between siblings; no
    // #   regression expected. Tests cover the boundary explicitly.
    const headingPara = paragraphs.items[headingIndex];
    let nextHeadingIndex = paragraphs.items.length;

    for (let i = headingIndex + 1; i < paragraphs.items.length; i++) {
      if (inTable(i)) continue;
      if (isHeadingStyle(i)) {
        nextHeadingIndex = i;
        break;
      }
    }

    // Guard against blanking the live doc during the post-Load settling window.
    // When no next heading was found we would delete from this heading to the
    // END of the document — correct only for a genuinely last section. But while
    // native numbering / built-in styles are still re-attaching after a Load,
    // later headings aren't recognized yet, so "no next heading" is a false
    // negative and this scan is degraded. If the whole document shows fewer than
    // two recognized headings yet a sizeable run of real content trails this one,
    // treat the doc as unsettled and abort rather than wipe it. (The caller
    // re-projects again after the load settles — see liveDocLoadState — and
    // markers reappear on the next reload, so aborting is non-fatal.) This is the
    // defense-in-depth backstop for the "blanks a few seconds after Load" bug.
    if (nextHeadingIndex === paragraphs.items.length) {
      let totalHeadings = 0;
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (!inTable(i) && isHeadingStyle(i)) totalHeadings++;
      }
      let trailingContent = 0;
      for (let i = headingIndex + 1; i < paragraphs.items.length; i++) {
        if (!inTable(i) && paragraphs.items[i].text.trim().length > 0) trailingContent++;
      }
      const SETTLE_ABORT_TRAILING = 8;
      if (totalHeadings < 2 && trailingContent > SETTLE_ABORT_TRAILING) {
        console.error(
          `[wordWriter] §${sectionNumber}: refusing delete-to-end — document appears unsettled ` +
            `(recognizedHeadings=${totalHeadings}, trailingContentParas=${trailingContent}). ` +
            "Likely a re-projection racing a document load; skipping to avoid blanking the live doc.",
        );
        return false;
      }
    }

    // Delete existing content between heading and next heading
    for (let i = nextHeadingIndex - 1; i > headingIndex; i--) {
      paragraphs.items[i].delete();
    }
    await context.sync();

    // Format markdown and insert with proper Word formatting:
    // - Heading deduplication (skip parent, keep sub-headings)
    // - ## / ### → bold paragraphs under the ``Normal`` style (NOT
    //   ``HeadingN`` — see ``renderHeading`` doc comment for why)
    // - **bold** → bold runs with run-level font
    // - [NOT FOUND], [REVIEWER:] → highlighted runs
    // - Tables → Word tables
    // - Times New Roman 11pt on every run
    //
    // ``context`` is threaded through so the renderer can ``await
    // context.sync()`` between each node — load-bearing, see the
    // ``renderNodes`` doc comment in wordRenderer.ts.
    await formatAndInsert(context, headingPara, content, sectionNumber, {
      demoMode: isDemoMode(),
    });

    // NOTE: we intentionally do NOT call select()/scroll here.
    //
    // An earlier version selected the heading's start range to pull
    // Word's viewport to the freshly inserted content. That behaved
    // badly during multi-section runs: each new section's insert
    // yanked the viewport back down, fighting writers who were
    // scrolling up to review earlier content. They reported it as
    // "the document pulls itself to the bottom whenever I scroll."
    //
    // Word's native cursor/viewport respects where the writer has
    // scrolled to — letting it alone is the honest default. Spine
    // UI already surfaces the active section for writers who want
    // to follow along.
    return true;
  });
}

/**
 * Replace a section's content in the live Word document.
 * Used by the chat interface when Claude writes a revision.
 *
 * Same as insertSectionContent but explicitly named for clarity.
 */
export async function replaceSectionContent(sectionNumber: string, newContent: string): Promise<boolean> {
  return insertSectionContent({ sectionNumber, content: newContent });
}

/**
 * Insert markdown as LEADING content at the very top of the body — above the
 * first heading, with NO synthetic heading.
 *
 * Used to restore a CUSTOM submission's title-page block (the writer's own
 * title line + confidentiality note + metadata table, captured verbatim before
 * the first heading) as leading chrome on Load. A custom title page is NOT a
 * numbered section, so it can't go through ``insertSectionContent`` (which
 * anchors on a heading by number). We reuse ``formatAndInsert`` with an empty
 * section number (disables heading-dedup) anchored on a fresh paragraph at body
 * Start; the empty anchor is removed afterwards so no stray blank line leads.
 */
export async function insertLeadingContent(markdown: string): Promise<boolean> {
  if (!isOfficeAvailable()) {
    console.warn("[wordWriter] Office JS not available — skipping leading insert");
    return false;
  }
  const content = (markdown ?? "").trim();
  if (!content) return false;

  return runWord("insertLeadingContent", async (context) => {
    // Track Changes off (mirror insertSectionContent) so restore isn't tracked.
    try {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();
      if ((doc.changeTrackingMode as string) === "TrackAll") {
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
        await context.sync();
      }
    } catch {
      // Track Changes API may be unavailable — non-critical.
    }

    const body = context.document.body;
    // Anchor an empty paragraph at the very start; formatAndInsert renders the
    // title block AFTER it, so the block lands above the first scaffolded
    // heading. The anchor is removed afterwards.
    const anchor = body.insertParagraph("", "Start");
    anchor.styleBuiltIn = "Normal";
    await context.sync();

    await formatAndInsert(context, anchor, content, "", { demoMode: isDemoMode() });
    await context.sync();

    try {
      anchor.delete();
      await context.sync();
    } catch {
      // Leaving one empty leading paragraph is cosmetic, not fatal.
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// 3. Detect which section the cursor is in
// ---------------------------------------------------------------------------

/**
 * Determine which section the user's cursor is currently in.
 * Walks backward from the cursor's paragraph using Paragraph.getPreviousOrNullObject()
 * until a numbered heading is found.
 *
 * # Why backward-walk and not load-all-paragraphs
 *
 * The previous implementation loaded every paragraph's text + style for the
 * whole body and queued a compareLocationWith for each. On a multi-thousand-
 * paragraph CSR that's hundreds of KB across the Office.js bridge — and this
 * runs on every selection-change. The cost was the dominant source of the
 * "Word freezes when the task pane is open" symptom.
 *
 * The walk loads ONE paragraph per step (style + text) and stops at the
 * nearest numbered heading. In practice that's a handful of paragraphs.
 *
 * @returns Section info, or null if cursor is not in a section.
 */
/**
 * Hot-path sibling of ``detectCursorSection`` for the chat rail's scope chip.
 *
 * Returns the dotted section number (e.g. ``"3.2.P.1"``) of the section the
 * cursor is in, or null if no preceding ``ASTHRA_SEC_*`` bookmark exists.
 *
 * # Why this is cheap enough to run on every debounced selection change
 *
 * ``scaffoldDocument`` bookmarks every skeleton heading at scaffold time, so
 * the document has a sparse, complete index of section starts. Word.js
 * exposes ``Range.getBookmarks(includeHidden, includeAdjacent)`` which returns
 * the names of all bookmarks inside a range in document order. We build a
 * range from the start of the body to the cursor, ask for its bookmarks, and
 * take the last ``ASTHRA_SEC_*`` name in the array — that's the nearest
 * preceding section heading by construction.
 *
 * Cost: ONE ``context.sync()`` regardless of how deep the cursor is inside a
 * section. The range walk happens natively on the Office C++ side rather than
 * via per-paragraph bridge round-trips, so it is orders of magnitude lighter
 * than the paragraph-walk in ``detectCursorSection``.
 *
 * Callers MUST still gate on "not actively drafting" — even a cheap call can
 * contend with the write queue during a long run and reintroduce the historical
 * Word freeze symptom. See VariantCShell's selection-change subscriber.
 */
export async function detectCursorSectionLight(): Promise<string | null> {
  if (!isOfficeAvailable()) return null;
  return runWord("detectCursorSectionLight", async (context) => {
    const body = context.document.body;
    const cursor = context.document.getSelection();
    const rangeBefore = body.getRange("Start").expandToOrNullObject(cursor);
    const bookmarks = rangeBefore.getBookmarks(true, true);
    await context.sync();
    const names: string[] = bookmarks.value ?? [];
    for (let i = names.length - 1; i >= 0; i--) {
      const decoded = decodeSectionBookmarkName(names[i]);
      if (decoded) return decoded;
    }
    return null;
  });
}

export async function detectCursorSection(): Promise<CursorSectionInfo | null> {
  if (!isOfficeAvailable()) {
    return null;
  }

  return runWord("detectCursorSection", async (context) => {
    const selection = context.document.getSelection();
    let para: Word.Paragraph = selection.paragraphs.getFirstOrNullObject();
    let listItem = para.listItemOrNullObject;
    para.load("text,style,styleBuiltIn,isNullObject");
    listItem.load("isNullObject,listString");
    await context.sync();
    if (para.isNullObject) return null;

    // Hard ceiling so a malformed doc can't loop unbounded.
    const MAX_WALK = 500;
    for (let step = 0; step < MAX_WALK; step++) {
      // _isHeadingParagraph checks both styleBuiltIn AND the custom style
      // display name — a paragraph styled AsthraHeading1/2/3 (the backend's
      // numbered-heading style, see doc_compiler.py) reports a generic
      // styleBuiltIn like "Other", not "Heading1", so a styleBuiltIn-only
      // check silently walks past every CSR/CER heading and returns null.
      if (_isHeadingParagraph(para)) {
        // Text parse covers legacy literal-numbered headings; the list label
        // covers native list numbering, where the number is rendered but
        // absent from paragraph.text.
        const parsed = _parseSectionNumber(para.text);
        const listNumber = listItem.isNullObject ? null : _numberFromListString(listItem.listString);
        const number = parsed?.number ?? listNumber;
        if (number) {
          return {
            sectionNumber: number,
            headingText: (para.text as string).trim(),
            level: _getHeadingLevel((para.styleBuiltIn as string) || ""),
          };
        }
      }
      const prev: Word.Paragraph = para.getPreviousOrNullObject();
      const prevListItem = prev.listItemOrNullObject;
      prev.load("text,style,styleBuiltIn,isNullObject");
      prevListItem.load("isNullObject,listString");
      await context.sync();
      if (prev.isNullObject) return null;
      para = prev;
      listItem = prevListItem;
    }
    return null;
  });
}

/**
 * Two-tier cursor-section resolution: bookmark probe first, heading walk
 * second. The light probe misses on docs whose ``ASTHRA_SEC_*`` bookmarks
 * were never (re)planted — e.g. a server-built docx loaded via hydration —
 * and THROWS on hosts without ``Range.getBookmarks`` (WordApi 1.4, absent in
 * Compatibility Mode); both cases must degrade to the paragraph walk rather
 * than abort, so the scope chip and the citations popover resolve the same
 * section for the same cursor.
 *
 * ``runRead`` lets callers route the underlying Word.run probes through
 * their own serializer (the shell passes ``queueWordRead``); one-shot
 * callers (citations fetch on click) use the direct default.
 */
export async function detectCursorSectionNumberWithFallback(
  runRead: <T>(label: string, fn: () => Promise<T>) => Promise<T> = (_label, fn) => fn(),
): Promise<string | null> {
  let num: string | null = null;
  try {
    num = await runRead("detectCursorSection", detectCursorSectionLight);
  } catch (err) {
    console.warn("[wordWriter] detectCursorSectionLight failed — falling back to heading walk:", err);
  }
  if (num) return num;
  try {
    const detailed = await runRead("detectCursorSection:fallback", detectCursorSection);
    return detailed?.sectionNumber ?? null;
  } catch (err) {
    console.warn("[wordWriter] detectCursorSection fallback failed:", err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Heading level from the style. The built-in style wins when it IS a heading
 * (``Heading2`` → 2); otherwise the display name is consulted, so the
 * backend's custom ``AsthraHeading2`` (``styleBuiltIn === "Other"``) also
 * resolves to 2 instead of the "unknown" level 4 — which made a custom-styled
 * parent section's read span stop at its first child. Single-argument callers
 * are unchanged.
 */
function _getHeadingLevel(styleBuiltIn: string, styleName = ""): number {
  const src = (styleBuiltIn || "").toLowerCase().includes("heading") ? styleBuiltIn : styleName || "";
  if (src.includes("1")) return 1;
  if (src.includes("2")) return 2;
  if (src.includes("3")) return 3;
  return 4;
}

function _parseSectionNumber(headingText: string): { number: string; title: string } | null {
  // Match patterns like "9.1  Overall Study Design" or "12.1.1  Sensitivity"
  const match = headingText.trim().match(/^(\d+(?:\.\d+)*)\s{1,4}(.+)$/);
  if (!match) return null;
  return { number: match[1], title: match[2] };
}

/**
 * True when a heading paragraph's text denotes the same section as ``targetTitle``.
 *
 * A CUSTOM-submission provision ONLY: a custom heading renders its title with no
 * leading number and no ``ASTHRA_SEC_*`` bookmark, so it can't be resolved by the
 * number strategies. This compares the heading's TITLE (its text minus any leading
 * ``N.N`` number via {@link _parseSectionNumber}, else the whole trimmed text)
 * against the target, normalized (trim, collapse internal whitespace, lowercase).
 *
 * Never matches on a blank/absent target — the guard that keeps this an additive
 * fallback: callers pass a title only for custom runs, so a template run (which
 * passes none) never reaches a title comparison.
 */
function _headingTitleMatches(headingText: string, targetTitle: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  const target = norm(targetTitle);
  if (!target) return false;
  const headingTitle = _parseSectionNumber(headingText)?.title ?? headingText;
  return norm(headingTitle) === target;
}

// ---------------------------------------------------------------------------
// 4. Navigate Word cursor to a section heading
// ---------------------------------------------------------------------------

/**
 * Move the Word cursor to a specific section heading.
 * Used by the Section Navigator when the user clicks a section.
 *
 * @returns true if the heading was found and cursor moved.
 */
export async function navigateToSection(sectionNumber: string): Promise<boolean> {
  if (!isOfficeAvailable()) {
    return false;
  }

  return runWord("navigateToSection", async (context) => {
    // Fast path: the ASTHRA_SEC_* bookmark planted at scaffold/hydrate time.
    // Guarded — getBookmarkRangeOrNullObject needs WordApi 1.4 and bookmarks
    // are ephemeral (a reopened doc may have none), so fall through to the
    // paragraph scan on any miss or throw.
    try {
      const bookmarkRange = context.document.getBookmarkRangeOrNullObject(_sectionBookmarkName(sectionNumber));
      bookmarkRange.load("isNullObject");
      await context.sync();
      if (!bookmarkRange.isNullObject) {
        bookmarkRange.getRange("Start").select();
        await context.sync();
        console.log(`[wordWriter] Navigated to §${sectionNumber} (bookmark)`);
        return true;
      }
    } catch {
      // fall through to the paragraph scan
    }

    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text,styleBuiltIn,style");
    await context.sync();

    // Native list numbering renders the number outside paragraph.text, so
    // load each paragraph's list label alongside the text prefixes.
    const paraListItems = paragraphs.items.map((p: Word.Paragraph) => p.listItemOrNullObject);
    for (const li of paraListItems) {
      li.load("isNullObject,listString");
    }
    await context.sync();

    const prefix = `${sectionNumber} `;
    const prefixDouble = `${sectionNumber}  `;

    for (let i = 0; i < paragraphs.items.length; i++) {
      const para = paragraphs.items[i];
      if (!_isHeadingParagraph(para)) continue;
      const text = para.text.trim();
      const listNumber = paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString);

      if (text.startsWith(prefix) || text.startsWith(prefixDouble) || listNumber === sectionNumber) {
        // Select the heading and scroll to it
        const range = para.getRange("Start");
        range.select();
        await context.sync();
        console.log(`[wordWriter] Navigated to §${sectionNumber}`);
        return true;
      }
    }

    console.warn(`[wordWriter] Heading §${sectionNumber} not found`);
    return false;
  });
}

/**
 * Select + scroll to a literal text marker in the live document — used by the
 * review tab to jump to a ``[REGULATOR FLAG: …]`` marker when its mini-report
 * card is clicked. Mirrors ``navigateToSection``'s select-and-scroll pattern.
 *
 * Returns false when Office isn't available or the marker isn't found.
 * ``Word.Range.search`` caps the search string at 255 chars; we truncate the
 * needle (marker bodies are capped at ~200 so this is just a safety net) and,
 * when truncated, drop the trailing ``]`` so the prefix still matches.
 *
 * Matching is tiered so a click still lands when the marker text has drifted
 * slightly from the ledger's stored ``marker_text`` (case folded by a repaint,
 * a reworded/removed closing bracket, trailing punctuation): exact →
 * case-insensitive → case-insensitive prefix up to the closing bracket. Only
 * after all tiers miss does the caller fall back to the section heading.
 */
export async function selectTextMarker(markerText: string): Promise<boolean> {
  if (!isOfficeAvailable() || !markerText) {
    return false;
  }

  let needle = markerText;
  if (needle.length > 255) {
    needle = needle.slice(0, 255);
    const lastClose = needle.lastIndexOf("]");
    if (lastClose > 0) needle = needle.slice(0, lastClose); // search a stable prefix
  }

  // Progressive fallbacks: stop at the first that hits. The prefix tier drops
  // the trailing "]" so a marker whose closing bracket moved (e.g. the audit
  // appended a recommendation) still matches on its stable head.
  const close = needle.lastIndexOf("]");
  const prefix = close > 0 ? needle.slice(0, close) : needle;
  const attempts: Array<{ text: string; matchCase: boolean }> = [
    { text: needle, matchCase: true },
    { text: needle, matchCase: false },
  ];
  if (prefix !== needle) {
    attempts.push({ text: prefix, matchCase: false });
  }

  return runWord("selectTextMarker", async (context) => {
    for (const attempt of attempts) {
      const results = context.document.body.search(attempt.text, {
        matchCase: attempt.matchCase,
        matchWildcards: false,
      });
      results.load("items");
      await context.sync();
      if (results.items.length > 0) {
        results.items[0].select();
        await context.sync();
        return true;
      }
    }
    console.warn(`[wordWriter] Marker not found: ${needle.slice(0, 60)}…`);
    return false;
  });
}

/**
 * Select the WHOLE paragraph that contains ``markerText`` (not just the
 * marker itself), scroll it into view, and leave it as the live Word
 * selection. Used by "Resolve with asthra" so the writer lands in chat with
 * the marker's paragraph highlighted, ready to capture into the chat
 * selection-context pill. Mirrors selectTextMarker's search/truncation, then
 * expands the hit to its enclosing paragraph.
 *
 * @returns true when the marker was found and its paragraph selected.
 */
export async function selectMarkerParagraph(markerText: string): Promise<boolean> {
  if (!isOfficeAvailable() || !markerText) {
    return false;
  }

  let needle = markerText;
  if (needle.length > 255) {
    needle = needle.slice(0, 255);
    const lastClose = needle.lastIndexOf("]");
    if (lastClose > 0) needle = needle.slice(0, lastClose); // search a stable prefix
  }

  return runWord("selectMarkerParagraph", async (context) => {
    const results = context.document.body.search(needle, {
      matchCase: true,
      matchWildcards: false,
    });
    results.load("items");
    await context.sync();

    if (results.items.length === 0) {
      console.warn(`[wordWriter] Marker not found: ${needle.slice(0, 60)}…`);
      return false;
    }
    results.items[0].paragraphs.getFirst().getRange("Whole").select();
    await context.sync();
    return true;
  });
}

// A single ``[REGULATOR FLAG: …]`` marker, optionally preceded by one space.
// Local mirror of ``REGULATOR_MARKER_RE`` in variant-c/services/draftSanitizer.ts
// (the SSOT, itself mirroring ``_MARKER_RE`` in src/csr_sim/regulatory/auditor.py)
// — duplicated here rather than imported so this shared ``services`` module keeps
// its dependency direction (variant-c depends on services, not the reverse).
const _REGULATOR_MARKER_RE = / ?\[REGULATOR FLAG:[^\]]*\]/g;

/** One paragraph's shape for the table-cell flag-strip planner. */
interface _StripPlanPara {
  text: string;
  isHeading: boolean;
  isTable: boolean;
  /** Section number rendered by this heading's list label (null when unlisted). */
  listNumber: string | null;
}

/** A queued deletion: the exact marker runs to remove from one paragraph. */
interface _StripPlanEntry {
  paraIndex: number;
  markers: string[];
}

/** A strip target: a heading resolved by section ``number`` and/or — for CUSTOM
 *  submissions whose headings carry no number — by ``title``. */
export type _StripTarget = { number?: string; title?: string };

/**
 * Pure core of the regulator-flag strip: given each paragraph's shape and the
 * target section(s), decide which paragraphs are in range and which
 * ``[REGULATOR FLAG: …]`` marker runs to delete from each.
 *
 * Scoping mirrors ``insertSectionContent``: a section's range is
 * [heading, nextHeading-of-any-level). ``includeProse`` controls the sweep width:
 * false (default) sweeps only table-cell paragraphs — the historical
 * ``stripRegulatorFlagsInTableCells`` behaviour, kept byte-identical; true also
 * sweeps prose paragraphs (used by ``stripRegulatorFlagsInSection`` for the robust
 * audit-start clean slate). A marker over Word's 255-char search cap is skipped.
 *
 * ``targets`` accepts the historical ``Set<string>`` of numbers (normalized to
 * ``{number}`` — old callers/tests unchanged) or ``_StripTarget[]`` (number and/or
 * title). Title is a CUSTOM-only additive fallback: matched only when a title is
 * supplied AND the number missed, so template targets (number-only) resolve
 * identically to before. Side-effect-free so the range logic is unit-testable.
 */
function _planTableCellFlagStrip(
  paras: _StripPlanPara[],
  targets: Set<string> | ReadonlyArray<_StripTarget>,
  includeProse = false,
): _StripPlanEntry[] {
  const normalized: _StripTarget[] =
    targets instanceof Set ? [...targets].map((number) => ({ number })) : [...targets];
  const inTargetRange = new Array<boolean>(paras.length).fill(false);
  for (const { number, title } of normalized) {
    const hasNumber = !!number && number !== "TITLE";
    if (!hasNumber && !title) continue;
    let headingIndex = -1;
    const headingPrefix = `${number} `;
    for (let i = 0; i < paras.length; i++) {
      if (paras[i].isTable || !paras[i].isHeading) continue;
      const text = paras[i].text.trim();
      const numberMatch =
        hasNumber &&
        (text.startsWith(headingPrefix) || text.startsWith(`${number}  `) || paras[i].listNumber === number);
      // Title is the CUSTOM-only fallback — only when the number didn't match.
      const titleMatch = !numberMatch && !!title && _headingTitleMatches(paras[i].text, title);
      if (numberMatch || titleMatch) {
        headingIndex = i;
        break;
      }
    }
    if (headingIndex === -1) continue;
    let nextHeadingIndex = paras.length;
    for (let i = headingIndex + 1; i < paras.length; i++) {
      if (paras[i].isTable) continue;
      if (paras[i].isHeading) {
        nextHeadingIndex = i;
        break;
      }
    }
    for (let i = headingIndex + 1; i < nextHeadingIndex; i++) inTargetRange[i] = true;
  }

  const plan: _StripPlanEntry[] = [];
  for (let i = 0; i < paras.length; i++) {
    if (!inTargetRange[i]) continue;
    if (!includeProse && !paras[i].isTable) continue;
    if (paras[i].text.indexOf("[REGULATOR FLAG:") === -1) continue;
    // Word.Range.search caps the needle at 255 chars; a marker over that simply
    // wouldn't match, so drop it from the plan rather than queue a doomed search.
    const markers = (paras[i].text.match(_REGULATOR_MARKER_RE) ?? []).filter((m) => m.length <= 255);
    if (markers.length > 0) plan.push({ paraIndex: i, markers });
  }
  return plan;
}

/** Normalize the mixed public target form (bare number strings and/or
 *  ``{number,title}``) into ``_StripTarget[]``, dropping empties/TITLE-only. */
function _normalizeStripTargets(targets: ReadonlyArray<string | _StripTarget>): _StripTarget[] {
  const out: _StripTarget[] = [];
  for (const t of targets) {
    const target = typeof t === "string" ? { number: t } : t;
    const hasNumber = !!target.number && target.number !== "TITLE";
    if (hasNumber || target.title) out.push(target);
  }
  return out;
}

/**
 * Shared live-doc runner: delete every ``[REGULATOR FLAG: …]`` marker run in the
 * target section(s), in place — removing only the marker run and leaving the
 * surrounding text and any table structure untouched. Returns the count deleted.
 *
 * ``includeProse`` false sweeps only table cells (the historical behaviour);
 * true also sweeps prose paragraphs. Best-effort: a no-op (0) when Office is
 * unavailable, no target heading resolves, or no markers are present.
 */
async function _runRegulatorFlagStrip(
  label: string,
  targets: _StripTarget[],
  includeProse: boolean,
): Promise<number> {
  if (!isOfficeAvailable() || targets.length === 0) return 0;

  return runWord(label, async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("text,style,styleBuiltIn");
    await context.sync();

    // body.paragraphs includes paragraphs inside table cells. Load the
    // table-cell + list-label state the heading match and the in-cell test need.
    const parentTableCells = paragraphs.items.map((p) => p.parentTableCellOrNullObject);
    for (const c of parentTableCells) c.load("isNullObject");
    const paraListItems = paragraphs.items.map((p) => p.listItemOrNullObject);
    for (const li of paraListItems) li.load("isNullObject,listString");
    await context.sync();

    const shapes: _StripPlanPara[] = paragraphs.items.map((p, i) => ({
      text: p.text,
      isHeading: _isHeadingParagraph(p),
      isTable: !parentTableCells[i].isNullObject,
      listNumber: paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString),
    }));
    const plan = _planTableCellFlagStrip(shapes, targets, includeProse);
    if (plan.length === 0) return 0;

    // Queue an exact-text search for each planned marker run, then delete every
    // hit in one batch. Searching per paragraph keeps each delete scoped to the
    // right paragraph/cell even when identical marker text repeats elsewhere.
    const searches: Word.RangeCollection[] = [];
    for (const { paraIndex, markers } of plan) {
      for (const marker of markers) {
        const hits = paragraphs.items[paraIndex].search(marker, { matchCase: true, matchWildcards: false });
        hits.load("items");
        searches.push(hits);
      }
    }
    await context.sync();

    let deleted = 0;
    for (const hits of searches) {
      for (const hit of hits.items) {
        hit.delete();
        deleted += 1;
      }
    }
    if (deleted > 0) await context.sync();
    return deleted;
  });
}

/**
 * Remove every ``[REGULATOR FLAG: …]`` marker inside a TABLE CELL of the given
 * section(s) from the live document, in place. Returns the number deleted.
 *
 * Why table-only: the audit-start "clean slate" re-projects each section from the
 * stripped server draft via ``insertSectionContent``, which deletes the paragraphs
 * between headings and re-renders them — clearing inline PROSE markers. But that
 * delete boundary deliberately never removes table content (a paragraph-deleting
 * replace would corrupt the table — see ``_coveredSpanTouchesTable``), so a marker
 * in a table cell survives the re-render. This sweep closes that gap.
 *
 * Targets accept bare number strings (template runs) and/or ``{number,title}``
 * (custom submissions whose headings carry no number).
 */
export async function stripRegulatorFlagsInTableCells(
  sections: ReadonlyArray<string | _StripTarget>,
): Promise<number> {
  return _runRegulatorFlagStrip(
    "stripRegulatorFlagsInTableCells",
    _normalizeStripTargets(sections),
    false,
  );
}

/**
 * Remove EVERY ``[REGULATOR FLAG: …]`` marker in the given section(s) from the
 * live document, in place — prose paragraphs AND table cells — deleting only the
 * marker runs and leaving all other content untouched. Returns the number deleted.
 *
 * Used for the robust audit-start clean slate on CUSTOM submissions: unlike the
 * re-render strip it depends on neither the server draft nor ``customSectionNumber``,
 * and it resolves the writer's heading by number OR title. Best-effort (returns 0
 * on any miss); idempotent; never blanks or duplicates content.
 */
export async function stripRegulatorFlagsInSection(
  sections: ReadonlyArray<string | _StripTarget>,
): Promise<number> {
  return _runRegulatorFlagStrip("stripRegulatorFlagsInSection", _normalizeStripTargets(sections), true);
}

// ---------------------------------------------------------------------------
// 5. Read all sections from the live Word document
// ---------------------------------------------------------------------------

export interface LiveSection {
  number: string;
  heading: string;
  level: number;
  content: string;
}

/**
 * Read all sections from the live Word document via Office JS.
 *
 * Returns each section's heading, level, and full text content.
 * Used to create a snapshot of the live document before sending
 * a chat message — ensures Claude reads what the user sees,
 * not a stale ZIP on disk.
 *
 * @returns Array of sections, or empty array if not in Word.
 */
/**
 * Read the FULL subtree of a section — every paragraph from the heading
 * (exclusive) up to the next heading at the same or higher level. This
 * matches insertSectionContent's deletion boundary EXACTLY, so it can be
 * used to build a faithful BEFORE preview for the WriteGate approval card.
 *
 * Differs from readAllSections, which uses "next heading of any level"
 * (per-section view for the navigator). For write previews, use this.
 */
export async function readSectionSubtree(sectionNumber: string): Promise<string | null> {
  if (!isOfficeAvailable()) return null;

  return runWord("readSectionSubtree", async (context) => {
    const bounds = await _locateSectionBounds(context, sectionNumber);
    if (bounds === null) return null;
    const { paragraphs, headingIndex, nextHeadingIndex } = bounds;

    // Collect text between (heading, nextHeading) — preserving sub-headings
    // verbatim so the writer sees them in the BEFORE preview.
    const lines: string[] = [];
    for (let i = headingIndex + 1; i < nextHeadingIndex; i++) {
      const text = paragraphs.items[i].text;
      if (text && text.trim().length > 0) {
        lines.push(text);
      }
    }
    return lines.join("\n").trim();
  });
}

/** Section span in the live doc's paragraph list — heading + exclusive body
 *  boundary. ``nextHeadingIndex`` is the index of the next heading of the
 *  SAME OR HIGHER level (or ``paragraphs.items.length``), matching
 *  insertSectionContent's deletion boundary exactly. */
interface SectionBounds {
  paragraphs: Word.ParagraphCollection;
  headingIndex: number;
  nextHeadingIndex: number;
}

/** Locate a section's heading + subtree boundary. Factored out of
 *  ``readSectionSubtree`` so the plain-text reader and the OOXML capture
 *  (``readSectionSubtreeOoxml``) resolve IDENTICAL spans.
 *
 *  Loads the whole-document paragraph index ONCE (``_loadParagraphIndex`` —
 *  one bulk ``text,style,styleBuiltIn`` load + list labels for HEADING
 *  paragraphs only) unless the caller hands in a ``preloaded`` index, then
 *  resolves in memory (Strategy 1: heading text / list label) and only falls
 *  back to the bookmark strategy (Strategy 2) when that misses. Returns null
 *  when neither strategy finds the heading. */
async function _locateSectionBounds(
  context: Word.RequestContext,
  sectionNumber: string,
  preloaded?: DocParagraphIndex,
): Promise<SectionBounds | null> {
  const index = preloaded ?? (await _loadParagraphIndex(context));
  const span = _findBoundsInIndex(index, sectionNumber) ?? (await _findBoundsByBookmark(context, index, sectionNumber));
  if (span === null) return null;
  return { paragraphs: index.paragraphs, headingIndex: span.headingIndex, nextHeadingIndex: span.nextHeadingIndex };
}

/** Strategy 2 — bookmark fallback, used only when the in-memory heading match
 *  fails (e.g. the writer renamed the heading). Bookmarks are planted on the
 *  section's HEADING paragraph, so only heading paragraphs are compared: one
 *  ``compareLocationWith`` per heading (not per paragraph), ONE sync. Never
 *  reloads the paragraph collection — it resolves against ``index``. */
async function _findBoundsByBookmark(
  context: Word.RequestContext,
  index: DocParagraphIndex,
  sectionNumber: string,
): Promise<SectionSpan | null> {
  const bookmarkName = _sectionBookmarkName(sectionNumber);
  // An out-of-spec name (e.g. a section number with a hyphen) makes
  // ``getBookmarkRangeOrNullObject`` throw ``InvalidArgument`` instead of
  // returning a null object — skip the query entirely, since no bookmark can
  // carry an invalid name anyway. Any residual Office error is caught so a
  // single section degrades to "not found" rather than aborting the caller's
  // whole-document scan (which would discard already-captured section bodies).
  if (!_isValidBookmarkName(bookmarkName)) return null;
  try {
    const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
    bookmarkRange.load("isNullObject");
    await context.sync();
    if (bookmarkRange.isNullObject) return null;

    const comparisons: { i: number; res: OfficeExtension.ClientResult<Word.LocationRelation> }[] = [];
    for (let i = 0; i < index.count; i++) {
      if (!index.isHeading[i]) continue;
      comparisons.push({ i, res: bookmarkRange.compareLocationWith(index.paragraphs.items[i].getRange("Whole")) });
    }
    if (comparisons.length > 0) {
      await context.sync();
      for (const { i, res } of comparisons) {
        const v = res.value;
        if (v === "InsideStart" || v === "Equal" || v === "Inside") return _spanFromHeading(index, i);
      }
    }
    // The bookmark exists but sits on a paragraph that is not heading-styled
    // (mid-Load settle window, or the writer reset the style). Treated as not
    // found — the safe direction: the SSOT copy is left untouched.
    console.warn(
      `[wordWriter] _findBoundsByBookmark(${sectionNumber}): bookmark ${bookmarkName} present but on no heading paragraph — treating as not found`,
    );
    return null;
  } catch (err) {
    console.warn(
      `[wordWriter] _locateSectionBounds(${sectionNumber}): bookmark fallback failed — treating as not found:`,
      err,
    );
    return null;
  }
}

/** Result of an OOXML section capture. ``deferred`` = a figure in the section
 *  is still Word's ~850-byte loading-placeholder bitmap — capturing now would
 *  bake the placeholder into the snapshot (the syncEngine grey-box trap), so
 *  the caller skips OOXML for this Save and retries next time. */
export type SectionOoxmlCapture = { ooxml: string } | { deferred: true } | null;

/**
 * Capture a section's body as a flat-OPC OOXML package — the formatting-
 * faithful sibling of ``readSectionSubtree`` (identical span via
 * ``_locateSectionBounds``; heading EXCLUSIVE, so re-inserting the snapshot
 * can never duplicate the heading or disturb its ASTHRA_SEC_* bookmark).
 *
 * Returned package is Word's raw ``Range.getOoxml()`` output, unmodified —
 * fidelity is the contract; hash normalization happens separately
 * (sectionOoxml.ts) and only for the dirty check.
 */
export async function readSectionSubtreeOoxml(sectionNumber: string): Promise<SectionOoxmlCapture> {
  if (!isOfficeAvailable()) return null;

  return runWord("readSectionSubtreeOoxml", async (context) => {
    const bounds = await _locateSectionBounds(context, sectionNumber);
    if (bounds === null) return null;
    const { paragraphs, headingIndex, nextHeadingIndex } = bounds;
    if (nextHeadingIndex <= headingIndex + 1) return null; // empty body — nothing to snapshot

    // Placeholder-image guard (mirrors syncEngine's IMAGE_PLACEHOLDER defer):
    // batch-load every body paragraph's inline pictures, then their bytes.
    const picCollections = [] as Word.InlinePictureCollection[];
    for (let i = headingIndex + 1; i < nextHeadingIndex; i++) {
      const pics = paragraphs.items[i].inlinePictures;
      pics.load("items");
      picCollections.push(pics);
    }
    await context.sync();

    const picSrcResults: { src: OfficeExtension.ClientResult<string>; fmt: Word.InlinePicture }[] = [];
    for (const pics of picCollections) {
      for (const pic of pics.items) {
        pic.load("imageFormat");
        picSrcResults.push({ src: pic.getBase64ImageSrc(), fmt: pic });
      }
    }
    if (picSrcResults.length > 0) {
      await context.sync();
      for (const { src, fmt } of picSrcResults) {
        const dataUri = src.value ?? "";
        const comma = dataUri.indexOf(",");
        const base64 = comma >= 0 ? dataUri.slice(comma + 1) : dataUri;
        const format = String(fmt.imageFormat ?? "").toLowerCase() || "png";
        if (base64 && _isLikelyLoadingPlaceholderImage(base64, format)) {
          return { deferred: true } as const;
        }
      }
    }

    // Heading-exclusive body range: first body paragraph start → last body
    // paragraph end. Single getOoxml over the whole span.
    const start = paragraphs.items[headingIndex + 1].getRange("Start");
    const end = paragraphs.items[nextHeadingIndex - 1].getRange("End");
    const bodyRange = start.expandTo(end);
    const ooxmlResult = bodyRange.getOoxml();
    await context.sync();

    const ooxml = ooxmlResult.value ?? "";
    return ooxml.trim() ? { ooxml } : null;
  });
}

// ---------------------------------------------------------------------------
// Batch capture for the whole-document Save sweep.
//
// The single-section ``readSectionSubtree``/``readSectionSubtreeOoxml`` above
// each load the paragraph index once per call. Calling them per section (and
// twice per section — text + OOXML) makes a full-document Save
// O(sections × paragraphs) with hundreds of serialized ``context.sync()``
// round-trips. The batch readers below load the paragraph index ONCE, resolve
// all requested section spans against that in-memory snapshot (bookmark
// fallback included — it resolves against the same index, never a reload), and
// batch the Office.js calls (one sync for all image loads, one for all base64
// reads, one for all ``getOoxml`` calls). This is the hot path for
// ``captureAndSaveSections``.
// ---------------------------------------------------------------------------

/** One whole-document paragraph snapshot, loaded once and reused across every
 *  requested section. Every array mirrors ``paragraphs.items`` index-for-index
 *  so span resolution is pure in-memory. ``listNumbers``/``levels`` are only
 *  populated for heading paragraphs (``isHeading[i]``) — the only ones span
 *  resolution ever consults. */
interface DocParagraphIndex {
  paragraphs: Word.ParagraphCollection;
  count: number;
  texts: string[];
  /** ``styleBuiltIn`` (``Heading1``…, or ``Other`` for custom styles). */
  styles: string[];
  /** ``style`` display name (``AsthraHeading2``, ``Heading 2``, ``Normal``…). */
  styleNames: string[];
  /** ``_isHeadingStyle(styles[i], styleNames[i])`` — built-in OR display name. */
  isHeading: boolean[];
  /** Heading level 1–4 for headings, 0 otherwise. */
  levels: number[];
  /** Section number rendered by native list numbering, headings only. */
  listNumbers: (string | null)[];
}

/** Load the document's paragraphs ONCE and snapshot the fields span
 *  resolution needs. Two syncs: one bulk ``text,style,styleBuiltIn`` load of
 *  the whole collection, then list labels for the HEADING paragraphs only.
 *
 *  PERF (the Save hang, 2026-09-09): this used to mint a ``listItemOrNullObject``
 *  proxy for EVERY paragraph and load them all in one sync — O(document)
 *  tracked objects, thousands on a drafted CSR (table-cell paragraphs count
 *  too), which Word served in tens of seconds to minutes. The Load path hit the
 *  identical trap in July (see ``hydrateLiveDocFromBase64`` STEP3) and was
 *  fixed the same way: only heading paragraphs need a list label, and headings
 *  are ~two orders of magnitude fewer. ``_findBoundsInIndex`` never reads
 *  ``listNumbers`` for a non-heading, so this is behaviour-preserving. */
async function _loadParagraphIndex(context: Word.RequestContext): Promise<DocParagraphIndex> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("text,style,styleBuiltIn");
  await context.sync();

  const count = paragraphs.items.length;
  const texts: string[] = new Array(count);
  const styles: string[] = new Array(count);
  const styleNames: string[] = new Array(count);
  const isHeading: boolean[] = new Array(count);
  const levels: number[] = new Array(count);
  const listNumbers: (string | null)[] = new Array(count);
  const headingIndexes: number[] = [];
  for (let i = 0; i < count; i++) {
    const para = paragraphs.items[i];
    texts[i] = (para.text || "").trim();
    styles[i] = para.styleBuiltIn || "";
    styleNames[i] = para.style || "";
    isHeading[i] = _isHeadingStyle(styles[i], styleNames[i]);
    levels[i] = isHeading[i] ? _getHeadingLevel(styles[i], styleNames[i]) : 0;
    listNumbers[i] = null;
    if (isHeading[i]) headingIndexes.push(i);
  }

  // Native list numbering renders the number outside paragraph.text — load the
  // list labels of the heading paragraphs (only) so the in-memory match still
  // finds natively-numbered headings. Skipped entirely on a heading-less doc.
  if (headingIndexes.length > 0) {
    const listItemByIndex = new Map<number, Word.ListItem>();
    for (const i of headingIndexes) {
      const li = paragraphs.items[i].listItemOrNullObject;
      li.load("isNullObject,listString");
      listItemByIndex.set(i, li);
    }
    await context.sync();
    for (const [i, li] of listItemByIndex) {
      listNumbers[i] = li.isNullObject ? null : _numberFromListString(li.listString);
    }
  }
  console.info(`[wordWriter] paragraph index loaded: headings=${headingIndexes.length}/${count} paragraphs`);
  return { paragraphs, count, texts, styles, styleNames, isHeading, levels, listNumbers };
}

/** Resolve a section's heading + subtree boundary against a preloaded index —
 *  pure, in-memory Strategy 1 (heading text / list-label match + same-or-
 *  higher-level next-heading boundary). Returns null when no heading matches
 *  by text/list-label (the caller falls back to the bookmark strategy). */
function _findBoundsInIndex(index: DocParagraphIndex, sectionNumber: string): SectionSpan | null {
  const headingPrefix = `${sectionNumber} `;
  for (let i = 0; i < index.count; i++) {
    if (!index.isHeading[i]) continue;
    const text = index.texts[i];
    if (text.startsWith(headingPrefix) || text.startsWith(`${sectionNumber}  `) || index.listNumbers[i] === sectionNumber) {
      return _spanFromHeading(index, i);
    }
  }
  return null;
}

/** Span from a resolved heading index: body runs to the next heading of the
 *  SAME OR HIGHER level (lower or equal level number), or end of document.
 *
 *  NOTE: this intentionally DIFFERS from insertSectionContent, whose *delete*
 *  boundary stops at the next heading of ANY level (see the 2026-04-28
 *  decision comment there — deeply-nested CMC skeletons broke under the
 *  same-or-higher rule). So the read span for a parent section is WIDER than
 *  what an insert deletes; that asymmetry is deliberate and load-bearing —
 *  don't "align" them without re-checking the nested-CMC insert path. */
function _spanFromHeading(index: DocParagraphIndex, headingIndex: number): SectionSpan {
  const headingLevel = index.levels[headingIndex];
  let nextHeadingIndex = index.count;
  for (let i = headingIndex + 1; i < index.count; i++) {
    if (index.isHeading[i] && index.levels[i] <= headingLevel) {
      nextHeadingIndex = i;
      break;
    }
  }
  return { headingIndex, nextHeadingIndex };
}

/** A section's heading + exclusive body boundary within a paragraph list. */
interface SectionSpan {
  headingIndex: number;
  nextHeadingIndex: number;
}

/** Join the non-empty body paragraphs of a span (heading EXCLUSIVE) into the
 *  plain-text body — identical to ``readSectionSubtree``'s join. */
function _joinSpanBody(texts: string[], span: SectionSpan): string {
  const lines: string[] = [];
  for (let i = span.headingIndex + 1; i < span.nextHeadingIndex; i++) {
    const text = texts[i];
    if (text && text.trim().length > 0) lines.push(text);
  }
  return lines.join("\n").trim();
}

/**
 * Whether Word's Track Changes is currently ON (``TrackAll``). Best-effort:
 * returns ``false`` when Office is unavailable or the change-tracking API isn't
 * supported. Used by the Save capture to skip changed sections while Track
 * Changes is on — ``paragraph.text`` can fold in deleted text, and the
 * markdown-only Save has no OOXML capture to derive the accepted text from.
 */
export async function isChangeTrackingOn(): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  try {
    return await runWord("isChangeTrackingOn", async (context) => {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();
      return (doc.changeTrackingMode as string) === "TrackAll";
    });
  } catch {
    return false;
  }
}

/** Bodies + OOXML for many sections, captured together. */
export interface SectionCaptureResult {
  /** section number → body text (``null`` when the section isn't in the open
   *  doc — heading renamed away / never inserted). */
  bodies: Map<string, string | null>;
  /** section number → OOXML capture. Only populated for sections matched
   *  in-memory with a non-empty body; empty when ``withOoxml`` is false. */
  ooxml: Map<string, SectionOoxmlCapture>;
  /** True when the (best-effort) OOXML capture threw — bodies are still valid,
   *  so the caller degrades to a text-only save (matches the pre-unification
   *  behaviour where OOXML lived in its own ``Word.run``). */
  ooxmlFailed: boolean;
}

/**
 * Read the plain-text body AND (optionally) the OOXML of many sections in ONE
 * shared document scan — the hot path for ``captureAndSaveSections``. Loads the
 * whole-document paragraph index ONCE (the single largest Office.js marshalling
 * cost) and derives both outputs from it, instead of paying that full-doc load
 * twice (once for bodies, once for OOXML) as the separate readers did.
 *
 * Bodies contract matches ``readSectionSubtree``: a section absent from the open
 * doc maps to ``null`` (caller never overwrites the SSOT blindly). Sections not
 * matched in-memory fall back to ``_findBoundsByBookmark`` against the SAME
 * index (no paragraph reload); that set is normally empty. OOXML is batched exactly as
 * before — one sync for all inline-picture loads, one for all base64 reads
 * (placeholder-image defer), one for all ``getOoxml`` calls — and is captured
 * only for in-memory-matched, non-empty-body sections.
 */
export async function readSectionBodiesAndOoxml(
  sectionNumbers: string[],
  opts: { withOoxml?: boolean; onPhase?: (phase: "bodies" | "formatting") => void } = {},
): Promise<SectionCaptureResult> {
  const withOoxml = opts.withOoxml !== false;
  const bodies = new Map<string, string | null>();
  const ooxml = new Map<string, SectionOoxmlCapture>();
  let ooxmlFailed = false;
  if (!isOfficeAvailable()) {
    for (const sn of sectionNumbers) bodies.set(sn, null);
    return { bodies, ooxml, ooxmlFailed };
  }

  await runWord("readSectionBodiesAndOoxml", async (context) => {
    opts.onPhase?.("bodies");
    const index = await _loadParagraphIndex(context);

    // Resolve every requested span against the one in-memory snapshot. Bodies
    // land immediately; non-empty spans are also OOXML candidates.
    const notFound: string[] = [];
    const spans = new Map<string, SectionSpan>();
    for (const sn of sectionNumbers) {
      const span = _findBoundsInIndex(index, sn);
      if (span === null) {
        notFound.push(sn);
        continue;
      }
      bodies.set(sn, _joinSpanBody(index.texts, span));
      if (span.nextHeadingIndex > span.headingIndex + 1) spans.set(sn, span);
    }

    // Rare: heading renamed AND unnumbered → in-memory match misses. Resolve
    // just those bodies via the bookmark fallback — against the SAME index
    // (never a second paragraph load) — BEFORE the OOXML passes, so bodies are
    // fully captured even if OOXML then fails. OOXML for these is intentionally
    // omitted (text save covers correctness).
    if (notFound.length > 0) {
      console.info(
        `[wordWriter] readSectionBodiesAndOoxml: ${notFound.length}/${sectionNumbers.length} sections not matched by heading — trying bookmarks: ${notFound.join(", ")}`,
      );
    }
    for (const sn of notFound) {
      // Per-section guard: a single section's locate failure (an out-of-spec
      // bookmark name, or a sync that throws on this reconnected batch doc where
      // most spine sections have no matchable heading yet) must NOT propagate out
      // of ``runWord`` — that would discard the entire ``bodies`` map, including
      // the content already captured for the MATCHED sections above. Degrade just
      // this section to null (= "absent from the doc"); the compare step then
      // skips it WITHOUT overwriting the SSOT, while the matched sections' bodies
      // survive to be saved.
      try {
        const span = await _findBoundsByBookmark(context, index, sn);
        bodies.set(sn, span === null ? null : _joinSpanBody(index.texts, span));
      } catch (err) {
        console.warn(`[wordWriter] readSectionBodiesAndOoxml: locate failed for section ${sn} — treating as missing:`, err);
        bodies.set(sn, null);
      }
    }

    // OOXML passes (batched) — best-effort, only when the caller wants
    // formatting AND we have at least one non-empty matched span. Kept LAST and
    // in its own try/catch so a getOoxml failure degrades to a text-only save
    // (the pre-unification behaviour, when OOXML lived in a separate Word.run)
    // rather than losing the already-captured bodies.
    if (withOoxml && spans.size > 0) {
      opts.onPhase?.("formatting");
      try {
        // Pass 1: queue every body paragraph's inline pictures, one sync for all.
        const picsBySection = new Map<string, Word.InlinePictureCollection[]>();
        for (const [sn, span] of spans) {
          const cols: Word.InlinePictureCollection[] = [];
          for (let i = span.headingIndex + 1; i < span.nextHeadingIndex; i++) {
            const pics = index.paragraphs.items[i].inlinePictures;
            pics.load("items");
            cols.push(pics);
          }
          picsBySection.set(sn, cols);
        }
        await context.sync();

        // Pass 2: queue base64 + format for every picture across all sections.
        const picReadsBySection = new Map<
          string,
          { src: OfficeExtension.ClientResult<string>; fmt: Word.InlinePicture }[]
        >();
        let anyPics = false;
        for (const [sn, cols] of picsBySection) {
          const reads: { src: OfficeExtension.ClientResult<string>; fmt: Word.InlinePicture }[] = [];
          for (const pics of cols) {
            for (const pic of pics.items) {
              pic.load("imageFormat");
              reads.push({ src: pic.getBase64ImageSrc(), fmt: pic });
              anyPics = true;
            }
          }
          picReadsBySection.set(sn, reads);
        }
        if (anyPics) await context.sync();

        // Decide per section: defer (placeholder image) or queue getOoxml.
        const ooxmlResultsBySection = new Map<string, OfficeExtension.ClientResult<string>>();
        let anyOoxml = false;
        for (const [sn, span] of spans) {
          const reads = picReadsBySection.get(sn) ?? [];
          let deferred = false;
          for (const { src, fmt } of reads) {
            const dataUri = src.value ?? "";
            const comma = dataUri.indexOf(",");
            const base64 = comma >= 0 ? dataUri.slice(comma + 1) : dataUri;
            const format = String(fmt.imageFormat ?? "").toLowerCase() || "png";
            if (base64 && _isLikelyLoadingPlaceholderImage(base64, format)) {
              deferred = true;
              break;
            }
          }
          if (deferred) {
            ooxml.set(sn, { deferred: true });
            continue;
          }
          const start = index.paragraphs.items[span.headingIndex + 1].getRange("Start");
          const end = index.paragraphs.items[span.nextHeadingIndex - 1].getRange("End");
          const bodyRange = start.expandTo(end);
          ooxmlResultsBySection.set(sn, bodyRange.getOoxml());
          anyOoxml = true;
        }
        if (anyOoxml) await context.sync();

        for (const [sn, res] of ooxmlResultsBySection) {
          const raw = res.value ?? "";
          ooxml.set(sn, raw.trim() ? { ooxml: raw } : null);
        }
      } catch (err) {
        console.warn("[wordWriter] batch OOXML capture failed — saving text only:", err);
        ooxml.clear();
        ooxmlFailed = true;
      }
    }
  });
  return { bodies, ooxml, ooxmlFailed };
}

/**
 * Read the plain-text body of many sections in ONE document scan. Thin
 * delegator over ``readSectionBodiesAndOoxml`` (bodies only) — kept for
 * callers that don't need formatting. The Save sweep uses the unified reader
 * directly so it pays a single scan for bodies + OOXML.
 */
export async function readAllSectionBodies(sectionNumbers: string[]): Promise<Map<string, string | null>> {
  const { bodies } = await readSectionBodiesAndOoxml(sectionNumbers, { withOoxml: false });
  return bodies;
}

/**
 * Capture the flat-OPC OOXML of many sections in ONE document scan. Thin
 * delegator over ``readSectionBodiesAndOoxml`` (OOXML only) — kept for the
 * public API. The Save sweep uses the unified reader directly so bodies and
 * OOXML share a single paragraph-index load.
 */
export async function readSectionsOoxml(sectionNumbers: string[]): Promise<Map<string, SectionOoxmlCapture>> {
  const { ooxml } = await readSectionBodiesAndOoxml(sectionNumbers, { withOoxml: true });
  return ooxml;
}

export type SectionSpanReplaceResult =
  | "replaced"
  | "ambiguous_first_replaced"
  | "not_found"
  // The covered span reaches into a table cell. The paragraph-splice
  // write paths delete their covered paragraphs, which would corrupt the
  // table, so they abort with this instead. Table selections are meant to
  // go through the table-aware flow (captureTableSelection / applyTableRowEditsInSection);
  // this is the safety net for a prose edit that lands on a table.
  | "table_conflict";

/**
 * Replace one occurrence of ``originalText`` inside a section's body
 * (chat "edit highlight" Approve). Section-scoped so an identical
 * phrase elsewhere in the document is never touched.
 *
 * Strategy by original length:
 *   - ≤250 chars: ``Range.search`` within the section (Office.js caps
 *     search needles at ~255 chars). Formatting outside the match is
 *     untouched. Multiple hits are disambiguated by ``contextHint.prefix``
 *     against the section's plain text; when still ambiguous the FIRST
 *     occurrence is replaced and the result says so.
 *   - >250 chars: paragraph-level splice — the paragraphs covering the
 *     match are rewritten as plain text (surrounding paragraphs keep
 *     their formatting; the rewritten ones drop intra-paragraph runs).
 */
/** Result of section-scoped match discovery — shared by the plain
 *  and formatter-rendered span-replace paths. */
interface SectionMatchLocation {
  /** Paragraphs strictly between the section heading and the next
   *  same-or-higher heading, loaded with text + styleBuiltIn. */
  bodyParas: Word.Paragraph[];
  /** The section's heading paragraph — the insertion anchor when a
   *  formatter render must land at the top of the section body. */
  headingPara: Word.Paragraph;
  /** Normalized-join offsets of every occurrence of the needle in
   *  the section body text. Non-empty. */
  offsets: number[];
  /** Index into ``offsets`` selected by ``contextHint.prefix``
   *  disambiguation (first occurrence when ambiguous). */
  pickIndex: number;
  /** True when >1 occurrence and the hint couldn't disambiguate. */
  ambiguous: boolean;
  /** Whitespace-normalized needle used for the offset math. */
  originalNorm: string;
  /** Per-``bodyParas`` flag: true when that paragraph lives inside a
   *  table cell. The paragraph-splice write paths consult this to abort
   *  (``table_conflict``) rather than delete cell paragraphs and corrupt
   *  the table — mirroring the insert path's table safety invariant. */
  bodyParaInTable: boolean[];
}

const _normalizeSpanWs = (s: string) => s.replace(/\s+/g, " ").trim();

/**
 * Locate ``originalText`` inside ``sectionNumber``'s body. Section
 * discovery mirrors ``readSectionSubtree`` (heading text-prefix match
 * with the ``ASTHRA_SEC_*`` bookmark fallback); occurrence pick uses
 * the whitespace-normalized joined body text so hint-based
 * disambiguation maps 1:1 (in order) onto Office search hits.
 * Returns null when the section or the needle can't be found.
 */
async function _locateSectionMatch(
  context: Word.RequestContext,
  sectionNumber: string,
  originalText: string,
  contextHint?: { prefix?: string; suffix?: string },
): Promise<SectionMatchLocation | null> {
  const originalNorm = _normalizeSpanWs(originalText);
  if (!originalNorm) return null;

  const body = context.document.body;
  const paragraphs = body.paragraphs;
  paragraphs.load("text,styleBuiltIn");
  await context.sync();

  // Native list numbering renders the number outside paragraph.text —
  // batch-load list labels so the text-first discovery still matches.
  // Also batch-load parentTableCellOrNullObject so the splice paths can
  // detect (and refuse) a covered span that reaches into a table cell.
  const paraListItems = paragraphs.items.map((p: Word.Paragraph) => p.listItemOrNullObject);
  for (const li of paraListItems) {
    li.load("isNullObject,listString");
  }
  const paraTableCells = paragraphs.items.map((p: Word.Paragraph) => p.parentTableCellOrNullObject);
  for (const c of paraTableCells) {
    c.load("isNullObject");
  }
  await context.sync();

  // Locate the section heading — same discovery as readSectionSubtree.
  let headingIndex = -1;
  const headingPrefix = `${sectionNumber} `;
  for (let i = 0; i < paragraphs.items.length; i++) {
    const para = paragraphs.items[i];
    const text = para.text.trim();
    const style = para.styleBuiltIn || "";
    const listNumber = paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString);
    if (
      (style.includes("Heading") || style.includes("heading")) &&
      (text.startsWith(headingPrefix) || text.startsWith(`${sectionNumber}  `) || listNumber === sectionNumber)
    ) {
      headingIndex = i;
      break;
    }
  }
  if (headingIndex === -1) {
    const bookmarkName = _sectionBookmarkName(sectionNumber);
    const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
    bookmarkRange.load("text");
    await context.sync();
    if (!bookmarkRange.isNullObject) {
      const comparisons: OfficeExtension.ClientResult<Word.LocationRelation>[] = [];
      for (let i = 0; i < paragraphs.items.length; i++) {
        const paraRange = paragraphs.items[i].getRange("Whole");
        comparisons.push(bookmarkRange.compareLocationWith(paraRange));
      }
      await context.sync();
      for (let i = 0; i < comparisons.length; i++) {
        const v = comparisons[i].value;
        if (v === "InsideStart" || v === "Equal" || v === "Inside") {
          headingIndex = i;
          break;
        }
      }
    }
  }
  if (headingIndex === -1) return null;

  // Section body boundary — next heading of same-or-higher level.
  const headingLevel = _getHeadingLevel(paragraphs.items[headingIndex].styleBuiltIn || "");
  let nextHeadingIndex = paragraphs.items.length;
  for (let i = headingIndex + 1; i < paragraphs.items.length; i++) {
    const style = paragraphs.items[i].styleBuiltIn || "";
    if (style.includes("Heading") || style.includes("heading")) {
      if (_getHeadingLevel(style) <= headingLevel) {
        nextHeadingIndex = i;
        break;
      }
    }
  }
  if (nextHeadingIndex <= headingIndex + 1) return null;

  const bodyParas = paragraphs.items.slice(headingIndex + 1, nextHeadingIndex);
  const bodyParaInTable = paraTableCells
    .slice(headingIndex + 1, nextHeadingIndex)
    .map((c) => !c.isNullObject);
  const sectionText = _normalizeSpanWs(bodyParas.map((p) => p.text).join("\n"));

  // Which occurrence does the context hint select? Computed on the
  // normalized section text so it maps 1:1 (in order) onto the
  // Office search hits in the caller.
  const offsets: number[] = [];
  for (let at = sectionText.indexOf(originalNorm); at !== -1; at = sectionText.indexOf(originalNorm, at + 1)) {
    offsets.push(at);
  }
  if (offsets.length === 0) return null;
  let pickIndex = 0;
  let ambiguous = offsets.length > 1;
  if (ambiguous && contextHint?.prefix) {
    const prefixTail = _normalizeSpanWs(contextHint.prefix).slice(-40);
    if (prefixTail) {
      const matching = offsets
        .map((off, idx) => ({ off, idx }))
        .filter(({ off }) => sectionText.slice(Math.max(0, off - prefixTail.length - 5), off).includes(prefixTail));
      if (matching.length === 1) {
        pickIndex = matching[0].idx;
        ambiguous = false;
      }
    }
  }

  return {
    bodyParas,
    headingPara: paragraphs.items[headingIndex],
    offsets,
    pickIndex,
    ambiguous,
    originalNorm,
    bodyParaInTable,
  };
}

/** True when the covered paragraph span [firstPara..lastPara] reaches
 *  into a table cell — the guard the paragraph-splice write paths use to
 *  refuse a delete that would corrupt a table. */
function _coveredSpanTouchesTable(
  loc: SectionMatchLocation,
  span: { firstPara: number; lastPara: number },
): boolean {
  for (let i = span.firstPara; i <= span.lastPara; i++) {
    if (loc.bodyParaInTable[i]) return true;
  }
  return false;
}

/** Paragraph span covered by the picked occurrence, plus the text
 *  before/after the match inside the boundary paragraphs (normalized
 *  coordinates — intra-paragraph run formatting is not preserved). */
function _coveredParaSpan(
  bodyParas: Word.Paragraph[],
  loc: SectionMatchLocation,
): {
  firstPara: number;
  lastPara: number;
  prefixText: string;
  suffixText: string;
} | null {
  const target = loc.offsets[loc.pickIndex];
  let cursor = 0;
  let firstPara = -1;
  let lastPara = -1;
  let firstOffsetInPara = 0;
  const paraNorms = bodyParas.map((p) => _normalizeSpanWs(p.text));
  for (let i = 0; i < paraNorms.length; i++) {
    const start = cursor;
    const end = cursor + paraNorms[i].length;
    if (firstPara === -1 && target >= start && target < end) {
      firstPara = i;
      firstOffsetInPara = target - start;
    }
    if (firstPara !== -1 && target + loc.originalNorm.length <= end + 1) {
      lastPara = i;
      break;
    }
    cursor = end + 1; // +1 for the joining separator
  }
  if (firstPara === -1) return null;
  if (lastPara === -1) lastPara = paraNorms.length - 1;

  const prefixText = paraNorms[firstPara].slice(0, firstOffsetInPara).trimEnd();
  // Suffix within the LAST covered paragraph (chars after the match),
  // in the same normalized-join coordinates used to find ``target``.
  const lastStart = paraNorms.slice(0, lastPara).reduce((acc, t) => acc + t.length + 1, 0);
  const suffixText = paraNorms[lastPara].slice(Math.max(0, target + loc.originalNorm.length - lastStart)).trimStart();
  return { firstPara, lastPara, prefixText, suffixText };
}

export async function replaceTextInSection(
  sectionNumber: string,
  originalText: string,
  replacementText: string,
  contextHint?: { prefix?: string; suffix?: string },
): Promise<SectionSpanReplaceResult> {
  if (!isOfficeAvailable()) return "not_found";

  const result = await runWord("replaceTextInSection", async (context) => {
    const loc = await _locateSectionMatch(context, sectionNumber, originalText, contextHint);
    if (!loc) return "not_found" as SectionSpanReplaceResult;
    const { bodyParas, pickIndex, ambiguous, originalNorm } = loc;

    // The search path is only valid for needles Range.search can
    // actually match: Word's search-string ceiling is ~255 chars AND a
    // search can never cross a paragraph mark — a multi-paragraph
    // selection "found" by the normalized occurrence scan above would
    // always miss here, so route it to the splice path instead.
    // Both gates use the NORMALIZED needle (raw length overshoots for
    // whitespace-heavy selections; a raw-text search misses matches
    // the scan found, because the scan compared normalized text).
    const span = _coveredParaSpan(bodyParas, loc);
    const spansParagraphs = span !== null && span.lastPara > span.firstPara;

    if (originalNorm.length <= 250 && !spansParagraphs) {
      // Range.search path — full fidelity outside the match.
      const startRange = bodyParas[0].getRange("Start");
      const endRange = bodyParas[bodyParas.length - 1].getRange("End");
      const sectionRange = startRange.expandTo(endRange);
      const hits = sectionRange.search(originalNorm, {
        matchCase: true,
        ignoreSpace: true,
      });
      hits.load("items");
      await context.sync();
      let items = hits.items;
      if (items.length === 0) {
        const retry = sectionRange.search(originalNorm, {
          matchCase: false,
          ignoreSpace: true,
        });
        retry.load("items");
        await context.sync();
        items = retry.items;
      }
      if (items.length === 0) return "not_found" as SectionSpanReplaceResult;
      const target = items[Math.min(pickIndex, items.length - 1)];
      const inserted = target.insertText(replacementText, Word.InsertLocation.replace);
      // The replaced span may be (part of) a flag — [MISSING INFO], [REVIEWER:],
      // [REGULATOR FLAG:], etc. — carrying a font.highlightColor. insertText
      // inherits the replaced run's formatting, so the approved text would keep
      // the flag's highlight; clear it so the resolution reads as plain prose.
      // (null is Office.js's documented "off" value; see clearHighlight.)
      inserted.font.highlightColor = null as unknown as string;
      await context.sync();
      return (ambiguous && items.length > 1 ? "ambiguous_first_replaced" : "replaced") as SectionSpanReplaceResult;
    }

    // Long or multi-paragraph original: paragraph-level splice over
    // the covering span found via normalized per-paragraph offsets.
    if (!span) return "not_found" as SectionSpanReplaceResult;
    // Table safety: the splice deletes covered paragraphs. If any of them
    // is a table-cell paragraph, deleting would corrupt the table — refuse
    // (table selections belong on the table-aware flow).
    if (_coveredSpanTouchesTable(loc, span)) return "table_conflict" as SectionSpanReplaceResult;
    const { firstPara, lastPara, prefixText, suffixText } = span;

    const mergedText = [prefixText, replacementText.trim(), suffixText].filter((s) => s.length > 0).join(" ");
    const spliced = bodyParas[firstPara].insertText(mergedText, Word.InsertLocation.replace);
    // Drop any flag highlight inherited from the replaced span (see the
    // Range.search path above) so the approved text isn't left flagged.
    spliced.font.highlightColor = null as unknown as string;
    // Delete the remaining covered paragraphs in reverse order.
    for (let i = lastPara; i > firstPara; i--) {
      bodyParas[i].delete();
    }
    await context.sync();
    return (ambiguous ? "ambiguous_first_replaced" : "replaced") as SectionSpanReplaceResult;
  });

  return result ?? "not_found";
}

/**
 * Scroll to and SELECT the paragraph span a resolve proposal will change —
 * without modifying it. The review queue's "reveal in Word": when a proposal
 * card becomes the active one under review, the writer sees the exact target
 * paragraph highlighted in the open document. Same section-scoped discovery +
 * occurrence disambiguation as ``replaceTextInSection`` (shared
 * ``_locateSectionMatch``), but selects instead of replacing. Best-effort:
 * returns false when the section or span can't be located, so the caller
 * no-ops quietly rather than blocking the review.
 */
export async function selectSpanInSection(
  sectionNumber: string,
  originalText: string,
  contextHint?: { prefix?: string; suffix?: string },
): Promise<boolean> {
  if (!isOfficeAvailable()) return false;

  const result = await runWord("selectSpanInSection", async (context) => {
    const loc = await _locateSectionMatch(context, sectionNumber, originalText, contextHint);
    if (!loc) return false;
    const { bodyParas, pickIndex, originalNorm } = loc;
    const span = _coveredParaSpan(bodyParas, loc);
    const spansParagraphs = span !== null && span.lastPara > span.firstPara;

    // Short, single-paragraph span: search + select the picked occurrence so
    // the highlight is exactly the changing text (Range.select scrolls it in).
    if (originalNorm.length <= 250 && !spansParagraphs) {
      const startRange = bodyParas[0].getRange("Start");
      const endRange = bodyParas[bodyParas.length - 1].getRange("End");
      const sectionRange = startRange.expandTo(endRange);
      const hits = sectionRange.search(originalNorm, { matchCase: true, ignoreSpace: true });
      hits.load("items");
      await context.sync();
      let items = hits.items;
      if (items.length === 0) {
        const retry = sectionRange.search(originalNorm, { matchCase: false, ignoreSpace: true });
        retry.load("items");
        await context.sync();
        items = retry.items;
      }
      if (items.length > 0) {
        items[Math.min(pickIndex, items.length - 1)].select();
        await context.sync();
        return true;
      }
    }

    // Long / multi-paragraph span (or a search miss): select the covering
    // paragraph range so the whole changing block scrolls into view.
    if (span) {
      const range = bodyParas[span.firstPara]
        .getRange("Start")
        .expandTo(bodyParas[span.lastPara].getRange("End"));
      range.select();
      await context.sync();
      return true;
    }

    // Nothing precise located — at least scroll the section's first body
    // paragraph into view rather than leaving the writer where they were.
    bodyParas[0].getRange("Whole").select();
    await context.sync();
    return true;
  });

  return result ?? false;
}

/** Font fields loaded off a Word range — all proxy-loaded, any may be blank. */
interface LoadedRangeFont {
  name?: string;
  size?: number;
  color?: string;
}

/**
 * Build a ``FontConfig`` override from a Word range's loaded font so the
 * styled edit-highlight splice re-renders in the DESTINATION paragraph's
 * typeface — instead of the formatter default (Times New Roman 11pt black)
 * that made an approved edit visibly diverge from its surroundings.
 *
 * Only well-formed values are threaded: a non-empty ``name``, a positive
 * numeric ``size`` (→ ``sizeBody``), and a ``#RRGGBB`` ``color``. A mixed-run
 * or empty read (Word returns "" / null for a range spanning differing runs)
 * yields ``undefined`` for that field, and if nothing safe was read the whole
 * helper returns ``undefined`` so the caller keeps DEFAULT_CONFIG. Pure — it
 * never assigns a value back to Word, so it carries no batch-torch risk.
 */
export function _fontOverrideFromLoaded(font: LoadedRangeFont | undefined): FontConfig | undefined {
  if (!font) return undefined;
  const name = typeof font.name === "string" && font.name.trim() ? font.name : undefined;
  const size = typeof font.size === "number" && font.size > 0 ? font.size : undefined;
  const color = typeof font.color === "string" && /^#[0-9a-fA-F]{6}$/.test(font.color) ? font.color : undefined;
  if (!name && size === undefined && !color) return undefined;
  return {
    ...DEFAULT_CONFIG.font,
    ...(name ? { name } : {}),
    ...(size !== undefined ? { sizeBody: size } : {}),
    ...(color ? { color } : {}),
  };
}

/**
 * Replace one occurrence of ``originalText`` inside a section's body
 * with FORMATTER-RENDERED markdown (chat "edit highlight" Approve
 * when the replacement contains block content — pipe tables, ##/###
 * sub-headings, paragraph breaks, or bold runs).
 *
 * Same section-scoped discovery + occurrence disambiguation as
 * ``replaceTextInSection`` (shared ``_locateSectionMatch``), but the
 * write path is ALWAYS a paragraph-level splice: the paragraphs
 * covering the match are deleted and ``prefix + replacement + suffix``
 * is re-rendered through ``formatAndInsert`` — the exact pipeline
 * fresh section drafts use — so tables become real Word tables and
 * headings become bold sub-heading paragraphs (never Word Heading
 * styles; the ICH outline numbering is untouched).
 *
 * Trade-off (documented parity with the >250-char splice path):
 * intra-paragraph run formatting of the COVERED paragraphs (e.g. a
 * bold word in the prefix text) is dropped — the surrounding
 * paragraphs outside the covered span keep their formatting.
 */
export async function replaceTextInSectionStyled(
  sectionNumber: string,
  originalText: string,
  replacementMarkdown: string,
  contextHint?: { prefix?: string; suffix?: string },
): Promise<SectionSpanReplaceResult> {
  if (!isOfficeAvailable()) return "not_found";

  const result = await runWord("replaceTextInSectionStyled", async (context) => {
    const loc = await _locateSectionMatch(context, sectionNumber, originalText, contextHint);
    if (!loc) return "not_found" as SectionSpanReplaceResult;
    const { bodyParas, headingPara, ambiguous } = loc;

    const span = _coveredParaSpan(bodyParas, loc);
    if (!span) return "not_found" as SectionSpanReplaceResult;
    // Table safety: this path ALWAYS deletes the covered paragraphs and
    // re-renders. If the span reaches a table cell, that would corrupt the
    // table — refuse (table selections belong on the table-aware flow).
    if (_coveredSpanTouchesTable(loc, span)) return "table_conflict" as SectionSpanReplaceResult;
    const { firstPara, lastPara, prefixText, suffixText } = span;

    // Prefix/suffix become their own formatter paragraphs around the
    // replacement block (blank-line separated so parseMarkdown treats
    // them as distinct paragraphs / table boundaries).
    const merged = [prefixText, replacementMarkdown.trim(), suffixText].filter((s) => s.length > 0).join("\n\n");

    // Insertion anchor: the body paragraph immediately BEFORE the
    // covered span — or the section heading itself when the span
    // starts at the first body paragraph (formatAndInsert inserts
    // AFTER its anchor).
    const anchorPara = firstPara > 0 ? bodyParas[firstPara - 1] : headingPara;

    // Capture the destination paragraph's run font BEFORE deleting it so
    // the re-rendered splice matches the surrounding body instead of the
    // formatter's Times-New-Roman-11 default. Read-only load — nothing is
    // assigned back to Word, so no batch-torch risk (see wordRenderer).
    const destFont = bodyParas[firstPara].getRange("Whole").font;
    destFont.load("name,size,color");
    await context.sync();
    const fontOverride = _fontOverrideFromLoaded(destFont);

    // Delete the covered paragraphs (reverse order keeps indices
    // stable), then render the merged markdown in their place.
    for (let i = lastPara; i >= firstPara; i--) {
      bodyParas[i].delete();
    }
    await context.sync();

    // Empty sectionNumber disables heading-dedup — we are not
    // re-inserting the section's own numbered heading. Config mirrors
    // insertSectionContent (the fresh-section pipeline), plus the
    // captured destination font so styled edits inherit the body typeface.
    await formatAndInsert(context, anchorPara, merged, "", {
      demoMode: isDemoMode(),
      ...(fontOverride ? { font: fontOverride } : {}),
    });
    await context.sync();

    return (ambiguous ? "ambiguous_first_replaced" : "replaced") as SectionSpanReplaceResult;
  });

  return result ?? "not_found";
}

// ---------------------------------------------------------------------------
// Selection anchoring (chat "edit highlight" Approve) — wrap the
// highlighted range in a hidden content control at proposal time so
// Approve replaces the EXACT range instead of re-finding it by string
// search. Robust to duplicate text, whitespace drift, >255-char and
// multi-paragraph selections, and edits elsewhere in the section. The
// string-search path (replaceTextInSection*) stays as the fallback
// when anchoring failed or the anchor was lost.
// ---------------------------------------------------------------------------

/** Tag prefix for edit anchors — see removeEditAnchors' stale sweep. */
export const EDIT_ANCHOR_TAG_PREFIX = "asthra-edit-";

export type AnchoredReplaceResult = SectionSpanReplaceResult | "anchor_lost";

/**
 * Wrap the CURRENT Word selection in a hidden content control tagged
 * ``tag`` — but only when the live selection still whitespace-
 * normalized-equals ``expectedText`` (the writer may have moved the
 * cursor since capture; anchoring the wrong range would make Approve
 * replace the wrong text, which is strictly worse than falling back
 * to the string path). Returns true when the anchor was placed.
 */
export async function anchorCurrentSelection(tag: string, expectedText: string): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  const expectedNorm = _normalizeSpanWs(expectedText);
  if (!expectedNorm) return false;

  const result = await runWord("anchorCurrentSelection", async (context) => {
    const selection = context.document.getSelection();
    selection.load("text,isEmpty");
    await context.sync();
    if (selection.isEmpty || _normalizeSpanWs(selection.text) !== expectedNorm) return false;

    const cc = selection.insertContentControl();
    cc.tag = tag;
    cc.title = "Asthra edit";
    // Hidden appearance — no bounding box / tag chrome in the doc.
    // Same idiom as citationMarkerWriter's citation controls.
    cc.appearance = Word.ContentControlAppearance.hidden;
    cc.cannotEdit = false;
    cc.cannotDelete = false;
    await context.sync();
    return true;
  });
  return result ?? false;
}

/**
 * Delete edit-anchor content controls, keeping their content. With a
 * ``tag`` only that anchor is removed; without one, EVERY control
 * whose tag starts with ``EDIT_ANCHOR_TAG_PREFIX`` is swept — the
 * defensive cleanup ``start()`` runs so an anchor orphaned by a
 * crashed prior flow can't linger in the writer's document.
 */
export async function removeEditAnchors(tag?: string): Promise<void> {
  if (!isOfficeAvailable()) return;
  await runWord("removeEditAnchors", async (context) => {
    const ccs = tag
      ? context.document.contentControls.getByTag(tag)
      : context.document.contentControls;
    ccs.load("items/tag");
    await context.sync();
    const candidates = ccs.items.filter(
      (cc) => tag || (cc.tag ?? "").startsWith(EDIT_ANCHOR_TAG_PREFIX),
    );
    // A content control wrapping a whole table cannot be removed safely —
    // cc.delete(keepContent) blanks the table. Newer flows never anchor a
    // table, but a leftover whole-table anchor from an older build must be
    // left in place (a hidden, harmless orphan) rather than blanked.
    for (const cc of candidates) cc.tables.load("items");
    await context.sync();
    let deleted = 0;
    for (const cc of candidates) {
      if (cc.tables.items.length > 0) continue; // wraps a table — never delete
      cc.delete(true); // keepContent — only the anchor goes
      deleted += 1;
    }
    if (deleted > 0) await context.sync();
    return undefined;
  });
}

/**
 * Replace the anchored selection with ``replacement``. Verifies the
 * anchor still holds the proposal's original text (normalized) before
 * writing — an edit INSIDE the anchor invalidates it (``anchor_lost``,
 * caller falls back to the string path). Plain prose replaces inline
 * (surrounding run formatting preserved); ``styled`` re-renders
 * prefix-free through the formatter pipeline via a paragraph splice.
 * The anchor control is always deleted (keepContent) on the way out.
 */
export async function replaceAnchoredSelection(
  tag: string,
  originalText: string,
  replacement: string,
  styled: boolean,
): Promise<AnchoredReplaceResult> {
  if (!isOfficeAvailable()) return "anchor_lost";

  const result = await runWord("replaceAnchoredSelection", async (context) => {
    const ccs = context.document.contentControls.getByTag(tag);
    ccs.load("items");
    await context.sync();
    if (ccs.items.length === 0) return "anchor_lost" as AnchoredReplaceResult;
    const cc = ccs.items[0];
    cc.load("text");
    await context.sync();

    // Table safety: load the anchored paragraphs' table-cell flags up front.
    // A styled splice deletes them, and a plain insertText that spans more
    // than one paragraph (i.e. more than one cell) would collapse cells —
    // either corrupts the table. Refuse (table_conflict); a highlight inside
    // a single cell (one paragraph) still replaces inline safely.
    const anchorParas = cc.paragraphs;
    anchorParas.load("items");
    await context.sync();
    const anchorTableCells = anchorParas.items.map((p) => p.parentTableCellOrNullObject);
    for (const c of anchorTableCells) c.load("isNullObject");
    await context.sync();
    const anchorInTable = anchorTableCells.some((c) => !c.isNullObject);

    if (_normalizeSpanWs(cc.text) !== _normalizeSpanWs(originalText)) {
      // Content drifted inside the anchor — the proposal no longer
      // describes what's there. Drop the anchor and let the caller
      // decide (fallback search or writer-facing error).
      cc.delete(true);
      await context.sync();
      return "anchor_lost" as AnchoredReplaceResult;
    }

    if (anchorInTable && (styled || anchorParas.items.length > 1)) {
      cc.delete(true);
      await context.sync();
      return "table_conflict" as AnchoredReplaceResult;
    }

    if (!styled) {
      const inserted = cc.insertText(replacement, Word.InsertLocation.replace);
      // The anchored span may be (part of) a flag carrying a
      // font.highlightColor; insertText inherits it, so the approved edit would
      // stay highlighted. Clear it so the resolution reads as plain prose.
      inserted.font.highlightColor = null as unknown as string;
      cc.delete(true);
      await context.sync();
      return "replaced" as AnchoredReplaceResult;
    }

    // Styled path — paragraph splice + formatter render, mirroring
    // replaceTextInSectionStyled but scoped by the anchor's ranges
    // instead of section discovery.
    const ccParas = cc.paragraphs;
    ccParas.load("items");
    await context.sync();
    if (ccParas.items.length === 0) {
      cc.delete(true);
      await context.sync();
      return "anchor_lost" as AnchoredReplaceResult;
    }
    const firstPara = ccParas.items[0];
    const lastPara = ccParas.items[ccParas.items.length - 1];

    // Text in the boundary paragraphs outside the anchored span.
    const prefixRange = firstPara.getRange("Start").expandTo(cc.getRange("Start"));
    const suffixRange = cc.getRange("End").expandTo(lastPara.getRange("End"));
    prefixRange.load("text");
    suffixRange.load("text");
    const prevPara = firstPara.getPreviousOrNullObject();
    prevPara.load("isNullObject");
    // Destination font — same read-only capture as replaceTextInSectionStyled,
    // piggybacked on this existing sync (no extra round-trip) so the styled
    // splice inherits the anchor paragraph's typeface instead of the default.
    const destFont = firstPara.getRange("Whole").font;
    destFont.load("name,size,color");
    await context.sync();

    const prefixText = _normalizeSpanWs(prefixRange.text);
    const suffixText = _normalizeSpanWs(suffixRange.text);
    const merged = [prefixText, replacement.trim(), suffixText].filter((s) => s.length > 0).join("\n\n");
    const fontOverride = _fontOverrideFromLoaded(destFont);

    if (prevPara.isNullObject) {
      // Anchor starts at the very first paragraph of the document —
      // no insertion anchor for formatAndInsert. Extremely unlikely
      // for a section span; fall back rather than guess.
      cc.delete(true);
      await context.sync();
      return "anchor_lost" as AnchoredReplaceResult;
    }

    // Delete the covered paragraphs (the anchor dies with them), then
    // render the merged markdown in their place — the same pipeline
    // fresh section drafts use.
    for (let i = ccParas.items.length - 1; i >= 0; i--) {
      ccParas.items[i].delete();
    }
    await context.sync();
    await formatAndInsert(context, prevPara, merged, "", {
      demoMode: isDemoMode(),
      ...(fontOverride ? { font: fontOverride } : {}),
    });
    await context.sync();
    return "replaced" as AnchoredReplaceResult;
  });

  return result ?? "anchor_lost";
}

// ---------------------------------------------------------------------------
// Table-aware "edit highlight" — when the writer highlights cells in a
// table, the affected ROWS are rewritten one at a time (see
// useHighlightEditFlow's table branch) and the changed cells are written
// back in place. Writing a cell is ``cell.body.insertText(text, replace)``
// — the setTableCellValues primitive — which never deletes a paragraph, so
// it cannot corrupt table structure (unlike the prose splice paths, which
// now refuse table spans with ``table_conflict``).
//
// captureTableSelection detects the table under the live selection and
// snapshots the highlighted rows' cell text (including their ORIGINAL cell
// values). It deliberately creates NO content control: a content control
// wrapping a whole table cannot be removed safely — cc.delete(keepContent)
// blanks the table — so Approve re-finds the table by section number +
// matching those original cell values instead (applyTableRowEditsInSection).
// ---------------------------------------------------------------------------

/** One highlighted row captured from a table selection: its 0-based
 *  index in the table (global, header-inclusive — the index getCell uses)
 *  and its current cell text. */
export interface CapturedTableRow {
  rowIndex: number;
  cells: string[];
}

export interface TableSelectionCapture {
  /** True when the live selection sits inside a single table AND the table
   *  is safe to edit cell-wise (no merged/ragged cells). False routes the
   *  caller to the ordinary prose flow. */
  usable: boolean;
  /** Why the table flow was declined (only when usable is false and the
   *  selection WAS in a table) — surfaced for logging, not the writer. */
  reason?: string;
  /** Header row (table row 0) cell text, for the preview's column headings.
   *  Null when the highlighted rows include row 0. */
  header: string[] | null;
  /** Highlighted rows in document order — the rewrite targets. Non-empty when
   *  usable. */
  rows: CapturedTableRow[];
  /** EVERY row of the table (0..N-1) with its current cell text, so the preview
   *  can show the WHOLE table diff in one shot (highlighted rows rewritten, the
   *  rest passthrough/unchanged). */
  allRows: CapturedTableRow[];
  columnCount: number;
}

const _emptyTableCapture: TableSelectionCapture = {
  usable: false,
  header: null,
  rows: [],
  allRows: [],
  columnCount: 0,
};

/**
 * Detect the table under the current selection and snapshot its highlighted
 * rows (with their original cell values). No content control is placed —
 * Approve re-finds the table by section + matching original cells.
 *
 * Declines (``usable: false``) — caller falls back to the prose flow — when:
 * the selection isn't in a table, the table has merged/ragged cells (pipe
 * round-trip would misalign data), or no row intersects the selection.
 */
export async function captureTableSelection(): Promise<TableSelectionCapture> {
  if (!isOfficeAvailable()) return _emptyTableCapture;

  const result = await runWord("captureTableSelection", async (context) => {
    const selection = context.document.getSelection();
    const parentTable = selection.parentTableOrNullObject;
    parentTable.load("isNullObject");
    await context.sync();
    if (parentTable.isNullObject) return _emptyTableCapture;

    // Snapshot the whole table's values so we can (a) map the selection
    // onto row indices and (b) detect merged/ragged cells.
    parentTable.load("values,rowCount");
    await context.sync();

    const values = (parentTable.values ?? []) as string[][];
    const rowCount = parentTable.rowCount ?? values.length;
    if (rowCount === 0 || values.length === 0) {
      return { ..._emptyTableCapture, reason: "empty_table" };
    }
    // Merged/ragged cells: uneven row lengths break the pipe round-trip.
    const widths = values.map((r) => r.length);
    const columnCount = Math.max(...widths);
    if (widths.some((w) => w !== columnCount)) {
      return { ..._emptyTableCapture, reason: "merged_cells" };
    }

    // Which rows does the selection intersect? Word.TableRow has no
    // getRange, so build each row's range from its first→last cell body
    // (start of column 0 → end of the last column). Batch every row-range
    // intersection into one sync.
    const rowRanges = Array.from({ length: rowCount }, (_, i) =>
      parentTable
        .getCell(i, 0)
        .body.getRange("Start")
        .expandTo(parentTable.getCell(i, columnCount - 1).body.getRange("End")),
    );
    const intersections = rowRanges.map((rr) => selection.intersectWithOrNullObject(rr));
    for (const ix of intersections) ix.load("isNullObject");
    await context.sync();

    const highlighted: number[] = [];
    for (let i = 0; i < intersections.length; i++) {
      if (!intersections[i].isNullObject) highlighted.push(i);
    }
    if (highlighted.length === 0) {
      return { ..._emptyTableCapture, reason: "no_row_intersection" };
    }

    const includesHeader = highlighted.includes(0);
    const capturedRows: CapturedTableRow[] = highlighted.map((rowIndex) => ({
      rowIndex,
      cells: (values[rowIndex] ?? []).slice(),
    }));
    // Every row, so the preview can render the whole table (not just the
    // highlighted rows) with the changed cells highlighted.
    const allRows: CapturedTableRow[] = values.map((cells, rowIndex) => ({
      rowIndex,
      cells: (cells ?? []).slice(),
    }));

    return {
      usable: true,
      header: includesHeader ? null : (values[0] ?? []).slice(),
      rows: capturedRows,
      allRows,
      columnCount,
    } as TableSelectionCapture;
  });

  return result ?? _emptyTableCapture;
}

/** One row's write-back for a section-scoped table edit (the bulk-resolve
 *  review queue AND the edit-highlight flow). The 0-based table row index,
 *  the full row of proposed cell values, which columns actually changed
 *  (only those cells are written), and the row's ORIGINAL cell values so the
 *  right table can be picked when a section has more than one (and a drifted
 *  row skipped rather than overwritten blindly). */
export interface SectionTableRowWrite {
  rowIndex: number;
  cells: string[];
  changedColumns: number[];
  originalCells: string[];
}

export type SectionTableApplyResult = "applied" | "not_found";

/** Locate the ``Word.Table`` objects inside ``sectionNumber``'s body (heading →
 *  next same-or-higher heading). Section discovery mirrors ``_locateSectionMatch``
 *  (heading text-prefix / native-list-number match with the ``ASTHRA_SEC_*``
 *  bookmark fallback).
 *
 *  Uses ``body.tables`` scoped by ``compareLocationWith`` — NOT ``range.tables``,
 *  which returns only tables FULLY CONTAINED in the range and so comes back empty
 *  when the section body IS the table (the first/last body paragraphs are then
 *  inside the table, and the range starts/ends inside it). Falls back to ALL body
 *  tables when the heading isn't discoverable or nothing is scoped in — the
 *  caller's per-row cell matching disambiguates. Always returns a list. */
async function _locateSectionTables(
  context: Word.RequestContext,
  sectionNumber: string,
): Promise<Word.Table[]> {
  const body = context.document.body;
  const allTables = body.tables;
  allTables.load("items");
  const paragraphs = body.paragraphs;
  paragraphs.load("text,styleBuiltIn");
  await context.sync();
  if (allTables.items.length === 0) return [];

  const paraListItems = paragraphs.items.map((p: Word.Paragraph) => p.listItemOrNullObject);
  for (const li of paraListItems) li.load("isNullObject,listString");
  await context.sync();

  let headingIndex = -1;
  const headingPrefix = `${sectionNumber} `;
  for (let i = 0; i < paragraphs.items.length; i++) {
    const para = paragraphs.items[i];
    const text = para.text.trim();
    const style = para.styleBuiltIn || "";
    const listNumber = paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString);
    if (
      (style.includes("Heading") || style.includes("heading")) &&
      (text.startsWith(headingPrefix) || text.startsWith(`${sectionNumber}  `) || listNumber === sectionNumber)
    ) {
      headingIndex = i;
      break;
    }
  }
  if (headingIndex === -1) {
    const bookmarkRange = context.document.getBookmarkRangeOrNullObject(_sectionBookmarkName(sectionNumber));
    bookmarkRange.load("isNullObject");
    await context.sync();
    if (!bookmarkRange.isNullObject) {
      const comparisons = paragraphs.items.map((p: Word.Paragraph) =>
        bookmarkRange.compareLocationWith(p.getRange("Whole")),
      );
      await context.sync();
      for (let i = 0; i < comparisons.length; i++) {
        const v = comparisons[i].value;
        if (v === "InsideStart" || v === "Equal" || v === "Inside") {
          headingIndex = i;
          break;
        }
      }
    }
  }
  // Heading undiscoverable → hand back every table; cell matching picks the right one.
  if (headingIndex === -1) return allTables.items;

  const headingLevel = _getHeadingLevel(paragraphs.items[headingIndex].styleBuiltIn || "");
  let nextHeadingIndex = paragraphs.items.length;
  for (let i = headingIndex + 1; i < paragraphs.items.length; i++) {
    const style = paragraphs.items[i].styleBuiltIn || "";
    if ((style.includes("Heading") || style.includes("heading")) && _getHeadingLevel(style) <= headingLevel) {
      nextHeadingIndex = i;
      break;
    }
  }

  // Section range from the heading start to the NEXT heading's start (or body
  // end) — deliberately NOT ``lastBodyPara.getRange("End")``, which would sit
  // inside a trailing table. Scope tables by comparing each table's range to it.
  const sectionStart = paragraphs.items[headingIndex].getRange("Start");
  const sectionEnd =
    nextHeadingIndex < paragraphs.items.length
      ? paragraphs.items[nextHeadingIndex].getRange("Start")
      : body.getRange("End");
  const sectionRange = sectionStart.expandTo(sectionEnd);
  const relations = allTables.items.map((t) => t.getRange("Whole").compareLocationWith(sectionRange));
  await context.sync();

  const inSection: Word.Table[] = [];
  for (let i = 0; i < allTables.items.length; i++) {
    const v = relations[i].value;
    if (v === "Inside" || v === "InsideStart" || v === "InsideEnd" || v === "Equal") {
      inSection.push(allTables.items[i]);
    }
  }
  // Nothing scoped in (odd heading/table geometry) → fall back to all tables.
  return inSection.length > 0 ? inSection : allTables.items;
}

/**
 * Mirror an approved TABLE proposal into the open Word document by writing
 * changed cells in place. Used by BOTH the bulk-resolve review queue and the
 * edit-highlight flow — neither anchors the table in a content control (a
 * whole-table content control blanks the table when removed). Finds the
 * section's table(s), picks the one whose rows match the edits' ``originalCells``
 * (disambiguating when a section has several tables, and skipping a table that
 * already drifted), then
 * writes each changed cell with ``getCell(r,c).body.insertText(text, replace)``
 * — no paragraph deletion, so the table structure is untouched. Returns
 * ``not_found`` when the section/table can't be located or nothing matched (the
 * SSOT is already updated server-side, so the caller falls back to a reload hint).
 */
export async function applyTableRowEditsInSection(
  sectionNumber: string,
  edits: readonly SectionTableRowWrite[],
): Promise<SectionTableApplyResult> {
  if (!isOfficeAvailable() || edits.length === 0) return "not_found";
  const norm = (s: string) => s.replace(/\s+/g, " ").trim();

  const result = await runWord("applyTableRowEditsInSection", async (context) => {
    const tables = await _locateSectionTables(context, sectionNumber);
    if (!tables || tables.length === 0) return "not_found" as SectionTableApplyResult;

    for (const t of tables) t.load("rowCount,values");
    await context.sync();

    const matchesTable = (values: string[][]): boolean =>
      edits.every((edit) => {
        const row = values[edit.rowIndex];
        return (
          Array.isArray(row) &&
          row.length === edit.originalCells.length &&
          edit.originalCells.every((c, i) => norm(row[i] ?? "") === norm(c))
        );
      });

    const table = tables.find((t) => matchesTable((t.values ?? []) as string[][]));
    if (!table) return "not_found" as SectionTableApplyResult;
    const rowCount = table.rowCount ?? (table.values ?? []).length;

    // Write ONLY the changed cells, one at a time with a sync after each. This
    // mirrors the proven existing-table update in updateTitlePageFields — and is
    // deliberately NOT a batched ``body.insertText(..., "Replace")`` across cells:
    // that collapses the whole table (only the last write survives, the rest
    // blank). ``body.clear()`` empties the cell while keeping the cell and its
    // shading/borders/width; ``insertText(..., "Start")`` then sets the new text.
    for (const edit of edits) {
      if (edit.rowIndex < 0 || edit.rowIndex >= rowCount) continue;
      for (const col of edit.changedColumns) {
        if (col < 0 || col >= edit.cells.length) continue;
        const cellBody = table.getCell(edit.rowIndex, col).body;
        cellBody.clear();
        const inserted = cellBody.insertText(`${edit.cells[col] ?? ""}`, Word.InsertLocation.start);
        // A resolved marker cell was styled as a flag ([MISSING INFO:…] etc.) —
        // highlight + red italic. A fresh insertText inherits that styling via
        // the cleared cell's paragraph mark, so neutralise it: the filled value
        // must read as plain cell text, not a lingering flag. highlightColor is
        // removed with ``null`` (the string "NoColor"/"None" throws on Word for
        // Web — see formatter/wordRenderer.clearHighlight).
        inserted.font.highlightColor = null as unknown as string;
        inserted.font.italic = false;
        inserted.font.bold = false;
        inserted.font.color = "#000000";
        await context.sync();
      }
    }
    return "applied" as SectionTableApplyResult;
  });

  return result ?? "not_found";
}

export async function readAllSections(): Promise<LiveSection[]> {
  if (!isOfficeAvailable()) {
    return [];
  }

  return runWord("readAllSections", async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text,styleBuiltIn");
    await context.sync();

    // Native list numbering renders section numbers outside paragraph.text —
    // batch-load every paragraph's list label (one sync, same pattern as
    // hydrateLiveDocFromBase64 STEP3) so numbered headings keep their ICH
    // identity even when the text is title-only.
    const paraListItems = paragraphs.items.map((p: Word.Paragraph) => p.listItemOrNullObject);
    for (const li of paraListItems) {
      li.load("isNullObject,listString");
    }
    await context.sync();

    const sections: LiveSection[] = [];
    let currentSection: LiveSection | null = null;
    const contentLines: string[] = [];

    const flushSection = () => {
      if (currentSection) {
        currentSection.content = contentLines.join("\n").trim();
        sections.push(currentSection);
        contentLines.length = 0;
      }
    };

    for (let i = 0; i < paragraphs.items.length; i++) {
      const para = paragraphs.items[i];
      const style = para.styleBuiltIn || "";
      const text = para.text.trim();

      if ((style.includes("Heading") || style.includes("heading")) && text.length > 0) {
        flushSection();
        const listNumber = paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString);
        const parsed = _parseSectionNumber(text) ?? (listNumber ? { number: listNumber, title: text } : null);
        // # DECISION (2026-04-24): accept headings WITHOUT a numeric
        // #   prefix (e.g. "Executive Summary") as real sections;
        // #   use the heading text itself as the identifier when
        // #   ``_parseSectionNumber`` returns null.
        // # WHY: the prior code dropped any Heading-styled paragraph
        // #      whose text didn't start with "N.N …" — which meant
        // #      EVERY human-authored document came back as 0
        // #      sections, even when Word clearly showed a heading
        // #      structure. The agent's ``doc_list_sections`` then
        // #      reported "document appears empty", gaslighting
        // #      the writer. The original strict regex was correct
        // #      for drafted output (ICH E3 enforces numeric
        // #      prefixes) but wrong for the general pre-existing-
        // #      doc case.
        // #
        // #      Downstream tools (read_section, write_section)
        // #      compare ids via string equality
        // #      (document_bridge.py L249–252), so non-numeric ids
        // #      work end-to-end. The agent sees "§Executive Summary
        // #      Executive Summary" in list_sections output and can
        // #      call ``doc_read_section(section_number="Executive
        // #      Summary")`` to read it.
        currentSection = {
          number: parsed ? parsed.number : text,
          heading: text,
          level: _getHeadingLevel(style),
          content: "",
        };
      } else if (currentSection && text.length > 0) {
        contentLines.push(text);
      }
    }

    // Flush last section
    flushSection();

    // Dedupe by section number — Word may have duplicates from
    // multiple insertions during prior runs. Keep the entry with
    // the most content (likely the actual draft, not a stale stub).
    const byNumber = new Map<string, LiveSection>();
    for (const s of sections) {
      const existing = byNumber.get(s.number);
      if (!existing || s.content.length > existing.content.length) {
        byNumber.set(s.number, s);
      }
    }
    const deduped = Array.from(byNumber.values());

    console.log(
      `[wordWriter] Read ${sections.length} sections from live Word document (${deduped.length} after dedup)`,
    );
    return deduped;
  });
}

// ---------------------------------------------------------------------------
// 5b. Document outline — lightweight headings + text-presence snapshot
// ---------------------------------------------------------------------------

/** Shape returned by ``readDocumentOutline``. Intentionally narrow —
 *  callers that need per-section content go through
 *  ``readAllSections``; this primitive exists for quick mount-time
 *  classification (empty vs has-content vs probable doctype). */
export interface DocumentOutline {
  /** Every heading paragraph's text + outline level (1–6).
   *  Preserves document order. Headings with empty text are dropped. */
  headings: ReadonlyArray<{ text: string; level: number }>;
  /** True when any paragraph in the body contains non-whitespace
   *  text — including non-heading body paragraphs. Used to detect
   *  "empty" more honestly than heading-count alone (some docs
   *  have prose without headings). */
  hasAnyText: boolean;
}

/**
 * Strict match for the outline-heading built-in styles
 * (``Heading1`` – ``Heading9``, either casing, optional space).
 *
 * # DECISION: strict match, NOT ``styleBuiltIn.includes("Heading")``.
 * # WHY: Office.js exposes many built-in styles whose names
 * #      contain "Heading" but are NOT outline headings — e.g.
 * #      ``tableOfFiguresHeading``, ``toaHeading``,
 * #      ``footnoteHeading``, ``endnoteHeading``, ``tocHeading``.
 * #      On a real CER doc with a TOC + figure captions +
 * #      deeply-nested list items auto-styled as Heading9, the
 * #      substring check balloons a 30-section doc to 1500+
 * #      "headings" (writer report, 2026-04-23). The strict
 * #      regex counts only the nine real outline levels, which
 * #      is what classification actually wants.
 * # DATE: 2026-04-23
 */
const OUTLINE_HEADING_STYLE_RE = /^heading\s?[1-9]$/i;

/**
 * Read a lightweight outline of the current Word document. Suitable
 * for mount-time classification; does NOT collect per-section
 * content (use ``readAllSections`` for that).
 *
 * Returns null when Office.js is unavailable (sandbox / browser
 * preview). The caller treats that as "unavailable" — distinct
 * from "empty", since we can't tell whether an empty classification
 * is real or a rendering-context artifact.
 */
export async function readDocumentOutline(): Promise<DocumentOutline | null> {
  if (!isOfficeAvailable()) return null;

  return runWord("readDocumentOutline", async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text,styleBuiltIn");
    await context.sync();

    const headings: Array<{ text: string; level: number }> = [];
    let hasAnyText = false;

    for (let i = 0; i < paragraphs.items.length; i++) {
      const para = paragraphs.items[i];
      const style = para.styleBuiltIn || "";
      const text = para.text.trim();
      if (text.length > 0) hasAnyText = true;
      if (OUTLINE_HEADING_STYLE_RE.test(style) && text.length > 0) {
        headings.push({ text, level: _getHeadingLevel(style) });
      }
    }

    return { headings, hasAnyText };
  });
}

// ---------------------------------------------------------------------------
// 6. Update title page fields from SPEC module output
// ---------------------------------------------------------------------------

/**
 * Resolve a field name to a table label for matching.
 *
 * Strategy (priority order):
 * 1. Direct: if key already looks like a table label (contains space or colon),
 *    use it as-is. This is the primary path when agents send exact labels.
 * 2. CSR legacy: hardcoded mappings for backward compat with SPEC modules.
 * 3. UPPER_SNAKE → Title Case: "DEVICE_NAME" → "Device Name:"
 */
function _resolveFieldLabel(fieldName: string): string | null {
  // Strategy 1: Direct — agent sends exact table labels like "Device Name:"
  // Detect by: contains a space, or already ends with ":"
  if (fieldName.includes(" ") || fieldName.endsWith(":")) {
    return fieldName.endsWith(":") ? fieldName : `${fieldName}:`;
  }

  // Strategy 2: CSR legacy mappings (backward compatibility)
  const csrMappings: Record<string, string> = {
    STUDY_TITLE: "Study Title:",
    PROTOCOL_NUMBER: "Protocol Number:",
    COMPOUND_NAME: "Compound:",
    REGULATORY_PHASE: "Phase:",
    INDICATION: "Indication:",
    SPONSOR_NAME: "Sponsor:",
    DATE_REPORT: "Report Date:",
    DATE_FPFV: "Study Initiation Date:",
    COMPARATOR: "Compound Code:",
  };

  if (csrMappings[fieldName]) return csrMappings[fieldName];

  // Strategy 3: Convert UPPER_SNAKE to Title Case: DEVICE_NAME → Device Name:
  const titleCase = fieldName
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

  return `${titleCase}:`;
}

/** Normalize a title-field label for matching: trim, drop trailing colons,
 *  lowercase. Mirror of word_writer.py::_norm_field_key — keep in sync. */
function _normFieldKey(key: string): string {
  return key.trim().replace(/:+$/, "").trim().toLowerCase();
}

/** True when the field is the one promoted to the large centered cover
 *  heading (and therefore excluded from the metadata table). Mirror of
 *  word_writer.py::_COVER_TITLE_KEY plus the legacy UPPER_SNAKE spelling. */
function _isCoverTitleKey(key: string): boolean {
  const norm = _normFieldKey(key);
  return norm === "study title" || norm === "study_title";
}

/** Bookmark names for the upserted title-page cover/statement paragraphs —
 *  lets a re-push update the text in place instead of duplicating it. */
const _TP_COVER_BOOKMARK = "AsthraTpCover";
function _statementBookmarkName(label: string): string {
  return `AsthraTpStmt_${_normFieldKey(label).replace(/[^a-z0-9]+/g, "_")}`;
}

/** Find the title-page confidentiality paragraph (italic, contains
 *  "confidential") among the first `scan` paragraphs. Requires
 *  `items,text,font/italic` to be loaded on the collection. */
function _findConfidentialityPara(paragraphs: Word.ParagraphCollection, scan = 15): Word.Paragraph | null {
  for (let i = 0; i < Math.min(paragraphs.items.length, scan); i++) {
    const p = paragraphs.items[i];
    if (p.font.italic && p.text.toLowerCase().includes("confidential")) {
      return p;
    }
  }
  return null;
}

/** Upsert one bookmarked centered title-page paragraph: replace the text in
 *  place when the bookmark exists, otherwise insert a new paragraph after
 *  `anchor` (skipped when anchor is null). Returns the paragraph, or null. */
async function _upsertBookmarkedPara(
  context: Word.RequestContext,
  bookmark: string,
  text: string,
  anchor: Word.Paragraph | null,
  format: (range: Word.Range, para: Word.Paragraph) => void,
): Promise<Word.Paragraph | null> {
  // Guarded — getBookmarkRangeOrNullObject needs WordApi 1.4.
  try {
    const existing = context.document.getBookmarkRangeOrNullObject(bookmark);
    existing.load("isNullObject");
    await context.sync();
    if (!existing.isNullObject) {
      const replaced = existing.insertText(text, "Replace");
      const para = replaced.paragraphs.getFirst();
      format(replaced, para);
      replaced.insertBookmark(bookmark);
      await context.sync();
      return para;
    }
  } catch {
    /* bookmark API unavailable — fall through to insert */
  }
  if (!anchor) return null;
  const para = anchor.insertParagraph(text, "After");
  const range = para.getRange("Whole");
  format(range, para);
  try {
    range.insertBookmark(bookmark);
  } catch {
    /* older WordApi — upsert degrades to insert-once */
  }
  await context.sync();
  return para;
}

/**
 * Read the current title-page field values back out of the live Word doc —
 * the capture counterpart of ``updateTitlePageFields``, so a writer's edits to
 * the title page (dates, version, sponsor, …) can be saved to the SSOT.
 *
 * Given the SSOT's known field keys, returns ``{originalKey: currentValue}`` for
 * every field it can locate in the doc:
 *  - Metadata table (first table): col 0 = label, col 1 = value. Matched to a
 *    known key by normalized label (``_normFieldKey`` of the key OR its rendered
 *    ``_resolveFieldLabel``), and the value is trimmed (the create path writes a
 *    leading two-space pad).
 *  - Cover Study Title (``AsthraTpCover`` bookmark) and statement fields
 *    (``AsthraTpStmt_*`` bookmarks) — the non-table title fields.
 * Keys it cannot find are omitted (the caller merges onto the SSOT dict).
 */
export async function readTitlePageFields(knownKeys: string[]): Promise<Record<string, string>> {
  if (!isOfficeAvailable() || knownKeys.length === 0) return {};

  return runWord("readTitlePageFields", async (context) => {
    const out: Record<string, string> = {};

    // normalized(label OR raw key) → original SSOT key.
    const keyByNorm = new Map<string, string>();
    for (const k of knownKeys) {
      keyByNorm.set(_normFieldKey(k), k);
      const label = _resolveFieldLabel(k);
      if (label) keyByNorm.set(_normFieldKey(label), k);
    }

    // 1) Metadata table rows (first table): col 0 = label, col 1 = value.
    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();
    if (tables.items.length > 0) {
      const table = tables.items[0];
      table.load("values");
      await context.sync();
      for (const row of table.values) {
        if (!row || row.length < 2) continue;
        const key = keyByNorm.get(_normFieldKey(row[0] ?? ""));
        if (!key) continue;
        out[key] = (row[1] ?? "").trim();
      }
    }

    // 2) Cover title + statement fields — bookmarked centered paragraphs, not
    // table rows. Batch every not-yet-found key's bookmark into one sync.
    const remaining = knownKeys.filter((k) => out[k] === undefined);
    if (remaining.length > 0) {
      try {
        const lookups = remaining.map((k) => {
          const bmName = _isCoverTitleKey(k) ? _TP_COVER_BOOKMARK : _statementBookmarkName(k);
          const rng = context.document.getBookmarkRangeOrNullObject(bmName);
          rng.load("isNullObject,text");
          return { k, rng };
        });
        await context.sync();
        for (const { k, rng } of lookups) {
          if (rng.isNullObject) continue;
          const t = (rng.text ?? "").trim();
          if (t) out[k] = t;
        }
      } catch {
        /* getBookmarkRangeOrNullObject needs WordApi 1.4 — table capture stands alone */
      }
    }

    return out;
  });
}

/**
 * Update title page table fields with extracted SPEC values.
 *
 * Finds the first table in the document (the title page metadata table)
 * and updates matching rows from [PENDING] to the extracted value.
 *
 * Fields declared in the doc-type YAML's ``title_page.statement_fields``
 * (passed as `statementFields`) are NOT table rows — they render as
 * free-standing centered paragraphs at the bottom of the title page,
 * mirroring word_writer._add_title_page. The "Study Title" field promotes
 * to the large centered cover heading and is excluded from the table too.
 *
 * @param fields - Map of SPEC field names to values (e.g. {STUDY_TITLE: "KEYNOTE-189..."})
 * @param style - Formatting spec from the doc-type YAML's title_page.style block
 * @param statementFields - title_page.statement_fields from the doc-type YAML
 * @returns Number of fields updated
 */
export async function updateTitlePageFields(
  fields: Record<string, string>,
  style?: TitlePageStyle,
  statementFields?: string[],
  options?: {
    /** Where to insert a newly-created title table when no confidentiality
     *  paragraph anchor is found. "end" (default, template/CER behavior) appends
     *  at the body end; "firstHeading" inserts before the first heading paragraph
     *  — used by the custom Load path, where the outline is already scaffolded so
     *  "end" would drop the table after the whole document. */
    anchorFallback?: "end" | "firstHeading";
  },
): Promise<number> {
  if (!isOfficeAvailable()) return 0;
  const anchorFallback = options?.anchorFallback ?? "end";

  // Merge provided style with defaults — SSOT from doc-type YAML
  const sty = { ...DEFAULT_TP_STYLE, ...style };

  // Partition (mirror of word_writer.partition_title_fields): cover title →
  // big centered heading; statement fields → centered paragraphs below the
  // table; everything else → table rows.
  const stmtNorms = new Set((statementFields ?? []).map(_normFieldKey));
  const allEntries = Object.entries(fields).filter(([, v]) => v);
  const statements: Array<[string, string]> = [];
  for (const declared of statementFields ?? []) {
    const hit = allEntries.find(([k]) => _normFieldKey(k) === _normFieldKey(declared));
    if (hit) statements.push(hit);
  }

  return runWord("updateTitlePageFields", async (context) => {
    // Update the centered title paragraph (looks for [PENDING: ...)
    const coverEntry = allEntries.find(([k]) => _isCoverTitleKey(k));
    const titleField = fields.STUDY_TITLE || fields["Device Name"] || fields.DEVICE_NAME || coverEntry?.[1];
    let coverHandled = false;
    if (titleField) {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("text");
      await context.sync();

      for (let i = 0; i < Math.min(paragraphs.items.length, 5); i++) {
        if (paragraphs.items[i].text.includes("[PENDING:")) {
          paragraphs.items[i].clear();
          const range = paragraphs.items[i].insertText(titleField, "Start");
          range.font.name = "Times New Roman";
          range.font.size = 12;
          range.font.bold = true;
          paragraphs.items[i].alignment = "Centered";
          await context.sync();
          coverHandled = true;
          break;
        }
      }
    }

    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();

    // Table rows exclude the promoted cover title and the statement fields.
    const entries = allEntries.filter(([k]) => !_isCoverTitleKey(k) && !stmtNorms.has(_normFieldKey(k)));
    if (entries.length === 0 && statements.length === 0 && !titleField) return 0;

    // Cover title + statement paragraphs — shared by the create and update
    // branches below. Upserts (bookmark-keyed) the big centered study title
    // under the document-title paragraph, and the statement fields (e.g. the
    // GCP compliance statement) as centered paragraphs at the BOTTOM of the
    // title page, after the confidentiality note. Requires a collection
    // loaded with "items,text,font/italic".
    const applyCoverAndStatements = async (paragraphs: Word.ParagraphCollection): Promise<number> => {
      let applied = 0;
      const confPara = _findConfidentialityPara(paragraphs);
      // Only a Study Title key promotes to a NEW cover paragraph — it is the
      // key excluded from the table rows above. Legacy keys (Device Name)
      // stay table rows and only fill a scaffolded [PENDING:] paragraph.
      const coverText = coverEntry?.[1];
      if (coverText && !coverHandled && paragraphs.items.length > 0) {
        const covPara = await _upsertBookmarkedPara(
          context,
          _TP_COVER_BOOKMARK,
          coverText,
          paragraphs.items[0],
          (range, para) => {
            range.font.name = sty.font_family;
            range.font.size = sty.title_size - 4;
            range.font.bold = true;
            range.font.italic = false;
            para.alignment = "Centered";
          },
        );
        if (covPara) {
          coverHandled = true;
          applied++;
        }
      }
      if (statements.length > 0) {
        const flags = await _loadFlags();
        let anchor: Word.Paragraph | null = confPara;
        for (const [label, value] of statements) {
          const stmtPara = await _upsertBookmarkedPara(
            context,
            _statementBookmarkName(label),
            value,
            anchor,
            (range, para) => {
              range.font.name = sty.font_family;
              range.font.size = sty.table_font_size;
              range.font.bold = false;
              range.font.italic = false;
              para.alignment = "Centered";
              const flag = _matchFlag(value, flags);
              if (flag) {
                _applyFlagStyle(range, flag);
              }
            },
          );
          if (stmtPara) {
            anchor = stmtPara;
            applied++;
          } else {
            console.warn(`[wordWriter] No anchor for title-page statement "${label}" — skipped`);
          }
        }
      }
      return applied;
    };

    // -----------------------------------------------------------------
    // No existing table — CREATE it from the dict (zero-translation).
    // This is the CER path: the scaffold started blank (no field
    // registry), and the agent's submit_draft(title_fields={...}) is
    // the first time we know what fields to show. The dict keys become
    // the left column labels, the values become the right column.
    //
    // Position: find the confidentiality paragraph (italic, centered,
    // near the top) and insert the table BEFORE it. If not found,
    // fall back to inserting at the end of the body.
    // -----------------------------------------------------------------
    if (tables.items.length === 0) {
      console.log("[wordWriter] No existing table — creating title page table from dict");

      const body = context.document.body;
      const paragraphs = body.paragraphs;
      paragraphs.load("items,text,font/italic,style,styleBuiltIn");
      await context.sync();

      // Find the confidentiality paragraph to insert BEFORE it.
      // Heuristic: italic paragraph in the first 15 paragraphs that
      // contains "confidential" — matches any doc-type's phrasing.
      let anchorPara: Word.Paragraph | null = _findConfidentialityPara(paragraphs);

      // No confidentiality anchor: the custom Load path scaffolds the outline
      // BEFORE painting the title page, so "End" would drop the table after the
      // whole document. Fall back to the first heading paragraph so the table
      // lands above the outline (and below any leading title/subtitle chrome).
      if (!anchorPara && anchorFallback === "firstHeading") {
        anchorPara =
          paragraphs.items.find((p) => _isHeadingStyle(p.styleBuiltIn, p.style)) ?? null;
      }

      // Cover title + statement fields (they're excluded from the table).
      const extras = await applyCoverAndStatements(paragraphs);

      if (entries.length === 0) {
        console.log("[wordWriter] No table rows to create — cover/statements only");
        return extras;
      }

      // ── Professional title page table creation ──
      // Matches python-docx word_writer output: borderless table with
      // right-aligned bold labels, left-aligned values, proper spacing.
      // All styling comes from the doc-type YAML config (sty parameter).
      //
      // Layout:
      //   [spacer]
      //   Label (right, bold)  |  Value (left, normal or gray italic if flag)
      //   ...
      //   [spacer]
      //   [confidentiality notice]

      // Spacer before table
      if (anchorPara) {
        for (let s = 0; s < (sty.spacers_after_subtitle ?? 2); s++) {
          const spacer = anchorPara.insertParagraph("", "Before");
          spacer.font.size = sty.spacer_font_size ?? 6;
        }
      }

      // Create table with per-cell alignment control
      let table: Word.Table;
      if (anchorPara) {
        table = anchorPara.insertTable(entries.length, 2, "Before");
      } else {
        table = body.insertTable(entries.length, 2, "End");
      }

      // Remove ALL borders for a clean, professional look
      table.style = "Table Grid";
      const borderTypes = ["Top", "Bottom", "Left", "Right", "InsideHorizontal", "InsideVertical"] as const;
      for (const bt of borderTypes) {
        try {
          table.getBorder(bt).type = "None";
        } catch {
          /* some versions */
        }
      }

      // Fit to window width for consistent layout
      try {
        table.autoFitWindow();
        await context.sync();
      } catch {
        /* acceptable degradation */
      }

      // Populate cells — all styling from YAML config (sty)
      const flags = await _loadFlags();
      for (let r = 0; r < entries.length; r++) {
        const [label, value] = entries[r];

        // Label cell: right-aligned, bold, explicitly not italic
        const labelCell = table.getCell(r, 0);
        const labelPara = labelCell.body.paragraphs.getFirst();
        labelPara.clear();
        const labelRange = labelPara.insertText(label, "Start");
        labelRange.font.name = sty.font_family;
        labelRange.font.size = sty.table_font_size;
        labelRange.font.bold = true;
        labelRange.font.italic = false;
        labelRange.font.color = "#000000";
        labelPara.alignment = "Right";
        labelPara.spaceAfter = 2;
        labelPara.spaceBefore = 2;

        // Value cell: left-aligned, explicitly normal weight + not italic
        // (must set italic=false because some document styles inherit italic)
        const valueCell = table.getCell(r, 1);
        const valuePara = valueCell.body.paragraphs.getFirst();
        valuePara.clear();
        const valueRange = valuePara.insertText(`  ${value}`, "Start");
        valueRange.font.name = sty.font_family;
        valueRange.font.size = sty.table_font_size;
        valueRange.font.bold = false;
        valueRange.font.italic = false;
        valueRange.font.color = "#000000";
        valuePara.alignment = "Left";
        valuePara.spaceAfter = 2;
        valuePara.spaceBefore = 2;

        // Flag styling (gray italic for [FILL:], [NOT FOUND], etc.)
        // Only applied when the value matches a flag pattern — normal
        // values keep the explicit non-italic formatting above.
        const flag = _matchFlag(value, flags);
        if (flag) {
          _applyFlagStyle(valueRange, flag);
        }
      }

      // Spacer after table (before confidentiality notice)
      if (anchorPara) {
        const spacerAfter = anchorPara.insertParagraph("", "Before");
        spacerAfter.font.size = sty.spacer_font_size ?? 6;
      }

      await context.sync();
      console.log(`[wordWriter] Created title page table: ${entries.length} rows`);
      return entries.length + extras;
    }

    // -----------------------------------------------------------------
    // Existing table — UPDATE matching rows (CSR legacy path).
    // The scaffold pre-created the table with [PENDING] values; we
    // find rows by label match and replace the values.
    // -----------------------------------------------------------------
    const table = tables.items[0];
    const rows = table.rows;
    rows.load("items");
    await context.sync();

    let updated = 0;

    for (const [fieldName, value] of entries) {
      // Resolve field name to table label (works for any doc type)
      const label = _resolveFieldLabel(fieldName);
      if (!label) continue;

      // Find the row with this label
      for (let r = 0; r < rows.items.length; r++) {
        const labelCell = table.getCell(r, 0);
        const labelPara = labelCell.body.paragraphs.getFirst();
        labelPara.load("text");
        await context.sync();

        // Case-insensitive match — handles "Date Of Evaluation:" vs "Date of Evaluation:"
        if (labelPara.text.trim().toLowerCase() === label.toLowerCase()) {
          const valueCell = table.getCell(r, 1);
          const valuePara = valueCell.body.paragraphs.getFirst();
          valuePara.clear();
          const range = valuePara.insertText(value, "Start");
          range.font.name = sty.font_family;
          range.font.size = sty.table_font_size;
          // Flag styling from shared registry
          const flags = await _loadFlags();
          const matchedFlag = _matchFlag(value, flags);
          if (matchedFlag) {
            _applyFlagStyle(range, matchedFlag);
          }
          await context.sync();
          updated++;
          break;
        }
      }
    }

    // Cover title + statement paragraphs also upsert on the update path
    // (re-push of title fields after the table already exists) — bookmark
    // keying makes this idempotent.
    const bodyParas = context.document.body.paragraphs;
    bodyParas.load("items,text,font/italic");
    await context.sync();
    updated += await applyCoverAndStatements(bodyParas);

    return updated;
  });
}

// ---------------------------------------------------------------------------
// Position-based live document retrieval — two-step efficient read protocol
// ---------------------------------------------------------------------------
//
// Step 1: findPositions()  — search doc, return lightweight anchors
// Step 2: readAtPositions() — fetch targeted content via those anchors
//
// These functions are only called via the WebSocket bridge by the generation
// agent. They avoid whole-document reads on large regulatory documents.

/** A single position anchor returned by findPositions(). */
export interface PositionMatch {
  /** Stable anchor id — encodes anchor_type + paragraph_index.
   *  Example: "h_42" (heading at index 42), "p_183" (paragraph at 183). */
  match_id: string;
  /** Element type: heading, paragraph, or table. */
  anchor_type: "heading" | "paragraph" | "table";
  /** Dotted section number of the containing section, or null. */
  section_number: string | null;
  /** Heading text of the containing section, or null. */
  section_heading: string | null;
  /** Index in body.paragraphs.items (0-based). */
  paragraph_index: number;
  /** First 100 characters of the matching element's text. */
  content_preview: string;
  /** Character offset of the query match within content_preview. */
  char_offset: number;
}

/** Options for findPositions(). */
export interface FindPositionsOptions {
  query: string;
  /** Restrict search to this section (and its subsections). */
  section_scope?: string | null;
  /** Element types to search. Defaults to all when omitted. */
  anchor_types?: Array<"heading" | "paragraph" | "table">;
  /** Max matches to return (default 10, capped at 25). */
  limit?: number;
}

/** Expansion parameters for a single anchor in readAtPositions(). */
export interface ReadAtExpand {
  /** 'section': read entire section body (heading → next same-or-higher heading).
   *  'paragraph': read surrounding paragraphs using before/after counts. */
  mode?: "section" | "paragraph";
  /** Paragraphs before the match (default 0). Only used when mode='paragraph'. */
  paragraphs_before?: number;
  /** Paragraphs after the match (default 3). Only used when mode='paragraph'. */
  paragraphs_after?: number;
}

/** A single anchor passed to readAtPositions(). */
export interface ReadAtAnchor {
  match_id: string;
  expand?: ReadAtExpand;
}

/** Result for a single anchor from readAtPositions(). */
export interface ReadAtResult {
  match_id: string;
  /** null on success; 'stale_anchor' | 'out_of_range' | string on error. */
  error: string | null;
  /** The retrieved text content, or null on error. */
  content: string | null;
  start_paragraph_index?: number;
  end_paragraph_index?: number;
  paragraph_count?: number;
  /** Word style display name of the anchor paragraph (e.g. 'Title', 'Heading 1', 'Normal'). */
  style?: string;
  /** Font family name of the anchor paragraph (e.g. 'Calibri'). */
  font_name?: string;
  /** Font size in points. */
  font_size?: number;
  /** Whether the anchor paragraph's font is bold. */
  bold?: boolean;
  /** Whether the anchor paragraph's font is italic. */
  italic?: boolean;
}

// ---------------------------------------------------------------------------
// Table listing/reading — compact payload by default
// ---------------------------------------------------------------------------

export interface ListTablesOptions {
  /** Optional cap; defaults to 25. */
  limit?: number;
}

export interface TableListItem {
  /** Stable table anchor id (table index): `t_0`, `t_1`, ... */
  match_id: string;
  table_index: number;
  row_count: number;
  column_count: number;
  header_preview: string;
}

export type TableIncludeMode = "schema" | "sample" | "full";

export interface GetTableOptions {
  match_id: string;
  include?: TableIncludeMode;
  /** In sample mode, max body rows after header. Defaults to 5, capped at 20. */
  row_window?: number;
}

export interface TableReadResult {
  match_id: string;
  error: string | null;
  row_count?: number;
  column_count?: number;
  header?: string[];
  rows?: string[][];
  truncated?: boolean;
}

export interface MergeTableCellsOptions {
  match_id: string;
  start_cell: string;
  end_cell: string;
  label?: string;
}

export interface MergeTableCellsResult {
  match_id: string;
  success: boolean;
  error: string | null;
  merged_range?: string;
}

export interface SplitTableCellOptions {
  match_id: string;
  cell: string;
  rows: number;
  columns: number;
}

export interface SplitTableCellResult {
  match_id: string;
  success: boolean;
  error: string | null;
  split_cell?: string;
}

export interface InsertDeleteTableRowsOptions {
  match_id: string;
  cell: string;
  count?: number;
  position?: "before" | "after";
}

export interface InsertDeleteTableRowsResult {
  match_id: string;
  success: boolean;
  error: string | null;
  affected_rows?: number;
}

export interface InsertDeleteTableColumnsOptions {
  match_id: string;
  cell: string;
  count?: number;
  position?: "before" | "after";
}

export interface InsertDeleteTableColumnsResult {
  match_id: string;
  success: boolean;
  error: string | null;
  affected_columns?: number;
}

export interface SetTableBordersOptions {
  match_id: string;
  start_cell: string;
  end_cell: string;
  border_location?:
    | "Top"
    | "Left"
    | "Bottom"
    | "Right"
    | "InsideHorizontal"
    | "InsideVertical"
    | "Inside"
    | "Outside"
    | "All";
  border_type?: "None" | "Single" | "Double" | "Dotted" | "Dashed" | "DotDashed" | "Dot2Dashed";
  color?: string;
  width?: number;
}

export interface SetTableBordersResult {
  match_id: string;
  success: boolean;
  error: string | null;
  updated_range?: string;
}

export interface SetTableShadingOptions {
  match_id: string;
  start_cell: string;
  end_cell: string;
  color: string;
}

export interface SetTableShadingResult {
  match_id: string;
  success: boolean;
  error: string | null;
  updated_range?: string;
}

export interface ResizeTableColumnsOptions {
  match_id: string;
  start_column: string;
  end_column: string;
  width_points: number;
}

export interface ResizeTableColumnsResult {
  match_id: string;
  success: boolean;
  error: string | null;
  resized_columns?: string;
}

export interface SetTableCellValuesOptions {
  match_id: string;
  start_cell: string;
  values: string[][];
}

export interface SetTableCellValuesResult {
  match_id: string;
  success: boolean;
  error: string | null;
  written_cells?: number;
  updated_range?: string;
}

export interface InsertRowsAndSetValuesOptions {
  match_id: string;
  cell: string;
  values: string[][];
  count?: number;
  position?: "before" | "after";
  start_cell?: string;
}

export interface InsertRowsAndSetValuesResult {
  match_id: string;
  success: boolean;
  error: string | null;
  affected_rows?: number;
  written_cells?: number;
  updated_range?: string;
}

export interface TableCellRef {
  rowIndex: number;
  columnIndex: number;
}

export interface TableCellRange {
  start: TableCellRef;
  end: TableCellRef;
}

function _parseTableMatchId(matchId: string): number | null {
  if (!matchId.startsWith("t_")) return null;
  const idx = Number.parseInt(matchId.slice(2), 10);
  return Number.isNaN(idx) || idx < 0 ? null : idx;
}

function _parseTableCellRef(cellRef: string): TableCellRef | null {
  const match = /^([A-Za-z]+)([1-9]\d*)$/.exec(cellRef.trim());
  if (!match) return null;

  const [, rawColumn, rawRow] = match;
  let columnIndex = 0;
  for (const ch of rawColumn.toUpperCase()) {
    columnIndex = columnIndex * 26 + (ch.charCodeAt(0) - 64);
  }

  return {
    rowIndex: Number.parseInt(rawRow, 10) - 1,
    columnIndex: columnIndex - 1,
  };
}

function _parseTableCellRange(rangeRef: string): TableCellRange | null {
  const parts = rangeRef
    .split(":")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length !== 2) return null;

  const left = _parseTableCellRef(parts[0]);
  const right = _parseTableCellRef(parts[1]);
  if (!left || !right) return null;

  return {
    start: {
      rowIndex: Math.min(left.rowIndex, right.rowIndex),
      columnIndex: Math.min(left.columnIndex, right.columnIndex),
    },
    end: {
      rowIndex: Math.max(left.rowIndex, right.rowIndex),
      columnIndex: Math.max(left.columnIndex, right.columnIndex),
    },
  };
}

function _parseTableColumnRef(columnRef: string): number | null {
  const raw = columnRef.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(raw)) return null;
  let columnIndex = 0;
  for (const ch of raw) {
    columnIndex = columnIndex * 26 + (ch.charCodeAt(0) - 64);
  }
  return columnIndex - 1;
}

function _columnLettersFromIndex(idx: number): string {
  let n = idx + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCodePoint(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function _normalizeHexColor(color: string): string | null {
  const raw = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toUpperCase();
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toUpperCase()}`;
  return null;
}

function _isWordApiSupported(version: string): boolean {
  try {
    const reqs = (globalThis as { Office?: { context?: { requirements?: unknown } } }).Office?.context?.requirements as
      | { isSetSupported?: (setName: string, setVersion: string) => boolean }
      | undefined;
    return typeof reqs?.isSetSupported === "function" && reqs.isSetSupported("WordApi", version) === true;
  } catch {
    return false;
  }
}

function _headerPreview(values: string[][]): string {
  if (!values.length) return "";
  const head = values[0] ?? [];
  return head.slice(0, 4).join(" | ").slice(0, 160);
}

export async function listTables(options?: ListTablesOptions): Promise<TableListItem[]> {
  if (!isOfficeAvailable()) return [];

  return runWord("listTables", async (context) => {
    const limit = Math.max(1, Math.min(options?.limit ?? 25, 100));
    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();

    const count = Math.min(limit, tables.items.length);
    for (let i = 0; i < count; i++) {
      tables.items[i].load("values,rowCount");
    }
    await context.sync();

    const out: TableListItem[] = [];
    for (let i = 0; i < count; i++) {
      const t = tables.items[i];
      const values = t.values ?? [];
      const rowCount = t.rowCount ?? values.length;
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      out.push({
        match_id: `t_${i}`,
        table_index: i,
        row_count: rowCount,
        column_count: columnCount,
        header_preview: _headerPreview(values),
      });
    }

    return out;
  });
}

export async function getTable(options: GetTableOptions): Promise<TableReadResult> {
  if (!isOfficeAvailable()) return { match_id: options.match_id, error: "office_unavailable" };

  const tableIndex = _parseTableMatchId(options.match_id);
  if (tableIndex === null) {
    return { match_id: options.match_id, error: "invalid_match_id" };
  }

  return runWord("getTable", async (context) => {
    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();

    if (tableIndex >= tables.items.length) {
      return { match_id: options.match_id, error: "stale_anchor" };
    }

    const include = options.include ?? "sample";
    const sampleWindow = Math.max(1, Math.min(options.row_window ?? 5, 20));
    const table = tables.items[tableIndex];
    table.load("values,rowCount");
    await context.sync();

    const values = (table.values ?? []) as string[][];
    const rowCount = table.rowCount ?? values.length;
    const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
    const header = values[0] ?? [];
    const bodyRows = values.slice(1);

    if (include === "schema") {
      return {
        match_id: options.match_id,
        error: null,
        row_count: rowCount,
        column_count: columnCount,
        header,
      };
    }

    if (include === "full") {
      return {
        match_id: options.match_id,
        error: null,
        row_count: rowCount,
        column_count: columnCount,
        header,
        rows: bodyRows,
        truncated: false,
      };
    }

    const rows = bodyRows.slice(0, sampleWindow);
    return {
      match_id: options.match_id,
      error: null,
      row_count: rowCount,
      column_count: columnCount,
      header,
      rows,
      truncated: bodyRows.length > rows.length,
    };
  });
}

export async function mergeTableCells(options: MergeTableCellsOptions): Promise<MergeTableCellsResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.4")) {
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };
  }

  const tableIndex = _parseTableMatchId(options.match_id);
  if (tableIndex === null) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  }

  const range = _parseTableCellRange(`${options.start_cell}:${options.end_cell}`);
  if (!range) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell_range",
    };
  }

  try {
    return await runWord("mergeTableCells", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      if (tableIndex >= tables.items.length) {
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      }

      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();

      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;

      if (
        range.start.rowIndex < 0 ||
        range.end.rowIndex >= rowCount ||
        range.start.columnIndex < 0 ||
        range.end.columnIndex >= columnCount
      ) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      const start = table.getCell(range.start.rowIndex, range.start.columnIndex);
      const end = table.getCell(range.end.rowIndex, range.end.columnIndex);

      // WordApi merge is exposed on TableCell (not Table).
      start.merge(end);
      await context.sync();

      if (options.label?.trim()) {
        const mergedCell = table.getCell(range.start.rowIndex, range.start.columnIndex);
        mergedCell.body.insertText(options.label.trim(), Word.InsertLocation.replace);
        await context.sync();
      }

      return {
        match_id: options.match_id,
        success: true,
        error: null,
        merged_range: `${options.start_cell}:${options.end_cell}`,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `merge_failed:${message || "unknown"}`,
    };
  }
}

export async function splitTableCell(options: SplitTableCellOptions): Promise<SplitTableCellResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.4"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const cell = _parseTableCellRef(options.cell);
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!cell)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell",
    };
  if (options.rows < 1 || options.columns < 1) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_split_shape",
    };
  }

  try {
    return await runWord("splitTableCell", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };

      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (cell.rowIndex >= rowCount || cell.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      const target = table.getCell(cell.rowIndex, cell.columnIndex);
      target.split(options.rows, options.columns);
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        split_cell: options.cell,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `split_failed:${message || "unknown"}`,
    };
  }
}

export async function insertTableRows(options: InsertDeleteTableRowsOptions): Promise<InsertDeleteTableRowsResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const cell = _parseTableCellRef(options.cell);
  const count = Math.max(1, Math.min(options.count ?? 1, 10));
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!cell)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell",
    };

  try {
    return await runWord("insertTableRows", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (cell.rowIndex >= rowCount || cell.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      const anchor = table.getCell(cell.rowIndex, cell.columnIndex);
      anchor.insertRows(options.position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after, count);
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        affected_rows: count,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `insert_rows_failed:${message || "unknown"}`,
    };
  }
}

export async function deleteTableRows(options: InsertDeleteTableRowsOptions): Promise<InsertDeleteTableRowsResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const cell = _parseTableCellRef(options.cell);
  const count = Math.max(1, Math.min(options.count ?? 1, 10));
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!cell)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell",
    };

  try {
    return await runWord("deleteTableRows", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (rowCount <= 1)
        return {
          match_id: options.match_id,
          success: false,
          error: "cannot_delete_last_row",
        };
      if (cell.rowIndex >= rowCount || cell.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }
      const allowed = Math.min(count, rowCount - 1);
      for (let i = 0; i < allowed; i++) {
        table.getCell(cell.rowIndex, cell.columnIndex).deleteRow();
      }
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        affected_rows: allowed,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `delete_rows_failed:${message || "unknown"}`,
    };
  }
}

export async function insertTableColumns(
  options: InsertDeleteTableColumnsOptions,
): Promise<InsertDeleteTableColumnsResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const cell = _parseTableCellRef(options.cell);
  const count = Math.max(1, Math.min(options.count ?? 1, 10));
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!cell)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell",
    };

  try {
    return await runWord("insertTableColumns", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (cell.rowIndex >= rowCount || cell.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }
      table
        .getCell(cell.rowIndex, cell.columnIndex)
        .insertColumns(options.position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after, count);
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        affected_columns: count,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `insert_columns_failed:${message || "unknown"}`,
    };
  }
}

export async function deleteTableColumns(
  options: InsertDeleteTableColumnsOptions,
): Promise<InsertDeleteTableColumnsResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const cell = _parseTableCellRef(options.cell);
  const count = Math.max(1, Math.min(options.count ?? 1, 10));
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!cell)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell",
    };

  try {
    return await runWord("deleteTableColumns", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const values = (table.values ?? []) as string[][];
      const rowCount = table.rowCount ?? 0;
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (columnCount <= 1)
        return {
          match_id: options.match_id,
          success: false,
          error: "cannot_delete_last_column",
        };
      if (cell.rowIndex >= rowCount || cell.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }
      const allowed = Math.min(count, columnCount - 1);
      for (let i = 0; i < allowed; i++) {
        table.getCell(cell.rowIndex, cell.columnIndex).deleteColumn();
      }
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        affected_columns: allowed,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `delete_columns_failed:${message || "unknown"}`,
    };
  }
}

export async function setTableBorders(options: SetTableBordersOptions): Promise<SetTableBordersResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const range = _parseTableCellRange(`${options.start_cell}:${options.end_cell}`);
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!range)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell_range",
    };

  const location = options.border_location ?? "All";
  const borderType = options.border_type ?? "Single";
  const normalizedColor = options.color ? _normalizeHexColor(options.color) : null;
  if (options.color && !normalizedColor) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_color",
    };
  }

  try {
    return await runWord("setTableBorders", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (range.end.rowIndex >= rowCount || range.end.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      for (let r = range.start.rowIndex; r <= range.end.rowIndex; r++) {
        for (let c = range.start.columnIndex; c <= range.end.columnIndex; c++) {
          const border = table.getCell(r, c).getBorder(location);
          border.type = borderType;
          if (normalizedColor) border.color = normalizedColor;
          if (typeof options.width === "number" && options.width > 0) border.width = options.width;
        }
      }
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        updated_range: `${options.start_cell}:${options.end_cell}`,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `set_borders_failed:${message || "unknown"}`,
    };
  }
}

export async function setTableShading(options: SetTableShadingOptions): Promise<SetTableShadingResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const range = _parseTableCellRange(`${options.start_cell}:${options.end_cell}`);
  const normalizedColor = _normalizeHexColor(options.color);
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!range)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell_range",
    };
  if (!normalizedColor)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_color",
    };

  try {
    return await runWord("setTableShading", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const rowCount = table.rowCount ?? 0;
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (range.end.rowIndex >= rowCount || range.end.columnIndex >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      for (let r = range.start.rowIndex; r <= range.end.rowIndex; r++) {
        for (let c = range.start.columnIndex; c <= range.end.columnIndex; c++) {
          table.getCell(r, c).shadingColor = normalizedColor;
        }
      }
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        updated_range: `${options.start_cell}:${options.end_cell}`,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `set_shading_failed:${message || "unknown"}`,
    };
  }
}

export async function resizeTableColumns(options: ResizeTableColumnsOptions): Promise<ResizeTableColumnsResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const startColumn = _parseTableColumnRef(options.start_column);
  const endColumn = _parseTableColumnRef(options.end_column);
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (startColumn === null || endColumn === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_column_range",
    };
  if (!(options.width_points > 0))
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_width",
    };

  const left = Math.min(startColumn, endColumn);
  const right = Math.max(startColumn, endColumn);

  try {
    return await runWord("resizeTableColumns", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };
      const table = tables.items[tableIndex];
      table.load("values,rowCount");
      await context.sync();
      const values = (table.values ?? []) as string[][];
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;
      if (right >= columnCount)
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };

      for (let c = left; c <= right; c++) {
        table.getCell(0, c).columnWidth = options.width_points;
      }
      await context.sync();
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        resized_columns: `${options.start_column}:${options.end_column}`,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `resize_columns_failed:${message || "unknown"}`,
    };
  }
}

export async function setTableCellValues(options: SetTableCellValuesOptions): Promise<SetTableCellValuesResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const start = _parseTableCellRef(options.start_cell);
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!start)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_start_cell",
    };

  const matrix = options.values;
  if (!Array.isArray(matrix) || matrix.length === 0 || matrix.some((row) => !Array.isArray(row) || row.length === 0)) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_values_matrix",
    };
  }

  const rowSpan = matrix.length;
  const colSpan = Math.max(...matrix.map((row) => row.length));

  try {
    return await runWord("setTableCellValues", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };

      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const values = (table.values ?? []) as string[][];
      const rowCount = table.rowCount ?? values.length;
      const columnCount = values.length ? Math.max(...values.map((r) => r.length)) : 0;

      const endRow = start.rowIndex + rowSpan - 1;
      const endCol = start.columnIndex + colSpan - 1;
      if (start.rowIndex < 0 || start.columnIndex < 0 || endRow >= rowCount || endCol >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      let written = 0;
      for (let dr = 0; dr < rowSpan; dr++) {
        const rowValues = matrix[dr];
        for (let dc = 0; dc < rowValues.length; dc++) {
          const text = `${rowValues[dc] ?? ""}`;
          table.getCell(start.rowIndex + dr, start.columnIndex + dc).body.insertText(text, Word.InsertLocation.replace);
          written += 1;
        }
      }
      await context.sync();

      const endCell = `${_columnLettersFromIndex(endCol)}${endRow + 1}`;
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        written_cells: written,
        updated_range: `${options.start_cell}:${endCell}`,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `set_cell_values_failed:${message || "unknown"}`,
    };
  }
}

export async function insertTableRowsAndSetValues(
  options: InsertRowsAndSetValuesOptions,
): Promise<InsertRowsAndSetValuesResult> {
  if (!isOfficeAvailable())
    return {
      match_id: options.match_id,
      success: false,
      error: "office_unavailable",
    };
  if (!_isWordApiSupported("1.3"))
    return {
      match_id: options.match_id,
      success: false,
      error: "unsupported_capability",
    };

  const tableIndex = _parseTableMatchId(options.match_id);
  const anchorCell = _parseTableCellRef(options.cell);
  if (tableIndex === null)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_match_id",
    };
  if (!anchorCell)
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_cell",
    };

  const matrix = options.values;
  if (!Array.isArray(matrix) || matrix.length === 0 || matrix.some((row) => !Array.isArray(row) || row.length === 0)) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_values_matrix",
    };
  }

  const count = Math.max(1, Math.min(options.count ?? 1, 10));
  const position = options.position === "before" ? "before" : "after";
  const explicitStart = options.start_cell ? _parseTableCellRef(options.start_cell) : null;
  if (options.start_cell && !explicitStart) {
    return {
      match_id: options.match_id,
      success: false,
      error: "invalid_start_cell",
    };
  }

  const rowSpan = matrix.length;
  const colSpan = Math.max(...matrix.map((row) => row.length));

  try {
    return await runWord("insertTableRowsAndSetValues", async (context) => {
      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();
      if (tableIndex >= tables.items.length)
        return {
          match_id: options.match_id,
          success: false,
          error: "stale_anchor",
        };

      const table = tables.items[tableIndex];
      table.load("rowCount,values");
      await context.sync();
      const beforeValues = (table.values ?? []) as string[][];
      const beforeRowCount = table.rowCount ?? beforeValues.length;
      const beforeColumnCount = beforeValues.length ? Math.max(...beforeValues.map((r) => r.length)) : 0;

      if (anchorCell.rowIndex >= beforeRowCount || anchorCell.columnIndex >= beforeColumnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      table
        .getCell(anchorCell.rowIndex, anchorCell.columnIndex)
        .insertRows(position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after, count);
      await context.sync();

      table.load("rowCount,values");
      await context.sync();
      const afterValues = (table.values ?? []) as string[][];
      const rowCount = table.rowCount ?? afterValues.length;
      const columnCount = afterValues.length ? Math.max(...afterValues.map((r) => r.length)) : 0;

      const start = explicitStart ?? {
        rowIndex: position === "before" ? anchorCell.rowIndex : anchorCell.rowIndex + count,
        columnIndex: anchorCell.columnIndex,
      };

      const endRow = start.rowIndex + rowSpan - 1;
      const endCol = start.columnIndex + colSpan - 1;
      if (start.rowIndex < 0 || start.columnIndex < 0 || endRow >= rowCount || endCol >= columnCount) {
        return {
          match_id: options.match_id,
          success: false,
          error: "out_of_bounds",
        };
      }

      let written = 0;
      for (let dr = 0; dr < rowSpan; dr++) {
        const rowValues = matrix[dr];
        for (let dc = 0; dc < rowValues.length; dc++) {
          const text = `${rowValues[dc] ?? ""}`;
          table.getCell(start.rowIndex + dr, start.columnIndex + dc).body.insertText(text, Word.InsertLocation.replace);
          written += 1;
        }
      }
      await context.sync();

      const startCellLabel = options.start_cell ?? `${_columnLettersFromIndex(start.columnIndex)}${start.rowIndex + 1}`;
      const endCell = `${_columnLettersFromIndex(endCol)}${endRow + 1}`;
      return {
        match_id: options.match_id,
        success: true,
        error: null,
        affected_rows: count,
        written_cells: written,
        updated_range: `${startCellLabel}:${endCell}`,
      };
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      match_id: options.match_id,
      success: false,
      error: `insert_rows_and_set_values_failed:${message || "unknown"}`,
    };
  }
}

/**
 * Search the live Word document and return structured position anchors.
 *
 * Loads paragraph text + styles in a single sync, then walks the list to
 * find query matches. Each match becomes a PositionMatch whose match_id
 * encodes the paragraph index for a subsequent readAtPositions() call.
 *
 * Capped at `limit` results (max 25).
 */
export async function findPositions(options: FindPositionsOptions): Promise<PositionMatch[]> {
  if (!isOfficeAvailable()) return [];

  return runWord("findPositions", async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    // Load both styleBuiltIn (enum) and style (display name) so the heading
    // check works for custom styles that don't use a built-in heading enum.
    paragraphs.load("text,style,styleBuiltIn");
    await context.sync();

    // Native list numbering renders section numbers outside paragraph.text —
    // load the list labels so section_scope tracking still resolves numbers.
    const paraListItems = paragraphs.items.map((p: Word.Paragraph) => p.listItemOrNullObject);
    for (const li of paraListItems) {
      li.load("isNullObject,listString");
    }
    await context.sync();

    const queryLower = options.query.toLowerCase();
    const effectiveLimit = Math.min(options.limit ?? 10, 25);
    const scopeTypes = options.anchor_types ?? (["heading", "paragraph", "table"] as const);
    const sectionScope = options.section_scope ?? null;

    const matches: PositionMatch[] = [];

    let currentSectionNumber: string | null = null;
    let currentSectionHeading: string | null = null;
    // Whether the cursor has entered the scoped section.
    let inScope = !sectionScope;

    for (let i = 0; i < paragraphs.items.length && matches.length < effectiveLimit; i++) {
      const para = paragraphs.items[i];
      const styleRaw = para.styleBuiltIn ?? "";
      const styleName = para.style ?? "";
      const text = para.text.trim();
      const isHeading = styleRaw.toLowerCase().includes("heading") || styleName.toLowerCase().includes("heading");

      // Track section context for scope filtering and metadata. The text
      // parse covers legacy literal-numbered headings; the list label covers
      // native list numbering (number rendered outside paragraph.text).
      if (isHeading) {
        const listNumber = paraListItems[i].isNullObject ? null : _numberFromListString(paraListItems[i].listString);
        const number = _parseSectionNumber(text)?.number ?? listNumber;
        if (number) {
          currentSectionNumber = number;
          currentSectionHeading = text;

          if (sectionScope) {
            inScope = currentSectionNumber === sectionScope || currentSectionNumber.startsWith(`${sectionScope}.`);
          }
        }
      }

      if (!inScope) continue;

      const paraAnchorType: "heading" | "paragraph" = isHeading ? "heading" : "paragraph";
      if (!scopeTypes.includes(paraAnchorType)) continue;

      const textLower = text.toLowerCase();
      const matchIdx = textLower.indexOf(queryLower);
      if (matchIdx === -1) continue;

      const matchId = `${paraAnchorType[0]}_${i}`; // e.g. "h_42" or "p_183"
      matches.push({
        match_id: matchId,
        anchor_type: paraAnchorType,
        section_number: currentSectionNumber,
        section_heading: currentSectionHeading,
        paragraph_index: i,
        content_preview: text.slice(0, 100),
        char_offset: matchIdx,
      });
    }

    console.log(`[wordWriter] findPositions: ${matches.length} match(es) for "${options.query}"`);
    return matches;
  });
}

/**
 * Fetch targeted content from position anchors returned by findPositions().
 *
 * Loads paragraph text once, then for each anchor either reads the whole
 * section (mode='section') or a bounded paragraph window (mode='paragraph').
 * Output is capped at 8 000 characters per anchor.
 *
 * Returns a stale_anchor error when the paragraph index is out of bounds
 * (document was mutated since the find call).
 */
export async function readAtPositions(anchors: ReadAtAnchor[]): Promise<ReadAtResult[]> {
  if (!isOfficeAvailable()) {
    return anchors.map((a) => ({
      match_id: a.match_id,
      error: "office_unavailable",
      content: null,
    }));
  }

  return runWord("readAtPositions", async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    // Load style (display name), styleBuiltIn (enum), and font metadata so
    // each result can surface formatting info the agent needs for write decisions.
    paragraphs.load("text,style,styleBuiltIn,font/name,font/size,font/bold,font/italic");
    await context.sync();

    const total = paragraphs.items.length;
    const results: ReadAtResult[] = [];

    for (const anchor of anchors) {
      // Parse paragraph_index from match_id ("h_42" → 42, "p_183" → 183).
      const parts = anchor.match_id.split("_");
      const paraIndex = Number.parseInt(parts[parts.length - 1], 10);

      if (Number.isNaN(paraIndex) || paraIndex < 0 || paraIndex >= total) {
        results.push({
          match_id: anchor.match_id,
          error: "stale_anchor",
          content: null,
        });
        continue;
      }

      const expand = anchor.expand ?? {};
      const mode = expand.mode ?? "paragraph";

      let startPara = paraIndex;
      let endPara = paraIndex;

      if (mode === "section") {
        // Determine the heading level of the anchor paragraph.
        const anchorStyle = paragraphs.items[paraIndex].styleBuiltIn ?? "";
        const anchorLevel = _getHeadingLevel(anchorStyle);

        // Expand forward until the next heading at the same or higher level.
        for (let j = paraIndex + 1; j < total; j++) {
          const nextStyle = paragraphs.items[j].styleBuiltIn ?? "";
          const nextIsHeading = nextStyle.toLowerCase().includes("heading");
          if (nextIsHeading) {
            const nextLevel = _getHeadingLevel(nextStyle);
            if (nextLevel <= anchorLevel) {
              // Stop before this heading.
              endPara = j - 1;
              break;
            }
          }
          endPara = j; // keep extending
        }
      } else {
        // paragraph mode — bounded window
        const before = Math.max(0, expand.paragraphs_before ?? 0);
        const after = Math.max(0, expand.paragraphs_after ?? 3);
        startPara = Math.max(0, paraIndex - before);
        endPara = Math.min(total - 1, paraIndex + after);
      }

      const lines: string[] = [];
      for (let j = startPara; j <= endPara; j++) {
        lines.push(paragraphs.items[j].text);
      }
      let content = lines.join("\n");

      // Cap at 8 000 characters to prevent over-fetching.
      const CHAR_CAP = 8000;
      if (content.length > CHAR_CAP) {
        content = `${content.slice(0, CHAR_CAP)}\n[...content truncated at 8 000 characters. Use a narrower expand to read more.]`;
      }

      // Capture anchor paragraph metadata for the result.
      const anchorPara = paragraphs.items[paraIndex];
      results.push({
        match_id: anchor.match_id,
        error: null,
        content,
        start_paragraph_index: startPara,
        end_paragraph_index: endPara,
        paragraph_count: endPara - startPara + 1,
        style: anchorPara.style || undefined,
        font_name: anchorPara.font.name || undefined,
        font_size: anchorPara.font.size || undefined,
        bold: anchorPara.font.bold || undefined,
        italic: anchorPara.font.italic || undefined,
      });
    }

    console.log(`[wordWriter] readAtPositions: ${results.length} anchor(s) resolved`);
    return results;
  });
}

// ---------------------------------------------------------------------------
// writeAtPositions — position-based write (replace / insert_before / insert_after)
// ---------------------------------------------------------------------------
// Companion to readAtPositions. The agent supplies match_id values (from
// doc_find_positions or synthetic p_N anchors) together with markdown content
// and a write mode. Each anchor is processed in its own Word.run() so that
// formatAndInsert's internal syncs don't interfere with sibling anchors.

/** The three write modes for a position-based write. */
export type WriteAtMode = "replace" | "insert_before" | "insert_after";

/** A single write instruction passed to writeAtPositions(). */
export interface WriteAtAnchor {
  /** match_id from doc_find_positions, or synthetic 'p_N' / 'h_N' / 't_N'. */
  match_id: string;
  /** Markdown content to write. Rendered via formatAndInsert. */
  content: string;
  /**
   * How to place the content relative to the anchor paragraph:
   *   - "replace"       — delete the anchor paragraph, insert content in its place
   *   - "insert_after"  — insert content immediately after the anchor paragraph
   *   - "insert_before" — insert content immediately before the anchor paragraph
   * Defaults to "replace".
   */
  mode?: WriteAtMode;
  /**
   * Optional Word style name to apply to the first inserted paragraph
   * (e.g. 'Title', 'Heading 1', 'Normal'). For 'replace' mode the original
   * paragraph's style is automatically preserved when this is omitted.
   * For 'insert_before'/'insert_after' modes, supply this to control the style
   * of the inserted content.
   */
  style?: string;
}

/** Result for a single anchor from writeAtPositions(). */
export interface WriteAtResult {
  match_id: string;
  success: boolean;
  /** null on success; 'stale_anchor' | 'office_unavailable' | string on error. */
  error: string | null;
  /** Paragraph index that was targeted (absent on error). */
  paragraph_index?: number;
}

/**
 * Write content at specific paragraph positions in the live Word document.
 *
 * Each anchor is processed independently (one Word.run per anchor) so that
 * formatAndInsert's internal syncs cannot cause index drift across anchors.
 *
 * Track Changes must be enabled by the caller (documentBridge._handleWriteAtPositions)
 * BEFORE this function is called, matching the existing write_section pattern.
 */
export async function writeAtPositions(anchors: WriteAtAnchor[]): Promise<WriteAtResult[]> {
  if (!isOfficeAvailable()) {
    return anchors.map((a) => ({
      match_id: a.match_id,
      success: false,
      error: "office_unavailable",
    }));
  }

  const results: WriteAtResult[] = [];
  for (const anchor of anchors) {
    const result = await _writeSingleAnchor(anchor);
    results.push(result);
  }

  console.log(`[wordWriter] writeAtPositions: ${results.length} anchor(s) processed`);
  return results;
}

/** Internal: process one WriteAtAnchor inside its own Word.run(). */
async function _writeSingleAnchor(anchor: WriteAtAnchor): Promise<WriteAtResult> {
  return runWord("writeAtPositions", async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    // Load style, alignment, and font so we can fully copy the original
    // paragraph's formatting to the replacement paragraph (replace mode).
    paragraphs.load("text,style,styleBuiltIn,alignment,font/name,font/size,font/bold,font/italic,font/color");

    await context.sync();

    const total = paragraphs.items.length;

    // Decode paragraph index from match_id ("h_42" → 42, "p_5" → 5).
    const parts = anchor.match_id.split("_");
    const paraIndex = Number.parseInt(parts[parts.length - 1], 10);

    if (Number.isNaN(paraIndex) || paraIndex < 0 || paraIndex >= total) {
      return {
        match_id: anchor.match_id,
        success: false,
        error: "stale_anchor",
      };
    }

    const mode = anchor.mode ?? "replace";
    const content = anchor.content;
    let result: WriteAtResult;
    if (mode === "replace") {
      const targetPara = paragraphs.items[paraIndex];

      const targetStyle = anchor.style ?? targetPara.style;
      const targetAlignment = targetPara.alignment;
      const targetFontName = targetPara.font.name || undefined;
      const targetFontSize = targetPara.font.size || undefined;
      const targetFontColor = targetPara.font.color || undefined;

      // ── Fast path: plain-text content (no newlines, no markdown syntax) ───
      const isPlainText = _isPlainText(content);

      if (isPlainText) {
        targetPara.insertText(content, Word.InsertLocation.replace);
        await context.sync();
        result = {
          match_id: anchor.match_id,
          success: true,
          error: null,
          paragraph_index: paraIndex,
        };
      } else {
        // ── Markdown path: formatAndInsert → apply style → delete original ────
        const targetRef = targetPara;

        if (paraIndex === 0) {
          const dummy = body.insertParagraph("", Word.InsertLocation.start);
          await context.sync();
          await formatAndInsert(context, dummy, content, "");

          const fresh = body.paragraphs;
          fresh.load("style,alignment,font/name,font/size,font/color");
          await context.sync();
          if (fresh.items.length > 1) {
            const newPara = fresh.items[1];
            newPara.style = targetStyle;
            await context.sync();
          }
          targetRef.delete();
          dummy.delete();
          await context.sync();
        } else {
          const anchorPara = paragraphs.items[paraIndex - 1];
          await formatAndInsert(context, anchorPara, content, "");

          const fresh = body.paragraphs;
          fresh.load("style,alignment,font/name,font/size,font/color");
          await context.sync();
          if (fresh.items.length > paraIndex) {
            const newPara = fresh.items[paraIndex];
            newPara.style = targetStyle;
            await context.sync();
          } else {
            console.warn(
              `[wordWriter] write:replace ${anchor.match_id} ⚠ fresh.items.length=${fresh.items.length} ≤ paraIndex=${paraIndex} — style copy skipped`,
            );
          }
          targetRef.delete();
          await context.sync();
        }
        result = {
          match_id: anchor.match_id,
          success: true,
          error: null,
          paragraph_index: paraIndex,
        };
      } // end markdown path
    } else if (mode === "insert_after") {
      // Anchor paragraph stays; new content lands immediately after it.
      const anchorPara = paragraphs.items[paraIndex];
      await formatAndInsert(context, anchorPara, content, "");
      // Apply caller-supplied style to the first newly-inserted paragraph.
      // After formatAndInsert, the new paragraph is at paraIndex + 1.
      if (anchor.style) {
        const fresh = body.paragraphs;
        fresh.load("style");
        await context.sync();
        if (fresh.items.length > paraIndex + 1) {
          fresh.items[paraIndex + 1].style = anchor.style;
          await context.sync();
        }
      }
      result = {
        match_id: anchor.match_id,
        success: true,
        error: null,
        paragraph_index: paraIndex,
      };
    } else if (mode === "insert_before") {
      if (paraIndex === 0) {
        // Insert content before the first paragraph: dummy-then-delete pattern.
        const dummy = body.insertParagraph("", Word.InsertLocation.start);
        await context.sync();
        await formatAndInsert(context, dummy, content, "");
        // Apply caller-supplied style to the first newly-inserted paragraph.
        // Document order is [dummy(0), NEW_1(1), …] — NEW_1 is at index 1.
        if (anchor.style) {
          const fresh = body.paragraphs;
          fresh.load("style");
          await context.sync();
          if (fresh.items.length > 1) {
            fresh.items[1].style = anchor.style;
            await context.sync();
          }
        }
        dummy.delete();
        await context.sync();
      } else {
        // Insert after the preceding paragraph → lands before the anchor.
        const anchorPara = paragraphs.items[paraIndex - 1];
        await formatAndInsert(context, anchorPara, content, "");
        // Apply caller-supplied style to the first newly-inserted paragraph.
        // After formatAndInsert, the new paragraph is at paraIndex.
        if (anchor.style) {
          const fresh = body.paragraphs;
          fresh.load("style");
          await context.sync();
          if (fresh.items.length > paraIndex) {
            fresh.items[paraIndex].style = anchor.style;
            await context.sync();
          }
        }
      }
      result = {
        match_id: anchor.match_id,
        success: true,
        error: null,
        paragraph_index: paraIndex,
      };
    } else {
      result = {
        match_id: anchor.match_id,
        success: false,
        error: `unknown_mode:${mode}`,
      };
    }

    return result;
  });
}

// ---------------------------------------------------------------------------
// replaceText — surgical, range-level find/replace inside one paragraph
// ---------------------------------------------------------------------------
//
// This is the precise-edit primitive: it changes only the matched substring
// and leaves every other run (hyperlinks, bold spans, comments, footnote
// markers, list numbering, field codes, table-cell properties) untouched.
//
// Semantics
//   - One paragraph per anchor (multi-paragraph edits use replace-paragraph).
//   - Batch is validated first; if ANY anchor fails validation NO mutation
//     happens (atomic). This mirrors `git apply` patch-context behaviour.
//   - The whole batch runs inside ONE Word.run() → one Word undo unit, so
//     the writer's Ctrl+Z reverts every edit in a request as a single step.
//   - `expected_snippet` (optional but recommended) guards against stale
//     anchors: if the paragraph's current text does not contain the snippet
//     the agent saw at read-time, we refuse to write.
//   - Default occurrence is "first" — matches Word's Find & Replace UI.
//   - Multi-occurrence replacements walk the RangeCollection in reverse so
//     earlier replacements never invalidate later ones' positions.
//   - Wildcards are disabled (`matchWildcards: false`) — `find` is matched
//     as a literal Unicode string.

/** Occurrence selector for replaceText. */
export type ReplaceOccurrence = "first" | "all" | number;

/** A single find/replace instruction. */
export interface ReplaceTextAnchor {
  /** Paragraph anchor — same convention as WriteAtAnchor ('p_N', 'h_N', 't_N'). */
  match_id: string;
  /**
   * Optional substring the agent saw when it read this paragraph. If set
   * and the current paragraph text doesn't contain it (after Unicode +
   * quote + whitespace normalization), the anchor is rejected as stale.
   */
  expected_snippet?: string;
  /** Exact substring to find inside the paragraph. */
  find: string;
  /** Replacement text. May be empty (effectively a delete). */
  replace: string;
  /**
   * Which occurrence to replace:
   *   - "first" (default) — replace only the first occurrence
   *   - "all"             — replace every occurrence in the paragraph
   *   - <1-based number>  — replace only the N-th occurrence
   */
  occurrence?: ReplaceOccurrence;
  /** Case-sensitive match (default true). */
  match_case?: boolean;
  /** Whole-word match (default false). */
  whole_word?: boolean;
}

/** Result for a single replaceText anchor. */
export interface ReplaceTextResult {
  match_id: string;
  success: boolean;
  /** null on success. Codes: stale_anchor, stale_anchor_mismatch, find_not_found,
   *  ambiguous_match, occurrence_out_of_range, cross_paragraph_find,
   *  empty_find, office_unavailable, internal_error, batch_rejected. */
  error: string | null;
  /** Paragraph index actually used (post drift-recovery if any). */
  paragraph_index?: number;
  /** Paragraph text BEFORE this anchor's mutation. */
  before?: string;
  /** Paragraph text AFTER this anchor's mutation. */
  after?: string;
  /** Number of substring replacements actually applied. */
  replacements_made?: number;
}

/** Normalize a string for substring comparison only (NOT for the actual
 *  Word search). Folds smart quotes to straight, NBSP to space, collapses
 *  ASCII whitespace runs, and applies NFC. Used to compare the caller's
 *  `expected_snippet` against Word's paragraph text without false negatives
 *  caused by autocorrect or whitespace drift. */
function _normalizeForCompare(s: string): string {
  return s
    .normalize("NFC")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u00A0\u202F\u2009\u200A\u2007]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Build a quote-normalized search needle. We DON'T strip whitespace here —
 *  Word.search matches whitespace literally and changing it would alter the
 *  matched range. We only fold smart quotes (the autocorrect case). */
function _normalizeForSearch(s: string): string {
  return s
    .normalize("NFC")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"');
}

/** True when `content` is plain prose that can be inserted verbatim via
 *  `range.insertText` \u2014 no newlines and none of the markdown sigils
 *  (`* _ | # \` [ ]`) that the formatter would otherwise need to render. */
function _isPlainText(content: string): boolean {
  return !content.includes("\n") && !/[*_|#`\[\]]/.test(content);
}

/** True when `content` is *block* markdown \u2014 a table or any multi-line
 *  payload that `insertText` cannot render in place and that must go through
 *  `formatAndInsert` (e.g. converting a paragraph into a Word table). Inline-
 *  only markdown like a single-line `**bold**` is intentionally excluded: it
 *  has no newline and no table pipe, so it stays on the plain-text path. */
function _isBlockMarkdown(content: string): boolean {
  return !_isPlainText(content) && (content.includes("\n") || /^[ \t]*\|.*\|/m.test(content));
}

/** Resolve the paragraph index from a match_id ('p_N' → N) with optional
 *  drift recovery using expected_snippet. Returns -1 if no resolution. */
function _resolveParaIndex(
  paragraphs: Word.ParagraphCollection,
  matchId: string,
  expectedSnippet: string | undefined,
): number {
  const total = paragraphs.items.length;
  const parts = matchId.split("_");
  const hinted = Number.parseInt(parts[parts.length - 1], 10);

  const snippetNorm = expectedSnippet ? _normalizeForCompare(expectedSnippet) : "";
  const matches = (idx: number): boolean => {
    if (idx < 0 || idx >= total) return false;
    if (!snippetNorm) return true;
    return _normalizeForCompare(paragraphs.items[idx].text).includes(snippetNorm);
  };

  // Stage 1 — exact index hint
  if (!Number.isNaN(hinted) && matches(hinted)) return hinted;
  if (!snippetNorm) {
    // No snippet → fall back to bare index validity check
    return !Number.isNaN(hinted) && hinted >= 0 && hinted < total ? hinted : -1;
  }
  // Stage 2 — scan ±3 around the hint (intra-batch drift)
  for (let delta = 1; delta <= 3; delta++) {
    if (matches(hinted - delta)) return hinted - delta;
    if (matches(hinted + delta)) return hinted + delta;
  }
  // Stage 3 — unique full-document scan (concurrent-edit drift)
  let foundIdx = -1;
  let foundCount = 0;
  for (let i = 0; i < total; i++) {
    if (matches(i)) {
      foundIdx = i;
      foundCount++;
      if (foundCount > 1) return -1; // not unique
    }
  }
  return foundCount === 1 ? foundIdx : -1;
}

/**
 * Replace one or more substrings inside specific paragraphs without
 * disturbing surrounding runs, formatting, hyperlinks, comments,
 * footnotes, list numbering or table-cell properties.
 *
 * All anchors are validated first; if any anchor fails validation NO
 * mutation is performed and every anchor receives an error result
 * (the failed one gets its real error, the rest get "batch_rejected").
 */
export async function replaceText(anchors: ReplaceTextAnchor[]): Promise<ReplaceTextResult[]> {
  if (!isOfficeAvailable()) {
    return anchors.map((a) => ({
      match_id: a.match_id,
      success: false,
      error: "office_unavailable",
    }));
  }

  if (anchors.length === 0) return [];

  return runWord("replaceText", async (context) => {
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    paragraphs.load("text");
    await context.sync();

    // ── Phase 1: validate every anchor, build a mutation plan ──────────────
    // Word.Range.search accepts a partial options bag (it's NOT a ClientObject).
    type SearchOpts = {
      matchCase: boolean;
      matchWholeWord: boolean;
      matchWildcards: boolean;
    };
    type Plan = {
      anchor: ReplaceTextAnchor;
      paraIndex: number;
      before: string;
      // Either an error to surface (and skip mutation), or the data needed
      // to mutate this anchor.
      error: string | null;
      // 1-based indices INTO the RangeCollection returned by paragraph.search.
      // For "first" → [1]; for "all" → undefined (use them all); for N → [N].
      occurrenceFilter: number[] | "all";
      searchOptions: SearchOpts;
      searchNeedle: string;
    };

    const plans: Plan[] = [];
    let firstError: string | null = null;

    for (const anchor of anchors) {
      // Cheap up-front rejects.
      if (!anchor.find || anchor.find.length === 0) {
        plans.push({
          anchor,
          paraIndex: -1,
          before: "",
          error: "empty_find",
          occurrenceFilter: [],
          searchOptions: {
            matchCase: true,
            matchWholeWord: false,
            matchWildcards: false,
          },
          searchNeedle: "",
        });
        firstError ??= "empty_find";
        continue;
      }
      if (anchor.find.includes("\r") || anchor.find.includes("\n") || anchor.find.includes("\v")) {
        plans.push({
          anchor,
          paraIndex: -1,
          before: "",
          error: "cross_paragraph_find",
          occurrenceFilter: [],
          searchOptions: {
            matchCase: true,
            matchWholeWord: false,
            matchWildcards: false,
          },
          searchNeedle: "",
        });
        firstError ??= "cross_paragraph_find";
        continue;
      }

      const paraIndex = _resolveParaIndex(paragraphs, anchor.match_id, anchor.expected_snippet);
      if (paraIndex < 0) {
        const err = anchor.expected_snippet ? "stale_anchor_mismatch" : "stale_anchor";
        plans.push({
          anchor,
          paraIndex: -1,
          before: "",
          error: err,
          occurrenceFilter: [],
          searchOptions: {
            matchCase: true,
            matchWholeWord: false,
            matchWildcards: false,
          },
          searchNeedle: "",
        });
        firstError ??= err;
        continue;
      }

      const before = paragraphs.items[paraIndex].text;

      // Short-circuit no-op: find === replace (after quote normalization).
      const findNorm = _normalizeForSearch(anchor.find);
      const replaceNorm = _normalizeForSearch(anchor.replace);
      if (findNorm === replaceNorm) {
        plans.push({
          anchor,
          paraIndex,
          before,
          error: null,
          occurrenceFilter: [],
          searchOptions: {} as Word.SearchOptions,
          searchNeedle: findNorm,
        });
        continue;
      }

      const searchOptions: SearchOpts = {
        matchCase: anchor.match_case ?? true,
        matchWholeWord: anchor.whole_word ?? false,
        matchWildcards: false,
      };

      // Validate occurrence selector and find existence by checking the
      // normalized paragraph text. The actual Word.search is queued in
      // phase 2 so we don't burn proxy work for anchors that will fail.
      const beforeNormForCount = _normalizeForCompare(before);
      const needleForCount = _normalizeForCompare(anchor.find);
      // Count occurrences (case-aware according to match_case).
      const occCount = ((): number => {
        if (!needleForCount) return 0;
        const hay = searchOptions.matchCase ? beforeNormForCount : beforeNormForCount.toLowerCase();
        const ndl = searchOptions.matchCase ? needleForCount : needleForCount.toLowerCase();
        let count = 0;
        let from = 0;
        // For whole-word counting we'd need word-boundary checks, but the
        // count is only used to validate "occurrence" against availability;
        // Word.search will be the source of truth.
        while (true) {
          const idx = hay.indexOf(ndl, from);
          if (idx < 0) break;
          count++;
          from = idx + ndl.length;
        }
        return count;
      })();

      if (occCount === 0) {
        plans.push({
          anchor,
          paraIndex,
          before,
          error: "find_not_found",
          occurrenceFilter: [],
          searchOptions,
          searchNeedle: findNorm,
        });
        firstError ??= "find_not_found";
        continue;
      }

      const occRaw = anchor.occurrence ?? "first";
      let occurrenceFilter: number[] | "all";
      if (occRaw === "all") {
        occurrenceFilter = "all";
      } else if (occRaw === "first") {
        occurrenceFilter = [1];
      } else if (typeof occRaw === "number" && Number.isInteger(occRaw) && occRaw >= 1) {
        if (occRaw > occCount) {
          plans.push({
            anchor,
            paraIndex,
            before,
            error: "occurrence_out_of_range",
            occurrenceFilter: [],
            searchOptions,
            searchNeedle: findNorm,
          });
          firstError ??= "occurrence_out_of_range";
          continue;
        }
        occurrenceFilter = [occRaw];
      } else {
        plans.push({
          anchor,
          paraIndex,
          before,
          error: "invalid_occurrence",
          occurrenceFilter: [],
          searchOptions,
          searchNeedle: findNorm,
        });
        firstError ??= "invalid_occurrence";
        continue;
      }

      plans.push({
        anchor,
        paraIndex,
        before,
        error: null,
        occurrenceFilter,
        searchOptions,
        searchNeedle: findNorm,
      });
    }

    // Atomic gate — if any anchor failed validation, reject the whole batch
    // and propagate the failure reason. Anchors that individually validated
    // are marked "batch_rejected" so the agent knows they were not applied.
    if (firstError) {
      return plans.map((p) => ({
        match_id: p.anchor.match_id,
        success: false,
        error: p.error ?? "batch_rejected",
        paragraph_index: p.paraIndex >= 0 ? p.paraIndex : undefined,
        before: p.before || undefined,
      }));
    }

    // ── Phase 2: queue all the searches in one batch, then mutate ──────────
    // Sort plans by descending paragraph index so a same-document mutation
    // ordering can't shift later anchors' offsets. (Same-paragraph multi-
    // occurrence ordering is handled below by walking ranges in reverse.)
    const orderedPlans = [...plans].sort((a, b) => b.paraIndex - a.paraIndex);

    const results = new Map<ReplaceTextAnchor, ReplaceTextResult>();

    {
      // Queue searches.
      const searchHandles: { plan: Plan; ranges: Word.RangeCollection }[] = [];
      for (const plan of orderedPlans) {
        if (plan.searchNeedle === _normalizeForSearch(plan.anchor.replace)) {
          // No-op anchor — record an immediate success, no search needed.
          results.set(plan.anchor, {
            match_id: plan.anchor.match_id,
            success: true,
            error: null,
            paragraph_index: plan.paraIndex,
            before: plan.before,
            after: plan.before,
            replacements_made: 0,
          });
          continue;
        }
        const paraRange = paragraphs.items[plan.paraIndex].getRange();
        const ranges = paraRange.search(plan.searchNeedle, plan.searchOptions);
        ranges.load("text");
        searchHandles.push({ plan, ranges });
      }
      await context.sync();

      // Apply mutations (reverse range order per paragraph).
      for (const { plan, ranges } of searchHandles) {
        const items = ranges.items;
        if (items.length === 0) {
          // Could happen if the smart-quote normalization on our side
          // differs from Word's storage. Surface as find_not_found.
          results.set(plan.anchor, {
            match_id: plan.anchor.match_id,
            success: false,
            error: "find_not_found",
            paragraph_index: plan.paraIndex,
            before: plan.before,
          });
          continue;
        }

        // Resolve the 1-based occurrence filter against actual range count.
        let targets: Word.Range[];
        if (plan.occurrenceFilter === "all") {
          targets = items.slice();
        } else {
          targets = [];
          for (const n of plan.occurrenceFilter) {
            if (n >= 1 && n <= items.length) targets.push(items[n - 1]);
          }
          if (targets.length === 0) {
            results.set(plan.anchor, {
              match_id: plan.anchor.match_id,
              success: false,
              error: "occurrence_out_of_range",
              paragraph_index: plan.paraIndex,
              before: plan.before,
            });
            continue;
          }
        }

        const replacementText = _normalizeForSearch(plan.anchor.replace);

        // ── Block-markdown path: render a Word table (or other block markdown)
        // instead of inserting the raw `| col | col |` source as literal text.
        // Only valid when the replacement spans the WHOLE paragraph — a table
        // is a block element and cannot be inserted into a mid-paragraph range.
        // Mirrors the markdown path in `_writeSingleAnchor`: formatAndInsert
        // after an anchor paragraph → copy the original paragraph's style → del
        // the original. Partial / multi-target matches fall through to the plain
        // insert below (unchanged behavior).
        if (
          _isBlockMarkdown(replacementText) &&
          targets.length === 1 &&
          _normalizeForCompare(items[0].text) === _normalizeForCompare(plan.before)
        ) {
          const paraIndex = plan.paraIndex;
          const targetPara = paragraphs.items[paraIndex];
          targetPara.load("style");
          await context.sync();
          const targetStyle = targetPara.style;

          // Descending plan order (orderedPlans) means the delete+insert here
          // only shifts paragraph indices ≥ paraIndex — all already processed —
          // so earlier-resolved ranges for lower-index anchors stay valid.
          if (paraIndex === 0) {
            const dummy = body.insertParagraph("", Word.InsertLocation.start);
            await context.sync();
            await formatAndInsert(context, dummy, replacementText, "");
            const fresh = body.paragraphs;
            fresh.load("style");
            await context.sync();
            if (fresh.items.length > 1) {
              fresh.items[1].style = targetStyle;
              await context.sync();
            }
            targetPara.delete();
            dummy.delete();
            await context.sync();
          } else {
            const anchorPara = paragraphs.items[paraIndex - 1];
            await formatAndInsert(context, anchorPara, replacementText, "");
            const fresh = body.paragraphs;
            fresh.load("style");
            await context.sync();
            if (fresh.items.length > paraIndex) {
              fresh.items[paraIndex].style = targetStyle;
              await context.sync();
            }
            targetPara.delete();
            await context.sync();
          }

          results.set(plan.anchor, {
            match_id: plan.anchor.match_id,
            success: true,
            error: null,
            paragraph_index: paraIndex,
            before: plan.before,
            // The original paragraph is gone; report what we wrote so the
            // Phase-4 reload doesn't overwrite `after` with a now-shifted para.
            after: replacementText,
            replacements_made: 1,
          });
          continue;
        }

        // Walk in reverse document order so each replacement leaves earlier
        // ranges' offsets unaffected.
        targets.sort((a, b) => items.indexOf(b) - items.indexOf(a));
        for (const r of targets) {
          r.insertText(replacementText, Word.InsertLocation.replace);
        }
        results.set(plan.anchor, {
          match_id: plan.anchor.match_id,
          success: true,
          error: null,
          paragraph_index: plan.paraIndex,
          before: plan.before,
          replacements_made: targets.length,
        });
      }
      await context.sync();

      // ── Phase 4: re-load mutated paragraphs to capture `after` text ─────
      const reloaded = body.paragraphs;
      reloaded.load("text");
      await context.sync();
      for (const { plan } of searchHandles) {
        const r = results.get(plan.anchor);
        if (!r || !r.success) continue;
        // Block-markdown anchors already set `after` (their original paragraph
        // was deleted, so a reload by paraIndex would read the wrong para).
        if (r.after !== undefined) continue;
        const after = plan.paraIndex < reloaded.items.length ? reloaded.items[plan.paraIndex].text : "";
        r.after = after;
      }
    }

    // Return results in the caller's original order.
    const out: ReplaceTextResult[] = anchors.map(
      (a) =>
        results.get(a) ?? {
          match_id: a.match_id,
          success: false,
          error: "internal_error",
        },
    );
    console.log(`[wordWriter] replaceText: ${out.filter((r) => r.success).length}/${out.length} anchor(s) succeeded`);
    return out;
  });
}

// ---------------------------------------------------------------------------
// Exported for testing
// ---------------------------------------------------------------------------

export const _testing = {
  isOfficeAvailable,
  ICH_E3_SKELETON,
  _getHeadingLevel,
  _isHeadingStyle,
  _findBoundsInIndex,
  _spanFromHeading,
  _parseSectionNumber,
  _parseTableMatchId,
  _parseTableCellRef,
  _parseTableCellRange,
  _normalizeForCompare,
  _normalizeForSearch,
  _isPlainText,
  _isBlockMarkdown,
  _isHeadingsOnlyParagraphs,
  _extractHeadingNumber,
  _numberFromListString,
  _styleSpecToStylePlan,
  _pageSpecToHeaderFooterPlan,
  _builtInHeadingStyleForNumber,
  _skeletonTitleMap,
  _resolveHeadingNumberForHydrate,
  _numberingRebuildDecision,
  _skeletonTopStart,
  _planNativeNumbering,
  _scaffoldHeadingText,
  _coveredSpanTouchesTable,
  _planTableCellFlagStrip,
  _buildOutlineFromParagraphs,
  _rollUpHasContent,
  _outlineToCustomSpine,
  _placeTableRunsInParagraphs,
  _findTableRunStart,
  _titleCellsToFields,
};
